/** Runtime last-in-head liquid-glass lock.
 * Frost + specular sheen + refractive rim + chromatic edge.
 * CSS-only (no WebGL) so nested cards, feed, and overlays stay intact.
 */
export const GLASS_LOCK_CSS = `
:root,
html[data-theme="light"] {
  --wlj-card-radius: 4px;
  --wlj-btn-radius: 4px;
  --wlj-radius: 4px;
  --wlj-radius-sm: 4px;
  --wlj-radius-lg: 4px;
  --wlj-radius-btn: 4px;
  --wlj-modal-radius: 4px;
  --wlj-nav-radius: 4px;
  --wlj-input-radius: 4px;
  --wlj-glass-overlay: rgba(255, 255, 255, 0.14);
  --wlj-glass-surface: rgba(255, 255, 255, 0.14);
  --wlj-glass-bg-pane: rgba(255, 255, 255, 0.14);
  --wlj-glass-sheen: linear-gradient(155deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.04) 42%, transparent 74%);
  --wlj-glass-border: rgba(255, 255, 255, 0.38);
  --wlj-glass-edge: rgba(255, 255, 255, 0.4);
  --wlj-glass-surface-edge: rgba(255, 255, 255, 0.4);
  --wlj-glass-shadow: 0 8px 24px rgba(16, 24, 40, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.32);
  --wlj-glass-shadow-hover: 0 10px 28px rgba(16, 24, 40, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.42);
  --wlj-glass-surface-shadow: var(--wlj-glass-shadow);
  --wlj-glass-blur-overlay: saturate(150%) blur(20px);
  --wlj-glass-surface-blur: saturate(150%) blur(20px);
  --wlj-glass-search-fill: rgba(255, 255, 255, 0.1);
  --wlj-glass-search-blur: saturate(180%) blur(18px);
  --wlj-glass-search-border: rgba(255, 255, 255, 0.55);
  --wlj-glass-search-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45), 0 8px 24px rgba(16, 24, 40, 0.08);
  --wlj-glass-dropdown: rgba(255, 255, 255, 0.78);
  --wlj-glass-dropdown-blur: saturate(180%) blur(48px);
  --wlj-modal-scrim: color-mix(in srgb, var(--wlj-primary, #2563eb) 20%, rgba(210, 228, 255, 0.28));
  --wlj-modal-scrim-blur: blur(16px) saturate(70%);
  --wlj-modal-glass: var(--wlj-glass-dropdown);
  --wlj-modal-glass-blur: var(--wlj-glass-dropdown-blur);
  --wlj-modal-pane: var(--wlj-modal-glass);
  --wlj-modal-header: transparent;
  --wlj-glass-field: rgba(255, 255, 255, 0.08);
  --wlj-modal-field: rgba(255, 255, 255, 0.08);
  --wlj-modal-field-blur: saturate(180%) blur(28px);
  --wlj-modal-placeholder: #475569;
  --background-color3: rgba(255, 255, 255, 0.08);
  --searchbg: rgba(255, 255, 255, 0.08);
}
html[data-theme="dark"] {
  --wlj-glass-overlay: rgba(28, 28, 36, 0.32);
  --wlj-glass-surface: rgba(28, 28, 36, 0.32);
  --wlj-glass-bg-pane: rgba(28, 28, 36, 0.32);
  --wlj-glass-sheen: linear-gradient(155deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 42%, transparent 74%);
  --wlj-glass-border: rgba(255, 255, 255, 0.14);
  --wlj-glass-edge: rgba(255, 255, 255, 0.18);
  --wlj-glass-surface-edge: rgba(255, 255, 255, 0.18);
  --wlj-glass-shadow: 0 10px 28px rgba(0, 0, 0, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.12);
  --wlj-glass-shadow-hover: 0 12px 32px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.16);
  --wlj-glass-surface-shadow: var(--wlj-glass-shadow);
  --wlj-glass-blur-overlay: saturate(150%) blur(20px);
  --wlj-glass-surface-blur: saturate(150%) blur(20px);
  --wlj-glass-search-fill: rgba(28, 28, 36, 0.22);
  --wlj-glass-search-blur: saturate(160%) blur(18px);
  --wlj-glass-search-border: rgba(255, 255, 255, 0.18);
  --wlj-glass-search-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 10px 28px rgba(0, 0, 0, 0.36);
  --wlj-glass-dropdown: rgba(16, 16, 22, 0.86);
  --wlj-glass-dropdown-blur: saturate(150%) blur(48px);
  --wlj-modal-scrim: color-mix(in srgb, var(--wlj-primary, #60a5fa) 22%, rgba(15, 32, 74, 0.42));
  --wlj-modal-scrim-blur: blur(16px) saturate(70%);
  --wlj-modal-glass: var(--wlj-glass-dropdown);
  --wlj-modal-glass-blur: var(--wlj-glass-dropdown-blur);
  --wlj-modal-pane: var(--wlj-modal-glass);
  --wlj-modal-header: transparent;
  --wlj-glass-field: rgba(28, 28, 36, 0.32);
  --wlj-modal-field: rgba(28, 28, 36, 0.32);
  --wlj-modal-field-blur: saturate(150%) blur(20px);
  --wlj-modal-placeholder: #cbd5e1;
}
@media (prefers-reduced-transparency: reduce) {
  :root {
    --wlj-glass-sheen: none;
    --wlj-glass-overlay: rgba(255, 255, 255, 0.92);
    --wlj-glass-surface: rgba(255, 255, 255, 0.92);
    --wlj-glass-bg-pane: rgba(255, 255, 255, 0.96);
    --wlj-glass-blur-overlay: none;
    --wlj-glass-surface-blur: none;
    --wlj-glass-blur-pane: none;
    --wlj-glass-dropdown: rgba(255, 255, 255, 0.96);
    --wlj-glass-dropdown-blur: none;
    --wlj-glass-search-fill: rgba(255, 255, 255, 0.92);
    --wlj-glass-search-blur: none;
    --wlj-modal-scrim-blur: none;
    --wlj-modal-glass: rgba(255, 255, 255, 0.96);
    --wlj-modal-glass-blur: none;
    --wlj-modal-pane: rgba(255, 255, 255, 0.96);
    --wlj-modal-header: #ffffff;
  }
  html[data-theme="dark"] {
    --wlj-glass-overlay: rgba(28, 28, 36, 0.94);
    --wlj-glass-surface: rgba(28, 28, 36, 0.94);
    --wlj-glass-bg-pane: rgba(28, 28, 36, 0.96);
    --wlj-glass-sheen: none;
    --wlj-glass-dropdown: rgba(28, 28, 36, 0.96);
    --wlj-glass-dropdown-blur: none;
    --wlj-glass-search-fill: rgba(28, 28, 36, 0.94);
    --wlj-glass-search-blur: none;
    --wlj-modal-scrim-blur: none;
    --wlj-modal-glass: rgba(28, 28, 36, 0.96);
    --wlj-modal-glass-blur: none;
    --wlj-modal-pane: rgba(28, 28, 36, 0.96);
    --wlj-modal-header: rgba(15, 23, 42, 0.96);
  }
}
html body.wlj-neat-gradient-active,
html body.wlj-neat-gradient-active,
html:has(.wlj-neat-page-background) body,
html body.wlj-neat-gradient-active #root,
html body.wlj-neat-gradient-active .app,
html body.wlj-neat-gradient-active .rootLayout,
html body.wlj-neat-gradient-active .postfeed-center,
html body.wlj-neat-gradient-active .laptop-mobileview,
html body.wlj-neat-gradient-active .container-fluid.rootLayout,
html body.wlj-neat-gradient-active .wlj-shell-container,
html body.wlj-neat-gradient-active .leftSidebarData,
html body.wlj-neat-gradient-active .app > .container-fluid,
html body.wlj-neat-gradient-active .sideLinks,
html body.wlj-neat-gradient-active .sideLinks.leftNav,
html body.wlj-neat-gradient-active .rigtside-recommendation,
html body.wlj-neat-gradient-active .main-container,
html body.wlj-neat-gradient-active .main-container.connectionPage,
html body.wlj-neat-gradient-active .main-container.manageJobsPage,
html body.wlj-neat-gradient-active .manageJobsPage-ad,
html body.wlj-neat-gradient-active .manageJobs-section,
html body.wlj-neat-gradient-active .wlj-page-shell,
html body.wlj-neat-gradient-active .wlj-page-header,
html body.wlj-neat-gradient-active .job-search-workspace,
html body.wlj-neat-gradient-active .jobMainContainer,
html body.wlj-neat-gradient-active .job-search-list-column,
html body.wlj-neat-gradient-active .newsfeedPosts,
html body.wlj-neat-gradient-active .mainProfileContainer,
html body.wlj-neat-gradient-active .ProfileInner,
html body.wlj-neat-gradient-active .ProfileInner.new-ProfileInner,
html body.wlj-neat-gradient-active .profileTabContent,
html body.wlj-neat-gradient-active .profileTabContent > div,
html body.wlj-neat-gradient-active .follow-list,
html body.wlj-neat-gradient-active .job-notification-main-container,
html body.wlj-neat-gradient-active .Newjob-notification-main-container,
html body.wlj-neat-gradient-active .notification-main-container,
html body.wlj-neat-gradient-active .notification-messages,
html body.wlj-neat-gradient-active .friendsHeaderText,
html body.wlj-neat-gradient-active .jobHeader,
html body.wlj-neat-gradient-active .blog-detail-page-container,
html body.wlj-neat-gradient-active .blogContainer,
html body.wlj-neat-gradient-active .blogSingleCard,
html body.wlj-neat-gradient-active .blogs-custom-card {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .mainheader,
html body.wlj-neat-gradient-active .mainheader.headerLayout,
html body.wlj-neat-gradient-active .mainheader.desktopView,
html body.wlj-neat-gradient-active .mainheader.mobileView,
html body.wlj-neat-gradient-active .blog-nav-bar,
html body.wlj-neat-gradient-active .blogTab,
html body.wlj-neat-gradient-active .sidebar-light-card,
html body.wlj-neat-gradient-active .sidebar-widget-card,
html body.wlj-neat-gradient-active .sidebar-nav-card,
html body.wlj-neat-gradient-active .sidebar-companies-card,
html body.wlj-neat-gradient-active .sideLinks.leftNav .personal,
html body.wlj-neat-gradient-active .sideLinks.leftNav .personal.sidebar-light-card,
html body.wlj-neat-gradient-active .sideLinks.leftNav a.personal.sidebar-light-card,
html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-profile-link,
html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-profile-link .personal,
html body.wlj-neat-gradient-active .sideLinks.leftNav ul.sidebar-nav-card,
html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card,
html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-companies-card,
html body.wlj-neat-gradient-active .featured-card,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent,
html body.wlj-neat-gradient-active .createPostContainer,
html body.wlj-neat-gradient-active .postAdd:not(.modal),
html body.wlj-neat-gradient-active .draftPost,
html body.wlj-neat-gradient-active .PeopleCard,
html body.wlj-neat-gradient-active .jobCard,
html body.wlj-neat-gradient-active .IncreaseWidthJobCard .jobCard,
html body.wlj-neat-gradient-active .job-search-workspace .jobCard,
html body.wlj-neat-gradient-active .manageJobsPage .jobCard,
html body.wlj-neat-gradient-active .profileCard,
html body.wlj-neat-gradient-active .new-profileCard,
html body.wlj-neat-gradient-active .UpdateProfile,
html body.wlj-neat-gradient-active .aboutMe,
html body.wlj-neat-gradient-active .settingSidebar,
html body.wlj-neat-gradient-active .setting-container,
html body.wlj-neat-gradient-active .job-notification-card,
html body.wlj-neat-gradient-active .job-apps-card,
html body.wlj-neat-gradient-active .card:not(.ImagesliderScroller):not(.job-apps-table-card):not(.transaction-table),
html body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
html body.wlj-neat-gradient-active .modal .container.articleContainer,
html body.wlj-neat-gradient-active .container.articleContainer,
html body.wlj-neat-gradient-active .modal-content.articleContainer,
html body.wlj-neat-gradient-active .dropdown-menu,
html body.wlj-neat-gradient-active .dropdown-menu.show,
html body.wlj-neat-gradient-active .suggestions-dropdown,
html body.wlj-neat-gradient-active .notificationDropdown,
html body.wlj-neat-gradient-active .topmessages,
html body.wlj-neat-gradient-active .wlj-header-quick-panel,
html body.wlj-neat-gradient-active .popover,
html body.wlj-neat-gradient-active .offcanvas,
html body.wlj-neat-gradient-active .flatpickr-calendar,
html body.wlj-neat-gradient-active .react-datepicker,
html body.wlj-neat-gradient-active .react-select__menu,
html body.wlj-neat-gradient-active .ant-select-dropdown,
html body.wlj-neat-gradient-active .ant-picker-dropdown,
html body.wlj-neat-gradient-active .ant-picker-panel-container,
html body.wlj-neat-gradient-active .ant-modal-content,
html body.wlj-neat-gradient-active .MuiMenu-paper,
html body.wlj-neat-gradient-active .MuiDialog-paper,
html body.wlj-neat-gradient-active .MuiPopover-paper,
html body.wlj-neat-gradient-active .swal2-popup,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-sidebar,
html body.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
html body.wlj-neat-gradient-active .job-search-workspace .clear-all-filter-cont,
html body.wlj-neat-gradient-active .job-search-workspace .job-view-toggle,
html body.wlj-neat-gradient-active .manageJobsTab,
html body.wlj-neat-gradient-active .tabButton.connectionTab,
html body.wlj-neat-gradient-active .chat-box,
html body.wlj-neat-gradient-active .chat-box-main-container,
html body.wlj-neat-gradient-active .notification-header,
html body.wlj-neat-gradient-active .custom-message-card,
html body.wlj-neat-gradient-active .message-wrap .message-cards,
html body.wlj-neat-gradient-active [class*="Card"]:not(button):not(a):not(span):not(p):not(.ImagesliderScroller):not(.IncreaseWidthJobCard):not([class*="resume-card"]):not([class*="Header"]):not([class*="Container"]):not([class*="Button"]):not([class*="Text"]):not([class*="text"]):not([class*="Content"]):not([class*="content"]):not(.message-cards):not(.messageCardContent):not(.mainCardText):not(.mainCardText1):not(.mainCardTex):not([class*="ARCard"]),
html body.wlj-neat-gradient-active [class*="-card"]:not(button):not(a):not(span):not(p):not(.ImagesliderScroller):not([class*="resume-card"]):not(.message-cards):not(.custom-message-card):not(.job-apps-table-card):not(.wlj-card-actions):not([class*="action"]) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border-color: var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

/* Company Follow / Job Alert row is a layout wrapper, not a glass card. */
html body.wlj-neat-gradient-active .businessCardButton,
html body.wlj-neat-gradient-active .followerTabsNewsInnerbusinessCardButton,
html body.wlj-neat-gradient-active .businessCardButton.followerTabsNewsInnerbusinessCardButton {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
}

/* Follow / Connect chip row is a layout wrapper, not a card — no nested white slab. */
html body.wlj-neat-gradient-active .wlj-card-actions,
html body.wlj-neat-gradient-active .PeopleCard .wlj-card-actions,
html body.wlj-neat-gradient-active .follower-button .wlj-card-actions,
html body.wlj-neat-gradient-active .resume-action-button .wlj-card-actions {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
}

/* Nested quoted original stays glass so the canvas shows between cards. */
html body.wlj-neat-gradient-active .wlj-shared-original,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent.wlj-shared-original,
html body.wlj-neat-gradient-active .user-postOnlyContent .wlj-shared-original {
  background: color-mix(in srgb, var(--wlj-primary-soft) 36%, var(--wlj-glass-overlay)) !important;
  background-color: color-mix(in srgb, var(--wlj-primary-soft) 36%, var(--wlj-glass-overlay)) !important;
  background-image: var(--wlj-glass-sheen) !important;
  backdrop-filter: var(--wlj-glass-blur-overlay) !important;
  -webkit-backdrop-filter: var(--wlj-glass-blur-overlay) !important;
  border-color: color-mix(in srgb, var(--wlj-primary) 22%, var(--wlj-glass-edge)) !important;
  border-inline-start: 3px solid var(--wlj-primary) !important;
  box-shadow: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-shared-original,
html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card.user-postOnlyContent.wlj-shared-original,
html[data-theme="dark"] body.wlj-neat-gradient-active .user-postOnlyContent .wlj-shared-original,
body.darkMode.wlj-neat-gradient-active .wlj-shared-original,
body.darkMode.wlj-neat-gradient-active .featured-card.user-postOnlyContent.wlj-shared-original,
body.darkMode.wlj-neat-gradient-active .user-postOnlyContent .wlj-shared-original {
  background: color-mix(in srgb, var(--wlj-primary) 16%, var(--wlj-glass-overlay)) !important;
  background-color: color-mix(in srgb, var(--wlj-primary) 16%, var(--wlj-glass-overlay)) !important;
  border-color: color-mix(in srgb, var(--wlj-primary) 34%, var(--wlj-glass-edge)) !important;
}

html body.wlj-neat-gradient-active .follow-list,
html body.wlj-neat-gradient-active .notification-main-container,
html body.wlj-neat-gradient-active .notification-messages,
html body.wlj-neat-gradient-active .profileTabContent > div,
html body.wlj-neat-gradient-active .profileTabContent .tabButton.connectionTab + div,
html body.wlj-neat-gradient-active .profileTabContent .company-jobs-toolbar,
html body.wlj-neat-gradient-active .profileTabContent .company-jobs-toolbar .tabButton.connectionTab,
html body.wlj-neat-gradient-active .profileTabContent .company-jobs-toolbar .wlj-tab-bar {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  filter: none !important;
}

html body.wlj-neat-gradient-active .follow-list > .aboutMe,
html body.wlj-neat-gradient-active .profileTabContent .follow-list .aboutMe {
  box-shadow: none !important;
  isolation: isolate;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader,
html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader.headerLayout,
html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader.desktopView,
html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader.mobileView,
html[data-theme="dark"] body.wlj-neat-gradient-active .blog-nav-bar,
html[data-theme="dark"] body.wlj-neat-gradient-active .sidebar-light-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sidebar-widget-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sidebar-nav-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sidebar-companies-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .personal,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .personal.sidebar-light-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav a.personal.sidebar-light-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-profile-link,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-profile-link .personal,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav ul.sidebar-nav-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-companies-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card.user-postOnlyContent,
html[data-theme="dark"] body.wlj-neat-gradient-active .createPostContainer,
html[data-theme="dark"] body.wlj-neat-gradient-active .postAdd:not(.modal),
html[data-theme="dark"] body.wlj-neat-gradient-active .draftPost,
html[data-theme="dark"] body.wlj-neat-gradient-active .PeopleCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .IncreaseWidthJobCard .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .manageJobsPage .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .profileCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .new-profileCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .UpdateProfile,
html[data-theme="dark"] body.wlj-neat-gradient-active .aboutMe,
html[data-theme="dark"] body.wlj-neat-gradient-active .settingSidebar,
html[data-theme="dark"] body.wlj-neat-gradient-active .setting-container,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-notification-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-apps-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .card:not(.ImagesliderScroller):not(.job-apps-table-card):not(.transaction-table),
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
html[data-theme="dark"] body.wlj-neat-gradient-active .modal .container.articleContainer,
html[data-theme="dark"] body.wlj-neat-gradient-active .container.articleContainer,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content.articleContainer,
html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu.show,
html[data-theme="dark"] body.wlj-neat-gradient-active .suggestions-dropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .notificationDropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .topmessages,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-header-quick-panel,
html[data-theme="dark"] body.wlj-neat-gradient-active .popover,
html[data-theme="dark"] body.wlj-neat-gradient-active .offcanvas,
html[data-theme="dark"] body.wlj-neat-gradient-active .flatpickr-calendar,
html[data-theme="dark"] body.wlj-neat-gradient-active .react-datepicker,
html[data-theme="dark"] body.wlj-neat-gradient-active .react-select__menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-select-dropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-picker-dropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-picker-panel-container,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-modal-content,
html[data-theme="dark"] body.wlj-neat-gradient-active .MuiMenu-paper,
html[data-theme="dark"] body.wlj-neat-gradient-active .MuiDialog-paper,
html[data-theme="dark"] body.wlj-neat-gradient-active .MuiPopover-paper,
html[data-theme="dark"] body.wlj-neat-gradient-active .swal2-popup,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .job-search-sidebar,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .clear-all-filter-cont,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .job-view-toggle,
html[data-theme="dark"] body.wlj-neat-gradient-active .manageJobsTab,
html[data-theme="dark"] body.wlj-neat-gradient-active .tabButton.connectionTab,
html[data-theme="dark"] body.wlj-neat-gradient-active .chat-box,
html[data-theme="dark"] body.wlj-neat-gradient-active .chat-box-main-container,
html[data-theme="dark"] body.wlj-neat-gradient-active .notification-header,
html[data-theme="dark"] body.wlj-neat-gradient-active .custom-message-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .message-wrap .message-cards,
html[data-theme="dark"] body.wlj-neat-gradient-active [class*="Card"]:not(button):not(a):not(span):not(p):not(.ImagesliderScroller):not(.IncreaseWidthJobCard):not([class*="resume-card"]):not([class*="Header"]):not([class*="Container"]):not([class*="Button"]):not([class*="Text"]):not([class*="text"]):not([class*="Content"]):not([class*="content"]):not(.message-cards):not(.messageCardContent):not(.mainCardText):not(.mainCardText1):not(.mainCardTex):not([class*="ARCard"]),
html[data-theme="dark"] body.wlj-neat-gradient-active [class*="-card"]:not(button):not(a):not(span):not(p):not(.ImagesliderScroller):not([class*="resume-card"]):not(.message-cards):not(.custom-message-card):not(.job-apps-table-card):not(.wlj-card-actions):not([class*="action"]),
body.darkMode.wlj-neat-gradient-active .mainheader,
body.darkMode.wlj-neat-gradient-active .blog-nav-bar,
body.darkMode.wlj-neat-gradient-active .sidebar-light-card,
body.darkMode.wlj-neat-gradient-active .sidebar-widget-card,
body.darkMode.wlj-neat-gradient-active .sidebar-nav-card,
body.darkMode.wlj-neat-gradient-active .featured-card,
body.darkMode.wlj-neat-gradient-active .createPostContainer,
body.darkMode.wlj-neat-gradient-active .draftPost,
body.darkMode.wlj-neat-gradient-active .PeopleCard,
body.darkMode.wlj-neat-gradient-active .jobCard,
body.darkMode.wlj-neat-gradient-active .profileCard,
body.darkMode.wlj-neat-gradient-active .UpdateProfile,
body.darkMode.wlj-neat-gradient-active .aboutMe,
body.darkMode.wlj-neat-gradient-active .settingSidebar,
body.darkMode.wlj-neat-gradient-active .setting-container,
body.darkMode.wlj-neat-gradient-active .job-notification-card,
body.darkMode.wlj-neat-gradient-active .card:not(.ImagesliderScroller):not(.job-apps-table-card):not(.transaction-table),
body.darkMode.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
body.darkMode.wlj-neat-gradient-active .dropdown-menu,
body.darkMode.wlj-neat-gradient-active .suggestions-dropdown,
body.darkMode.wlj-neat-gradient-active .notificationDropdown,
body.darkMode.wlj-neat-gradient-active .topmessages,
body.darkMode.wlj-neat-gradient-active .wlj-header-quick-panel,
body.darkMode.wlj-neat-gradient-active .chat-box,
body.darkMode.wlj-neat-gradient-active .chat-box-main-container,
body.darkMode.wlj-neat-gradient-active .notification-header,
body.darkMode.wlj-neat-gradient-active .custom-message-card,
body.darkMode.wlj-neat-gradient-active .message-wrap .message-cards,
body.darkMode.wlj-neat-gradient-active [class*="Card"]:not(button):not(a):not([class*="Header"]):not([class*="Container"]):not([class*="Button"]):not([class*="Text"]):not([class*="Content"]):not([class*="content"]):not(.message-cards):not(.messageCardContent):not(.mainCardTex):not([class*="ARCard"]),
body.darkMode.wlj-neat-gradient-active [class*="-card"]:not(button):not(a):not(.message-cards):not(.custom-message-card):not(.wlj-card-actions):not([class*="action"]) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-glass-surface-edge) !important;
}

html body.wlj-neat-gradient-active .jobCard,
html body.wlj-neat-gradient-active .jobCard:hover,
html body.wlj-neat-gradient-active .PeopleCard:hover,
html body.wlj-neat-gradient-active .featured-card:hover,
html body.wlj-neat-gradient-active .draftPost:hover,
html body.wlj-neat-gradient-active .job-notification-card,
html body.wlj-neat-gradient-active .job-notification-card:hover {
  transform: none !important;
}

html body.wlj-neat-gradient-active .featured-card:hover,
html body.wlj-neat-gradient-active .jobCard:hover,
html body.wlj-neat-gradient-active .draftPost:hover,
html body.wlj-neat-gradient-active .help-card:hover,
html body.wlj-neat-gradient-active .blogSingleCard:hover,
html body.wlj-neat-gradient-active .articles-custom-card:hover,
html body.wlj-neat-gradient-active .dashboard-card:hover,
html body.wlj-neat-gradient-active .job-apps-card:hover,
html body.wlj-neat-gradient-active .wlj-about-mission-card:hover,
html body.wlj-neat-gradient-active .wlj-about-service-card:hover,
html body.wlj-neat-gradient-active .xb-accordion .block:hover,
html body.wlj-neat-gradient-active .related-articles-card:hover {
  border-color: color-mix(in srgb, var(--buttonBase, #00419f) 28%, var(--wlj-glass-surface-edge)) !important;
  box-shadow: var(--wlj-glass-shadow-hover) !important;
}

html body.wlj-neat-gradient-active .notification-table td,
html body.wlj-neat-gradient-active .notification-table th,
html body.wlj-neat-gradient-active .job-apps-table td,
html body.wlj-neat-gradient-active .job-apps-table th,
html body.wlj-neat-gradient-active .job-apps-table thead th,
html body.wlj-neat-gradient-active .job-apps-table tbody td,
html body.wlj-neat-gradient-active .table-header th,
html body.wlj-neat-gradient-active .job-apps-table-card,
html body.wlj-neat-gradient-active .job-apps-table-card.card,
html body.wlj-neat-gradient-active .card.transaction-table,
html body.wlj-neat-gradient-active .transaction-table,
html body.wlj-neat-gradient-active .job-notification-card .card,
html body.wlj-neat-gradient-active .job-notification-card .transaction-table,
html body.wlj-neat-gradient-active .job-notification-card .table-responsive,
html body.wlj-neat-gradient-active .job-notification-card .card-image-text,
html body.wlj-neat-gradient-active .job-notification-card .card-text,
html body.wlj-neat-gradient-active .job-apps-actions,
html body.wlj-neat-gradient-active .transaction-table td,
html body.wlj-neat-gradient-active .transaction-table th,
html body.wlj-neat-gradient-active .table > :not(caption) > * > *,
html body.wlj-neat-gradient-active .accordion-item,
html body.wlj-neat-gradient-active .accordion-button,
html body.wlj-neat-gradient-active .accordion-body,
html body.wlj-neat-gradient-active .wlj-about-service-card-inner,
html body.wlj-neat-gradient-active .wlj-about-service-card-icon,
html body.wlj-neat-gradient-active .wlj-about-service-card-shape,
html body.wlj-neat-gradient-active .wlj-about-mission-icon,
html body.wlj-neat-gradient-active .wlj-about-mission-text,
html body.wlj-neat-gradient-active .xb-accordion .block .acc-btn,
html body.wlj-neat-gradient-active .xb-accordion .block .acc_body,
html body.wlj-neat-gradient-active .xb-accordion .block .content,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-list,
html body.wlj-neat-gradient-active .help-search-box input,
html body.wlj-neat-gradient-active .help-search-box .category-dropdown,
html body.wlj-neat-gradient-active .help-search-box .helpcenter-search,
html body.wlj-neat-gradient-active .Jobfilter .accordion-item,
html body.wlj-neat-gradient-active .Jobfilter .accordion-button,
html body.wlj-neat-gradient-active .Jobfilter .accordion-body,
html body.wlj-neat-gradient-active .job-search-workspace .jobSearch.jobdropdown,
html body.wlj-neat-gradient-active .job-search-workspace .wlj-filter-bar,
html body.wlj-neat-gradient-active .job-search-workspace .Jobfilter,
html body.wlj-neat-gradient-active .job-search-workspace .IncreaseWidthJobCard,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-sidebar .advertisementview,
html body.wlj-neat-gradient-active .search-container input,
html body.wlj-neat-gradient-active .search-container .search-input,
html body.wlj-neat-gradient-active .searchInput .ant-select-selector,
html body.wlj-neat-gradient-active .searchInput .ant-select,
html body.wlj-neat-gradient-active .searchInput input,
html body.wlj-neat-gradient-active .wlj-header-quick-panel .topnotification,
html body.wlj-neat-gradient-active .wlj-header-quick-panel .message-cards,
html body.wlj-neat-gradient-active .wlj-header-quick-panel .dropdown-menu-header,
html body.wlj-neat-gradient-active .wlj-header-quick-panel .dropdown-menu-footer,
html body.wlj-neat-gradient-active .modal .articleContainer .card,
html body.wlj-neat-gradient-active .modal .articleContainer .card-body,
html body.wlj-neat-gradient-active .modal .articleContainer .Article-body,
html body.wlj-neat-gradient-active .modal .articleContainer .ArticleBannerHuzCard,
html body.wlj-neat-gradient-active .articleContainer .card,
html body.wlj-neat-gradient-active .articleContainer .card-body,
html body.wlj-neat-gradient-active .articleContainer .Article-body,
html body.wlj-neat-gradient-active .articleContainer .ArticleBannerHuzCard,
html body.wlj-neat-gradient-active .dropdown-menu .topnotification,
html body.wlj-neat-gradient-active .dropdown-menu .message-cards,
html body.wlj-neat-gradient-active .dropdown-menu .notificationscroller,
html body.wlj-neat-gradient-active .message-wrap .messageBoxNew,
html body.wlj-neat-gradient-active .message-wrap .messageBoxSecondNew,
html body.wlj-neat-gradient-active .messageBoxNew,
html body.wlj-neat-gradient-active .messageBoxSecondNew,
html body.wlj-neat-gradient-active .messageBoxNew .tabButton.connectionTab,
html body.wlj-neat-gradient-active .messageBoxNew .chatListCont,
html body.wlj-neat-gradient-active .messageBoxNew .resume-section,
html body.wlj-neat-gradient-active .message-wrap .main-card,
html body.wlj-neat-gradient-active .messageBoxNew .main-card,
html body.wlj-neat-gradient-active .chat-box-main-container .username-header,
html body.wlj-neat-gradient-active .chat-box-main-container .chat-search-bar,
html body.wlj-neat-gradient-active .chat-box-main-container .chat-main-content,
html body.wlj-neat-gradient-active .chat-box-main-container .write-message-text,
html body.wlj-neat-gradient-active .resume-card-inner,
html body.wlj-neat-gradient-active .resume-card-inner-New,
html body.wlj-neat-gradient-active .resume-card-upper,
html body.wlj-neat-gradient-active .connection-imageProf,
html body.wlj-neat-gradient-active .PeopleCardContainer,
html body.wlj-neat-gradient-active .PeopleCardHeader,
html body.wlj-neat-gradient-active .PeopleCard .centerAlignment,
html body.wlj-neat-gradient-active .PeopleCard .mainCardText,
html body.wlj-neat-gradient-active .PeopleCard .mainCardText1,
html body.wlj-neat-gradient-active .mainCardText,
html body.wlj-neat-gradient-active .mainCardText1,
html body.wlj-neat-gradient-active .mainCardTex,
html body.wlj-neat-gradient-active .PeopleCard .mainCardTex,
html body.wlj-neat-gradient-active .Outercircle-container,
html body.wlj-neat-gradient-active .frindsCardContainer,
html body.wlj-neat-gradient-active .sidebar-widget-card .listLink > div,
html body.wlj-neat-gradient-active .sidebar-widget-card li,
html body.wlj-neat-gradient-active .sidebar-widget-card .message-cards,
html body.wlj-neat-gradient-active .rigtside-recommendation .message-cards,
html body.wlj-neat-gradient-active .connection-card .message-cards,
html body.wlj-neat-gradient-active .ad-container .AdwrapperwithText,
html body.wlj-neat-gradient-active .ad-container .Adwrapper,
html body.wlj-neat-gradient-active .ad-container .ad-wrapper,
html body.wlj-neat-gradient-active .ad-container .google-ad-unit,
html body.wlj-neat-gradient-active .ad-container ins.adsbygoogle,
html body.wlj-neat-gradient-active .AdwrapperwithText .ad-wrapper,
html body.wlj-neat-gradient-active .AdwrapperwithText .google-ad-unit,
html body.wlj-neat-gradient-active .AdwrapperwithText ins.adsbygoogle,
html body.wlj-neat-gradient-active .mainProfileContainer .new-newsfeedPosts .ad-wrapper {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
}

/* Contact info is a dedicated column — keep it opaque so chat never shows through. */
html body .chat-user-details-panel,
html body.wlj-neat-gradient-active .chat-user-details-panel,
html body .messageBoxSecondNew .chat-user-details-panel,
html body.wlj-neat-gradient-active .messageBoxSecondNew .chat-user-details-panel {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border-left: 1px solid var(--wlj-glass-edge, var(--wlj-border)) !important;
  box-shadow: none !important;
  isolation: isolate;
}

html body .chat-user-details-header,
html body .chat-user-details-body,
html body .chat-user-details-footer,
html body.wlj-neat-gradient-active .chat-user-details-header,
html body.wlj-neat-gradient-active .chat-user-details-body,
html body.wlj-neat-gradient-active .chat-user-details-footer {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body .messageBoxSecondNew .chat-box-main-container.contact-panel-open,
html body.wlj-neat-gradient-active .messageBoxSecondNew .chat-box-main-container.contact-panel-open {
  flex-direction: row !important;
}

html[dir="rtl"] body .messageBoxSecondNew .chat-box-main-container.contact-panel-open,
html[dir="rtl"] body.wlj-neat-gradient-active .messageBoxSecondNew .chat-box-main-container.contact-panel-open {
  flex-direction: row-reverse !important;
}

html[dir="rtl"] body .chat-user-details-panel,
html[dir="rtl"] body.wlj-neat-gradient-active .chat-user-details-panel {
  border-left: none !important;
  border-right: 1px solid var(--wlj-glass-edge, var(--wlj-border)) !important;
}

html body .chat-thread-pane,
html body.wlj-neat-gradient-active .chat-thread-pane {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

@media (max-width: 767px) {
  html body .messageBoxSecondNew .chat-box-main-container.contact-panel-open,
  html body.wlj-neat-gradient-active .messageBoxSecondNew .chat-box-main-container.contact-panel-open,
  html[dir="rtl"] body .messageBoxSecondNew .chat-box-main-container.contact-panel-open,
  html[dir="rtl"] body.wlj-neat-gradient-active .messageBoxSecondNew .chat-box-main-container.contact-panel-open {
    flex-direction: column !important;
  }

  html[dir="rtl"] body .chat-user-details-panel,
  html[dir="rtl"] body.wlj-neat-gradient-active .chat-user-details-panel {
    border-right: none !important;
  }
}

/* Notification inner rows stay clear — each custom-message-card is its own glass shell */
html body.wlj-neat-gradient-active .notification-main-container .notification-messages > a,
html body.wlj-neat-gradient-active .messageCardContent,
html body.wlj-neat-gradient-active .messageMainText,
html body.wlj-neat-gradient-active .messageSubText,
html body.wlj-neat-gradient-active .custom-message-card .msgLeft,
html body.wlj-neat-gradient-active .custom-message-card .messageCardContent,
html body.wlj-neat-gradient-active .custom-message-card h1,
html body.wlj-neat-gradient-active .custom-message-card p,
html body.wlj-neat-gradient-active .custom-message-card span,
html body.wlj-neat-gradient-active .custom-message-card a {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .notification-main-container .custom-message-card {
  isolation: isolate;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .notification-main-container .custom-message-card:hover {
  border-color: color-mix(in srgb, var(--buttonBase, #00419f) 28%, var(--wlj-glass-surface-edge)) !important;
  box-shadow: var(--wlj-glass-shadow-hover) !important;
}

/* Nested Achievement / Responsibilities — no second frost (parent already frosts) */
html body.wlj-neat-gradient-active .ARCard,
html body.wlj-neat-gradient-active .ARCard-header,
html body.wlj-neat-gradient-active .ARCard-body,
html body.wlj-neat-gradient-active .ARCard-item,
html body.wlj-neat-gradient-active .ARCard-item-main,
html body.wlj-neat-gradient-active .ARCard-title,
html body.wlj-neat-gradient-active .ARCard-heading,
html body.wlj-neat-gradient-active .ARCard-empty,
html body.wlj-neat-gradient-active .UpdateProfile .ARCard,
html body.wlj-neat-gradient-active .analytic-sub .ARCard {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .ARCard {
  border: 1px solid var(--wlj-glass-divider, rgba(16, 24, 40, 0.08)) !important;
}

html body.wlj-neat-gradient-active .ARCard-header {
  border-bottom: 1px solid var(--wlj-glass-divider, rgba(16, 24, 40, 0.08)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .ARCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .UpdateProfile .ARCard,
body.darkMode.wlj-neat-gradient-active .ARCard {
  background: transparent !important;
  background-color: transparent !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .ARCard-header,
body.darkMode.wlj-neat-gradient-active .ARCard-header {
  background: transparent !important;
  border-bottom-color: rgba(255, 255, 255, 0.1) !important;
}

html body.wlj-neat-gradient-active .form-control,
html body.wlj-neat-gradient-active .form-select,
html body.wlj-neat-gradient-active input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.connection-input):not(.discovery-search):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active input[type="search"]:not(.react-select__input):not([class*="__input"]):not(.search-input):not(.connection-input):not(.discovery-search):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active input[type="email"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active input[type="date"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active textarea,
html body.wlj-neat-gradient-active select,
html body.wlj-neat-gradient-active .react-select__control,
html body.wlj-neat-gradient-active .wlj-select__control,
html body.wlj-neat-gradient-active [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .flatpickr-input,
html body.wlj-neat-gradient-active .ant-input,
html body.wlj-neat-gradient-active .ant-select-selector,
html body.wlj-neat-gradient-active .ant-picker,
html body.wlj-neat-gradient-active .wlj-filter-chip:not(.is-active),
html body.wlj-neat-gradient-active .wljSecondaryBtn {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border-color: var(--wlj-field-border) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus-visible,
html body.wlj-neat-gradient-active .ReplyComment .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active .addComment .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active .commentBox > input,
html body.wlj-neat-gradient-active .commentBox > input:focus,
html body.wlj-neat-gradient-active .commentBox textarea,
html body.wlj-neat-gradient-active .commentBox textarea:focus {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .form-control,
html[data-theme="dark"] body.wlj-neat-gradient-active .form-select,
html[data-theme="dark"] body.wlj-neat-gradient-active input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html[data-theme="dark"] body.wlj-neat-gradient-active input[type="search"]:not(.react-select__input):not([class*="__input"]):not(.search-input):not(.ant-select-selection-search-input),
html[data-theme="dark"] body.wlj-neat-gradient-active input[type="email"]:not(.react-select__input):not([class*="__input"]),
html[data-theme="dark"] body.wlj-neat-gradient-active input[type="date"]:not(.react-select__input):not([class*="__input"]),
html[data-theme="dark"] body.wlj-neat-gradient-active textarea,
html[data-theme="dark"] body.wlj-neat-gradient-active select,
html[data-theme="dark"] body.wlj-neat-gradient-active .react-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .flatpickr-input,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-input,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-select-selector,
html[data-theme="dark"] body.wlj-neat-gradient-active .ant-picker,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-filter-chip:not(.is-active),
html[data-theme="dark"] body.wlj-neat-gradient-active .wljSecondaryBtn,
body.darkMode.wlj-neat-gradient-active .form-control,
body.darkMode.wlj-neat-gradient-active .form-select,
body.darkMode.wlj-neat-gradient-active input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
body.darkMode.wlj-neat-gradient-active textarea,
body.darkMode.wlj-neat-gradient-active select,
body.darkMode.wlj-neat-gradient-active .wljSecondaryBtn {
  background: rgba(28, 28, 36, 0.46) !important;
  background-color: rgba(28, 28, 36, 0.46) !important;
  color: #f1f5f9 !important;
  border-color: rgba(255, 255, 255, 0.16) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
body.darkMode.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
body.darkMode.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox > input,
body.darkMode.wlj-neat-gradient-active .commentBox > input,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox textarea,
body.darkMode.wlj-neat-gradient-active .commentBox textarea {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  border-width: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  color: inherit !important;
}

html body.wlj-neat-gradient-active .featured-card .rounded.pe-1,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .rounded.pe-1,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .rounded.pe-1,
html body.wlj-neat-gradient-active .new-newsfeedPosts .featured-card.user-postOnlyContent .rounded.pe-1,
html body.wlj-neat-gradient-active .featured-card .mainComment.rounded,
html body.wlj-neat-gradient-active .user-postOnlyContent .mainComment.rounded {
  background: var(--wlj-glass-sheen), var(--wlj-glass-field, rgba(255, 255, 255, 0.46)) !important;
  background-color: var(--wlj-glass-field, rgba(255, 255, 255, 0.46)) !important;
  backdrop-filter: saturate(180%) blur(16px) !important;
  -webkit-backdrop-filter: saturate(180%) blur(16px) !important;
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.65)) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), 0 1px 2px rgba(16, 24, 40, 0.06) !important;
}

html body.wlj-neat-gradient-active .featured-card .commentBox,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .commentBox,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .ReplyComment .commentBox,
html body.wlj-neat-gradient-active .addComment .commentBox,
html body.wlj-neat-gradient-active .postCommentWrapper .commentBox {
  position: relative !important;
  overflow: hidden !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  background-image: none !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

html body.wlj-neat-gradient-active .featured-card .commentBox::before,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .commentBox::before,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body.wlj-neat-gradient-active .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body.wlj-neat-gradient-active .user-postOnlyContent .commentArea .commentBox::before,
html body.wlj-neat-gradient-active .ReplyComment .commentBox::before,
html body.wlj-neat-gradient-active .addComment .commentBox::before,
html body.wlj-neat-gradient-active .postCommentWrapper .commentBox::before {
  content: none !important;
  display: none !important;
}

html body.wlj-neat-gradient-active .commentBox > * {
  position: relative;
  z-index: 1;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card .rounded.pe-1,
html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .rounded.pe-1,
html[data-theme="dark"] body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .rounded.pe-1,
html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card .mainComment.rounded,
body.darkMode.wlj-neat-gradient-active .featured-card .rounded.pe-1,
body.darkMode.wlj-neat-gradient-active .featured-card.user-postOnlyContent .rounded.pe-1,
body.darkMode.wlj-neat-gradient-active .featured-card .mainComment.rounded {
  background: var(--wlj-glass-sheen), rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: saturate(160%) blur(16px) !important;
  -webkit-backdrop-filter: saturate(160%) blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.16) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card .commentBox,
html[data-theme="dark"] body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html[data-theme="dark"] body.wlj-neat-gradient-active .ReplyComment .commentBox,
html[data-theme="dark"] body.wlj-neat-gradient-active .addComment .commentBox,
body.darkMode.wlj-neat-gradient-active .featured-card .commentBox,
body.darkMode.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
body.darkMode.wlj-neat-gradient-active .ReplyComment .commentBox,
body.darkMode.wlj-neat-gradient-active .addComment .commentBox {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  border-color: var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .featured-card .commentBox::before,
html[data-theme="dark"] body.wlj-neat-gradient-active .ReplyComment .commentBox::before,
html[data-theme="dark"] body.wlj-neat-gradient-active .addComment .commentBox::before,
body.darkMode.wlj-neat-gradient-active .featured-card .commentBox::before,
body.darkMode.wlj-neat-gradient-active .ReplyComment .commentBox::before,
body.darkMode.wlj-neat-gradient-active .addComment .commentBox::before {
  content: none !important;
  display: none !important;
}

/* Nested patches on Job Applications — one frost on the outer card only */
html body.wlj-neat-gradient-active .job-apps-table,
html body.wlj-neat-gradient-active .notification-table {
  --bs-table-bg: transparent !important;
  --bs-table-accent-bg: transparent !important;
  --bs-table-striped-bg: transparent !important;
  --bs-table-hover-bg: transparent !important;
  background: transparent !important;
}

html body.wlj-neat-gradient-active .job-apps-table tbody tr:hover td {
  background: rgba(0, 65, 159, 0.05) !important;
  background-color: rgba(0, 65, 159, 0.05) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .job-apps-actions .wljSecondaryBtn,
html body.wlj-neat-gradient-active .job-apps-actions .job-apps-btn-reject,
html body.wlj-neat-gradient-active .job-apps-actions .job-apps-btn-danger,
html body.wlj-neat-gradient-active .job-notification-card .wljSecondaryBtn,
html body.wlj-neat-gradient-active .job-notification-card .job-apps-btn-reject,
html body.wlj-neat-gradient-active .job-notification-card .job-apps-btn-danger {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card .navLinks:hover {
  background: color-mix(in srgb, var(--buttonBase, #00419f) 8%, transparent) !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active .navLinks,
html body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active.navLinks {
  background: color-mix(in srgb, var(--buttonBase, #00419f) 12%, transparent) !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card .navLinks:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active .navLinks,
html[data-theme="dark"] body.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active.navLinks,
body.darkMode.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card .navLinks:hover,
body.darkMode.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active .navLinks,
body.darkMode.wlj-neat-gradient-active .sideLinks.leftNav .sidebar-nav-card a.active.navLinks {
  background: color-mix(in srgb, #4d94ff 16%, transparent) !important;
  background-image: none !important;
}

html body.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
html body.wlj-neat-gradient-active .job-search-workspace .clear-all-filter-cont,
html body.wlj-neat-gradient-active .job-search-workspace .job-view-toggle,
html body.wlj-neat-gradient-active .job-search-workspace .job-sort-dropdown select,
html body.wlj-neat-gradient-active .job-search-workspace .wlj-filter-chip:not(.is-active) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border-color: var(--wlj-field-border) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .clear-all-filter-cont,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .job-view-toggle,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .job-sort-dropdown select,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-search-workspace .wlj-filter-chip:not(.is-active),
body.darkMode.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
body.darkMode.wlj-neat-gradient-active .job-search-workspace .clear-all-filter-cont,
body.darkMode.wlj-neat-gradient-active .job-search-workspace .job-view-toggle,
body.darkMode.wlj-neat-gradient-active .job-search-workspace .job-sort-dropdown select,
body.darkMode.wlj-neat-gradient-active .job-search-workspace .wlj-filter-chip:not(.is-active) {
  background: rgba(28, 28, 36, 0.38) !important;
  background-color: rgba(28, 28, 36, 0.38) !important;
}

html body.wlj-neat-gradient-active .wlj-search-field,
html body.wlj-neat-gradient-active .sidebar-widget-card .search-container,
html body.wlj-neat-gradient-active .right-sidebar-split .search-container,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container {
  height: var(--wlj-control-h, 44px) !important;
  min-height: var(--wlj-touch, 44px) !important;
  padding: 0 12px !important;
  border-radius: var(--wlj-input-radius, 4px) !important;
}

html body.wlj-neat-gradient-active .search-container input,
html body.wlj-neat-gradient-active .search-container .search-input,
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active input.search-input,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]),
body.darkMode.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .searchInput .ant-select-selector,
html body.wlj-neat-gradient-active .searchInput .ant-select,
html body.wlj-neat-gradient-active .searchInput input,
html body.wlj-neat-gradient-active .job-search-workspace .jobSearch.jobdropdown,
html body.wlj-neat-gradient-active .job-search-workspace .wlj-filter-bar,
html body.wlj-neat-gradient-active .job-search-workspace .Jobfilter,
html body.wlj-neat-gradient-active .job-search-workspace .IncreaseWidthJobCard,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-sidebar .advertisementview {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .search-container input[type="search"],
html body.wlj-neat-gradient-active .search-container input.search-input,
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"] {
  -webkit-appearance: none;
  appearance: none;
}

html body.wlj-neat-gradient-active .job-search-workspace .jobSearch.jobdropdown,
html body.wlj-neat-gradient-active .job-search-workspace .wlj-filter-bar,
html body.wlj-neat-gradient-active .job-search-workspace .IncreaseWidthJobCard {
  border-color: transparent !important;
}

/* Sticky job search chrome — keep canvas visible, no white slabs */
html body.wlj-neat-gradient-active .job-search-workspace .job-search-sticky-chrome,
html body.wlj-neat-gradient-active .job-search-workspace .job-list-panel .job-header {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  isolation: auto;
}

html body.wlj-neat-gradient-active .wljPrimaryBtn,
html body.wlj-neat-gradient-active .mainBtn,
html body.wlj-neat-gradient-active .btn-primary,
html body.wlj-neat-gradient-active .wlj-filter-chip.is-active {
  background: var(--buttonBase, #00419f) !important;
  background-color: var(--buttonBase, #00419f) !important;
  color: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .modal-content.ImagesliderScroller,
html body.wlj-neat-gradient-active .ImagesliderScroller,
html body.wlj-neat-gradient-active .user-resume {
  background: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

@media (max-width: 991.98px) {
  html body.wlj-neat-gradient-active .chat-box-main-container {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
}

html body.wlj-neat-gradient-active .main-container.manageJobsPage,
html body.wlj-neat-gradient-active .main-container.manageJobsPage.job-history,
html body.wlj-neat-gradient-active .main-container.manageJobsPage.wlj-page-shell,
html body.wlj-neat-gradient-active .leftSidebarData,
html body.wlj-neat-gradient-active .manageJobsPage-ad,
html body.wlj-neat-gradient-active .connectionPage-ad,
html body.wlj-neat-gradient-active .manageJobs-section,
html body.wlj-neat-gradient-active .connectionPage .resume-section,
html body.wlj-neat-gradient-active .job-history .card-container {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
}

html body.wlj-neat-gradient-active .manageJobsPage .manageJobsTab,
html body.wlj-neat-gradient-active .manageJobsPage .tabButton.connectionTab,
html body.wlj-neat-gradient-active .manageJobsPage .wlj-tab-bar,
html body.wlj-neat-gradient-active .job-history .tabButton.connectionTab,
html body.wlj-neat-gradient-active .connectionPage .allconnectionTab,
html body.wlj-neat-gradient-active .connectionPage .tabButton.connectionTab,
html body.wlj-neat-gradient-active .connectionPage .wlj-tab-bar {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

html body.wlj-neat-gradient-active .manageJobsPage .jobCard,
html body.wlj-neat-gradient-active .job-history .jobCard,
html body.wlj-neat-gradient-active .job-history .card-container .jobCard,
html body.wlj-neat-gradient-active .leftSidebarData .jobCard,
html body.wlj-neat-gradient-active .manageJobsPage-ad .Adwrapper,
html body.wlj-neat-gradient-active .connectionPage-ad .Adwrapper {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  transform: none !important;
}

html body.wlj-neat-gradient-active .main-container.connectionPage,
html body.wlj-neat-gradient-active .main-container.connectionPage.wlj-page-shell {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
}

html body.wlj-neat-gradient-active .connectionPage .resume-section {
  gap: 8px !important;
}

html body.wlj-neat-gradient-active .connectionPage .resume-cont,
html body.wlj-neat-gradient-active .connectionPage .resume-cont.New-resume-cont {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  border-radius: var(--wlj-card-radius, 4px) !important;
  transform: none !important;
  margin-bottom: 0 !important;
}

html body.wlj-neat-gradient-active .connectionPage .resume-section .row:last-child .resume-cont,
html body.wlj-neat-gradient-active .connectionPage .resume-cont:last-child {
  border-bottom: 1px solid var(--wlj-glass-surface-edge) !important;
}

html body.wlj-neat-gradient-active .connectionPage .resume-cont:hover,
html body.wlj-neat-gradient-active .connectionPage .resume-cont.New-resume-cont:hover,
html body.wlj-neat-gradient-active .connectionPage .resume-cont:focus-within {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  box-shadow: var(--wlj-shadow-lg, 0 8px 24px rgba(16, 24, 40, 0.12)) !important;
  border-color: color-mix(in srgb, var(--wlj-primary, var(--buttonBase, #00419f)) 35%, var(--wlj-glass-surface-edge, rgba(255, 255, 255, 0.4))) !important;
  transform: none !important;
}

/* Message list — each conversation is its own glass card, like Connections */
html body.wlj-neat-gradient-active .message-wrap .message-scroller {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: 8px;
  padding: 4px 8px 16px;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

html body.wlj-neat-gradient-active .message-wrap .message-list-item,
html body.wlj-neat-gradient-active .message-wrap .message-list-link {
  width: 100%;
}

html body.wlj-neat-gradient-active .message-wrap .message-cards {
  isolation: isolate;
  margin: 0 !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  border-radius: var(--wlj-card-radius, 4px) !important;
  transform: none !important;
}

html body.wlj-neat-gradient-active .message-wrap .message-cards:hover:not(.activeMsg),
html body.wlj-neat-gradient-active .message-wrap .message-cards:focus-within:not(.activeMsg) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  box-shadow: var(--wlj-shadow-lg, 0 8px 24px rgba(16, 24, 40, 0.12)) !important;
  border-color: color-mix(in srgb, var(--wlj-primary, var(--buttonBase, #00419f)) 35%, var(--wlj-glass-surface-edge, rgba(255, 255, 255, 0.4))) !important;
  transform: none !important;
}

html body.wlj-neat-gradient-active .message-wrap .message-cards.activeMsg {
  background: color-mix(
      in srgb,
      var(--wlj-primary, var(--buttonBase, #00419f)) 12%,
      var(--wlj-glass-surface, rgba(255, 255, 255, 0.14))
    ) !important;
  background-color: color-mix(
      in srgb,
      var(--wlj-primary, var(--buttonBase, #00419f)) 12%,
      var(--wlj-glass-surface, rgba(255, 255, 255, 0.14))
    ) !important;
  border-color: color-mix(in srgb, var(--wlj-primary, var(--buttonBase, #00419f)) 42%, var(--wlj-glass-surface-edge)) !important;
  box-shadow: var(--wlj-glass-shadow-hover, var(--wlj-shadow-lg)) !important;
}

html body.wlj-neat-gradient-active .connectionPage .wlj-search-field {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .connectionPage .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .connectionPage .wlj-search-field .connection-input,
html body.wlj-neat-gradient-active .connectionPage .wlj-search-field .HuzChnagesConnection {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .manageJobsPage .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .job-history .jobCard,
html[data-theme="dark"] body.wlj-neat-gradient-active .manageJobsPage-ad .Adwrapper,
html[data-theme="dark"] body.wlj-neat-gradient-active .connectionPage-ad .Adwrapper,
body.darkMode.wlj-neat-gradient-active .manageJobsPage .jobCard,
body.darkMode.wlj-neat-gradient-active .job-history .jobCard,
body.darkMode.wlj-neat-gradient-active .manageJobsPage-ad .Adwrapper,
body.darkMode.wlj-neat-gradient-active .connectionPage-ad .Adwrapper {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-glass-surface-edge) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .connectionPage .wlj-search-field,
body.darkMode.wlj-neat-gradient-active .connectionPage .wlj-search-field {
  background: transparent !important;
  background-color: transparent !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .connectionPage .wlj-search-field .form-control,
html[data-theme="dark"] body.wlj-neat-gradient-active .connectionPage .wlj-search-field .connection-input,
body.darkMode.wlj-neat-gradient-active .connectionPage .wlj-search-field .form-control,
body.darkMode.wlj-neat-gradient-active .connectionPage .wlj-search-field .connection-input {
  background: transparent !important;
  background-color: transparent !important;
}

html body.wlj-neat-gradient-active .jobCard .jobAction .mainBtn4,
html body.wlj-neat-gradient-active .manageJobsPage .jobCard .mainBtn4,
html body.wlj-neat-gradient-active .job-history .jobCard .mainBtn4 {
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1.5px solid var(--buttonBase, #00419f) !important;
  color: var(--buttonBase, #00419f) !important;
  border-radius: var(--wlj-radius-btn, 4px) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .jobCard .jobAction .mainBtn4:hover,
html body.wlj-neat-gradient-active .manageJobsPage .jobCard .mainBtn4:hover,
html body.wlj-neat-gradient-active .job-history .jobCard .mainBtn4:hover {
  background: var(--buttonBase, #00419f) !important;
  color: #fff !important;
}

html body.wlj-neat-gradient-active .jobCard .jobAction .mainBtn:not(.appliedBtn) {
  background: var(--buttonBase, #00419f) !important;
  background-color: var(--buttonBase, #00419f) !important;
  color: #fff !important;
  border-radius: var(--wlj-radius-btn, 4px) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .jobCard .jobAction .mainBtn4,
html[data-theme="dark"] body.wlj-neat-gradient-active .manageJobsPage .jobCard .mainBtn4,
body.darkMode.wlj-neat-gradient-active .jobCard .jobAction .mainBtn4 {
  background: #1c1c24 !important;
  background-color: #1c1c24 !important;
}

/* Global search pane: match notification glass. Parent backdrop-filter
   on .search-container / .mainheader blocks child frost, so bar frost
   lives on ::before and header frost drops while a pane is open. */
html body.wlj-neat-gradient-active .mainheader:has(.suggestions-dropdown),
html body.wlj-neat-gradient-active .mainheader:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .mainheader.headerLayout:has(.suggestions-dropdown),
html body.wlj-neat-gradient-active .mainheader.headerLayout:has(.dropdown-menu.show) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .mainheader .search-container,
html body.wlj-neat-gradient-active .headerLayout .search-container,
html body.wlj-neat-gradient-active .mainheader.headerLayout.desktopView .search-container,
html body.wlj-neat-gradient-active .mainheader.mobileView .search-container {
  height: var(--wlj-control-h, 44px) !important;
  min-height: var(--wlj-touch, 44px) !important;
  padding: 0 12px !important;
  border-radius: var(--wlj-input-radius, 4px) !important;
}

html body.wlj-neat-gradient-active .mainheader .search-container:hover,
html body.wlj-neat-gradient-active .headerLayout .search-container:hover,
html body.wlj-neat-gradient-active .mainheader.mobileView .search-container:hover {
  border-color: color-mix(in srgb, var(--wlj-primary) 28%, var(--wlj-field-border)) !important;
}

html body.wlj-neat-gradient-active .mainheader .search-container:focus-within,
html body.wlj-neat-gradient-active .headerLayout .search-container:focus-within,
html body.wlj-neat-gradient-active .mainheader.mobileView .search-container:focus-within {
  border-color: var(--wlj-primary) !important;
  box-shadow: var(--wlj-focus-ring) !important;
}

html body.wlj-neat-gradient-active .mainheader .search-container,
html body.wlj-neat-gradient-active .headerLayout .search-container,
html body.wlj-neat-gradient-active .mainheader.mobileView .search-container {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .mainheader .search-container > .search-icon,
html body.wlj-neat-gradient-active .mainheader .search-container > .search-input-container,
html body.wlj-neat-gradient-active .headerLayout .search-container > .search-icon,
html body.wlj-neat-gradient-active .headerLayout .search-container > .search-input-container {
  position: relative;
  z-index: 1;
}

html body.wlj-neat-gradient-active .suggestions-dropdown,
html body.wlj-neat-gradient-active .headerLayout .suggestions-dropdown,
html body.wlj-neat-gradient-active .mainheader .suggestions-dropdown,
html body.wlj-neat-gradient-active .mainheader.mobileView .suggestions-dropdown {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .suggestions-dropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .headerLayout .suggestions-dropdown,
html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader .suggestions-dropdown,
body.darkMode.wlj-neat-gradient-active .suggestions-dropdown,
body.darkMode.wlj-neat-gradient-active .headerLayout .suggestions-dropdown,
body.darkMode.wlj-neat-gradient-active .mainheader .suggestions-dropdown {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-glass-surface-edge) !important;
}

/* Action menus / emoji pickers sit inside glass cards. Parent backdrop-filter is a
   backdrop root, so the overlay cannot sample the page the way
   .notificationDropdown / .mainheader do. Drop card frost while open. */
html body.wlj-neat-gradient-active .featured-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .featured-card:has(.InteractionModal),
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor),
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.InteractionModal),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.HuzEmojiPickerChangeColor),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.themed-emoji-picker-wrap),
html body.wlj-neat-gradient-active .draftPost:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .draftPost:has(.InteractionModal),
html body.wlj-neat-gradient-active .user-postOnlyContent:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .user-postOnlyContent:has(.HuzEmojiPickerChangeColor),
html body.wlj-neat-gradient-active .user-postOnlyContent:has(.themed-emoji-picker-wrap),
html body.wlj-neat-gradient-active .PeopleCard:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .PeopleCard:has(.InteractionModal),
html body.wlj-neat-gradient-active .HuzPeopleCard:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .jobCard:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .jobCard:has(.InteractionModal),
html body.wlj-neat-gradient-active .pr-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .aboutMe:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .aboutMe:has(.InteractionModal),
html body.wlj-neat-gradient-active .follow-list > .aboutMe:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .follow-list > .aboutMe:has(.InteractionModal),
html body.wlj-neat-gradient-active .profileCard:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .profileCard:has(.InteractionModal),
html body.wlj-neat-gradient-active .UpdateProfile:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .sidebar-widget-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .rigtside-recommendation:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .Recommendations-hashtags:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .search-result-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .sr-people-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .sr-post-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .sr-job-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .job-recommend-item:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .job-notification-card:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .SingleJob:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .IncreaseWidthJobCard:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .resume-cont:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .message-cards:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .custom-message-card:has(.dropdown-menu.show) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  isolation: auto !important;
  transform: none !important;
  filter: none !important;
  contain: none !important;
  overflow: visible !important;
}

html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor),
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.HuzEmojiPickerChangeColor),
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent:has(.themed-emoji-picker-wrap) {
  z-index: 50 !important;
}

html body.wlj-neat-gradient-active .featured-card:has(.InteractionModal.show) .postCommentWrapper,
html body.wlj-neat-gradient-active .featured-card:has(.dropdown-menu.show) .postCommentWrapper,
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor) .postCommentWrapper,
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap) .postCommentWrapper,
html body.wlj-neat-gradient-active .featured-card:has(.InteractionModal.show) .commentArea,
html body.wlj-neat-gradient-active .featured-card:has(.dropdown-menu.show) .commentArea,
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor) .commentArea,
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap) .commentArea,
html body.wlj-neat-gradient-active .featured-card:has(.InteractionModal.show) .addComment,
html body.wlj-neat-gradient-active .featured-card:has(.dropdown-menu.show) .addComment,
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor) .addComment,
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap) .addComment,
html body.wlj-neat-gradient-active .postCommentWrapper:has(.InteractionModal.show),
html body.wlj-neat-gradient-active .postCommentWrapper:has(.dropdown-menu.show),
html body.wlj-neat-gradient-active .commentArea:has(.InteractionModal.show),
html body.wlj-neat-gradient-active .commentArea:has(.dropdown-menu.show) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .notificationDropdown,
html body.wlj-neat-gradient-active .topmessages,
html body.wlj-neat-gradient-active .top-notifications-menu,
html body.wlj-neat-gradient-active .suggestions-dropdown,
html body.wlj-neat-gradient-active .headerLayout .suggestions-dropdown,
html body.wlj-neat-gradient-active .mainheader .suggestions-dropdown,
html body.wlj-neat-gradient-active .mainheader.mobileView .suggestions-dropdown {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

html body.wlj-neat-gradient-active .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu):not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .InteractionModal.dropdown-menu:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .dropdown-menu.InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .postMenu .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .postMenu .dropdown-menu:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .tabDropdownWrapper .dropdown-menu:not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .chat-message-menu,
html body.wlj-neat-gradient-active .chat-header-menu,
html body.wlj-neat-gradient-active .message-list-menu,
html body.wlj-neat-gradient-active .rigtside-recommendation .dropdown-menu:not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .Recommendations-hashtags .dropdown-menu:not(.wlj-company-action-menu) {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  isolation: isolate;
  z-index: 1080 !important;
}

html body.wlj-neat-gradient-active .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu):not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .InteractionModal.dropdown-menu:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .dropdown-menu.InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .postMenu .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu)::before,
html body.wlj-neat-gradient-active .chat-message-menu::before,
html body.wlj-neat-gradient-active .chat-header-menu::before,
html body.wlj-neat-gradient-active .message-list-menu::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 0;
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown);
  background-color: var(--wlj-glass-dropdown);
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
}

html body.wlj-neat-gradient-active .InteractionModal > *,
html body.wlj-neat-gradient-active .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu) > *,
html body.wlj-neat-gradient-active .commentInteraction .InteractionModal > * {
  position: relative;
  z-index: 1;
}

html body.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu),
html body.wlj-neat-gradient-active .commentInteraction .InteractionModal.dropdown-menu:not(.wlj-company-action-menu) {
  background: transparent !important;
  background-color: transparent !important;
}

html body.wlj-neat-gradient-active .mainheader .user,
html body.wlj-neat-gradient-active .mainheader .user > .dropdown-menu,
html body.wlj-neat-gradient-active .user .language-menu-item {
  overflow: visible !important;
}

/* Language fly-out is nested in the profile dropdown. Parent
   backdrop-filter is a backdrop root, so the fly-out cannot frost
   the page and labels wash out. Same recipe as .search-container:
   parent frost lives on ::before. */
html body.wlj-neat-gradient-active .mainheader .user > .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu),
html body.wlj-neat-gradient-active .user > .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu) {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  isolation: auto !important;
  filter: none !important;
  contain: none !important;
}

html body.wlj-neat-gradient-active .mainheader .user > .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu)::before,
html body.wlj-neat-gradient-active .user > .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu)::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 0;
  background: var(--wlj-glass-sheen), var(--wlj-glass-overlay);
  background-color: var(--wlj-glass-overlay);
  backdrop-filter: var(--wlj-glass-blur-overlay) !important;
  -webkit-backdrop-filter: var(--wlj-glass-blur-overlay) !important;
}

html body.wlj-neat-gradient-active .mainheader .user > .dropdown-menu > *,
html body.wlj-neat-gradient-active .user > .dropdown-menu > * {
  position: relative;
  z-index: 1;
}

html body.wlj-neat-gradient-active .mainheader .user .language-sub-menu,
html body.wlj-neat-gradient-active .user .language-sub-menu {
  position: absolute !important;
  top: 0 !important;
  right: calc(100% + var(--wlj-space-2, 8px)) !important;
  left: auto !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-overlay) !important;
  background-color: var(--wlj-glass-overlay) !important;
  backdrop-filter: var(--wlj-glass-blur-overlay) !important;
  -webkit-backdrop-filter: var(--wlj-glass-blur-overlay) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  isolation: isolate;
  z-index: 1080 !important;
}

html[dir="rtl"] body.wlj-neat-gradient-active .mainheader .user .language-sub-menu,
html[dir="rtl"] body.wlj-neat-gradient-active .user .language-sub-menu {
  right: auto !important;
  left: calc(100% + var(--wlj-space-2, 8px)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .mainheader .user .language-sub-menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .user .language-sub-menu,
body.darkMode.wlj-neat-gradient-active .mainheader .user .language-sub-menu,
body.darkMode.wlj-neat-gradient-active .user .language-sub-menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-overlay) !important;
  background-color: var(--wlj-glass-overlay) !important;
  border-color: var(--wlj-glass-edge) !important;
}

html body.wlj-neat-gradient-active .user .language-sub-menu .dropdown-item,
html body.wlj-neat-gradient-active .user .language-sub-menu span {
  color: var(--wlj-title, #0f172a) !important;
  opacity: 1 !important;
}
html[data-theme="light"] body.wlj-neat-gradient-active .user .language-sub-menu .dropdown-item,
html[data-theme="light"] body.wlj-neat-gradient-active .user .language-sub-menu span {
  color: #0f172a !important;
}
html[data-theme="dark"] body.wlj-neat-gradient-active .user .language-sub-menu .dropdown-item,
html[data-theme="dark"] body.wlj-neat-gradient-active .user .language-sub-menu span {
  color: #f8fafc !important;
}

html body.wlj-neat-gradient-active .InteractionModal .dropdownTxt,
html body.wlj-neat-gradient-active .dropdown-menu .dropdownTxt,
html body.wlj-neat-gradient-active .tabDropdownWrapper .dropdown-item {
  color: var(--wlj-title, #0f172a) !important;
}

html body.wlj-neat-gradient-active .InteractionModal .dropdownPara,
html body.wlj-neat-gradient-active .dropdown-menu .dropdownPara {
  color: var(--wlj-muted, #475569) !important;
  opacity: 1 !important;
}

html body.wlj-neat-gradient-active .InteractionModal .dropdownList,
html body.wlj-neat-gradient-active .InteractionModal .dropdown-item,
html body.wlj-neat-gradient-active .dropdown-menu .dropdownList,
html body.wlj-neat-gradient-active .dropdown-menu .dropdown-item:not(.topnotification) {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .InteractionModal .dropdownList:hover,
html body.wlj-neat-gradient-active .InteractionModal .dropdown-item:hover,
html body.wlj-neat-gradient-active .dropdown-menu .dropdownList:hover,
html body.wlj-neat-gradient-active .dropdown-menu .dropdown-item:hover:not(.topnotification),
html body.wlj-neat-gradient-active .dropdown-menu .dropdown-item:focus:not(.topnotification),
html body.wlj-neat-gradient-active .dropdown-menu .dropdownList.active {
  background: var(--wlj-glass-hover, rgba(255, 255, 255, 0.42)) !important;
  background-color: var(--wlj-glass-hover, rgba(255, 255, 255, 0.42)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
html[data-theme="dark"] body.wlj-neat-gradient-active .postMenu .InteractionModal:not(.wlj-company-action-menu),
html[data-theme="dark"] body.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu),
html[data-theme="dark"] body.wlj-neat-gradient-active .chat-message-menu,
body.darkMode.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
body.darkMode.wlj-neat-gradient-active .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu),
body.darkMode.wlj-neat-gradient-active .postMenu .InteractionModal:not(.wlj-company-action-menu),
body.darkMode.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu),
body.darkMode.wlj-neat-gradient-active .chat-message-menu {
  background: transparent !important;
  background-color: transparent !important;
  border-color: var(--wlj-glass-edge) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu)::before,
body.darkMode.wlj-neat-gradient-active .InteractionModal:not(.wlj-portal-interaction-menu):not(.wlj-company-action-menu)::before,
body.darkMode.wlj-neat-gradient-active .commentInteraction .InteractionModal:not(.wlj-company-action-menu)::before {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown);
  background-color: var(--wlj-glass-dropdown);
}

html[data-theme="dark"] body.wlj-neat-gradient-active .InteractionModal .dropdownTxt,
html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu .dropdownTxt,
body.darkMode.wlj-neat-gradient-active .InteractionModal .dropdownTxt,
body.darkMode.wlj-neat-gradient-active .dropdown-menu .dropdownTxt {
  color: var(--wlj-title, #f8fafc) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .InteractionModal .dropdownPara,
html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu .dropdownPara,
body.darkMode.wlj-neat-gradient-active .InteractionModal .dropdownPara,
body.darkMode.wlj-neat-gradient-active .dropdown-menu .dropdownPara {
  color: var(--wlj-muted, #94a3b8) !important;
}

html body.wlj-neat-gradient-active .wlj-terms-page,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-main,
html body.wlj-neat-gradient-active .wlj-terms-page.wlj-about-page > .wlj-terms-main,
html body.wlj-neat-gradient-active .wlj-terms-page.wlj-download-page > .wlj-terms-main,
html body.wlj-neat-gradient-active .wlj-about-intro,
html body.wlj-neat-gradient-active .wlj-about-spacer,
html body.wlj-neat-gradient-active .wlj-about-services,
html body.wlj-neat-gradient-active .wlj-about-contact,
html body.wlj-neat-gradient-active .wlj-about-fanfact,
html body.wlj-neat-gradient-active .wlj-download-body,
html body.wlj-neat-gradient-active .wlj-download-hero,
html body.wlj-neat-gradient-active .wlj-download-mockup,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-footer,
html body.wlj-neat-gradient-active .marketing-legal-page,
html body.wlj-neat-gradient-active .marketing-legal-page > main,
html body.wlj-neat-gradient-active .blogs-card-main-container,
html body.wlj-neat-gradient-active .blogMainContainer,
html body.wlj-neat-gradient-active .help-center-container,
html body.wlj-neat-gradient-active .helpCenterTab,
html body.wlj-neat-gradient-active .articles-main-container,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page,
html body.wlj-neat-gradient-active .friend-main-container.company-discovery,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .headerSection,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .frindsCardContainer {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .PeopleCard .PeopleCardContainer,
html body.wlj-neat-gradient-active .PeopleCard .PeopleCardHeader,
html body.wlj-neat-gradient-active .PeopleCard .mainCardText,
html body.wlj-neat-gradient-active .PeopleCard .mainCardText1,
html body.wlj-neat-gradient-active .PeopleCard .mainCardTex,
html body.wlj-neat-gradient-active .PeopleCard .wlj-card-actions,
html body.wlj-neat-gradient-active .HuzPeopleCard .PeopleCardContainer,
html body.wlj-neat-gradient-active .HuzPeopleCard .PeopleCardHeader,
html body.wlj-neat-gradient-active .HuzPeopleCard .mainCardText,
html body.wlj-neat-gradient-active .HuzPeopleCard .mainCardTex,
html body.wlj-neat-gradient-active .HuzPeopleCard .wlj-card-actions {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
}

html body.wlj-neat-gradient-active .friend-main-container.discovery-page .tabButton.connectionTab > button:not(.activeTab),
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .tabButton.connectionTab > button.activeTab {
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border-radius: 0 !important;
  color: inherit !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .friend-main-container.discovery-page .tabButton.connectionTab > button:not(.activeTab),
body.darkMode.wlj-neat-gradient-active .friend-main-container.discovery-page .tabButton.connectionTab > button:not(.activeTab) {
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  color: var(--wlj-tab-inactive, #94a3b8) !important;
}

html body.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field,
html body.wlj-neat-gradient-active .wlj-search-field {
  -webkit-appearance: none !important;
  appearance: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-search-field,
body.darkMode.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field,
body.darkMode.wlj-neat-gradient-active .wlj-search-field {
  background: transparent !important;
  background-color: transparent !important;
}

html body.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field .discovery-search,
html body.wlj-neat-gradient-active .wlj-search-field .discovery-search,
html body.wlj-neat-gradient-active .wlj-search-field input,
html body.wlj-neat-gradient-active .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .wlj-search-field .connection-input {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}

html body.wlj-neat-gradient-active .PeopleCard .wljDangerBtn,
html body.wlj-neat-gradient-active .PeopleCard .wljDangerBtn.wlj-chip-btn,
html body.wlj-neat-gradient-active .discovery-page .wljDangerBtn,
html body.wlj-neat-gradient-active .discovery-page .wljDangerBtn.wlj-chip-btn,
html body.wlj-neat-gradient-active .aboutMe .wljDangerBtn,
html body.wlj-neat-gradient-active .aboutMe .wljDangerBtn.wlj-chip-btn,
html body.wlj-neat-gradient-active .wlj-card-actions .wljDangerBtn {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .wljPrimaryBtn:hover,
html body.wlj-neat-gradient-active .aboutMe .wljPrimaryBtn:hover,
html body.wlj-neat-gradient-active .wlj-card-actions .wljPrimaryBtn:hover,
html body.wlj-neat-gradient-active .PeopleCard .wljPrimaryBtn:hover {
  background: var(--wlj-primary-hover, var(--buttonHover, #002760)) !important;
  background-color: var(--wlj-primary-hover, var(--buttonHover, #002760)) !important;
  border-color: var(--wlj-primary-hover, var(--buttonHover, #002760)) !important;
  color: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .wljDangerBtn:hover,
html body.wlj-neat-gradient-active button.wljDangerBtn:hover,
html body.wlj-neat-gradient-active .PeopleCard .wljDangerBtn:hover,
html body.wlj-neat-gradient-active .PeopleCard .wljDangerBtn.wlj-chip-btn:hover,
html body.wlj-neat-gradient-active .discovery-page .wljDangerBtn:hover,
html body.wlj-neat-gradient-active .aboutMe .wljDangerBtn:hover,
html body.wlj-neat-gradient-active .aboutMe .wljDangerBtn.wlj-chip-btn:hover,
html body.wlj-neat-gradient-active .wlj-card-actions .wljDangerBtn:hover {
  background: var(--wlj-danger, #b50000) !important;
  background-color: var(--wlj-danger, #b50000) !important;
  border-color: var(--wlj-danger, #b50000) !important;
  color: #fff !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .wljDangerBtn:hover .MuiSvgIcon-root,
html body.wlj-neat-gradient-active .wljDangerBtn:hover .material-symbols-sharp,
html body.wlj-neat-gradient-active .aboutMe .wljDangerBtn:hover .MuiSvgIcon-root,
html body.wlj-neat-gradient-active .wlj-card-actions .wljDangerBtn:hover .MuiSvgIcon-root {
  color: #fff !important;
}

html body.wlj-neat-gradient-active .wlj-content-wrap,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-content-wrap,
html body.wlj-neat-gradient-active .wlj-terms-page:not(.wlj-about-page) .wlj-content-wrap,
html body.wlj-neat-gradient-active .xb-accordion .block,
html body.wlj-neat-gradient-active .wlj-about-mission-card,
html body.wlj-neat-gradient-active .wlj-about-service-card,
html body.wlj-neat-gradient-active .wlj-about-service-lead,
html body.wlj-neat-gradient-active .wlj-about-contact-wrap,
html body.wlj-neat-gradient-active .wlj-about-hotline-inner,
html body.wlj-neat-gradient-active .wlj-footer-cta,
html body.wlj-neat-gradient-active .wlj-footer-links,
html body.wlj-neat-gradient-active .wlj-footer-copy,
html body.wlj-neat-gradient-active .wlj-mobile-menu,
html body.wlj-neat-gradient-active .wlj-download-hero-media,
html body.wlj-neat-gradient-active .auth-form-card,
html body.wlj-neat-gradient-active .help-card,
html body.wlj-neat-gradient-active .help-search-box,
html body.wlj-neat-gradient-active .related-articles-card,
html body.wlj-neat-gradient-active .blogSingleCard .blogContent,
html body.wlj-neat-gradient-active .articles-custom-card {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  transform: none !important;
}

html body.wlj-neat-gradient-active .blogSingleCard .blogContent {
  transform: translate(-50%, 10px) !important;
}

html body.wlj-neat-gradient-active .blogSingleCard:hover .blogContent {
  transform: translate(-50%, 4px) !important;
}

html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-header,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-header.is-fixed,
html body.wlj-neat-gradient-active .wlj-terms-page .wlj-terms-header.is-fixed .wlj-header-inner {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
  border-bottom: 1px solid var(--wlj-glass-surface-edge) !important;
}
html body.wlj-neat-gradient-active .wlj-about-mission-card:hover,
html body.wlj-neat-gradient-active .wlj-about-service-card:hover,
html body.wlj-neat-gradient-active .xb-accordion .block:hover,
html body.wlj-neat-gradient-active .help-card:hover,
html body.wlj-neat-gradient-active .related-articles-card:hover,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .PeopleCard:hover {
  transform: none !important;
}

html body.wlj-neat-gradient-active .wlj-footer-cta::before {
  background: transparent !important;
  background-color: transparent !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-content-wrap,
html[data-theme="dark"] body.wlj-neat-gradient-active .xb-accordion .block,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-about-mission-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-about-service-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-about-service-lead,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-about-contact-wrap,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-about-hotline-inner,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-footer-cta,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-footer-cta,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-footer-links,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-footer-copy,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-mobile-menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .auth-form-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .help-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .help-search-box,
html[data-theme="dark"] body.wlj-neat-gradient-active .related-articles-card,
html[data-theme="dark"] body.wlj-neat-gradient-active .blogSingleCard .blogContent,
body.darkMode.wlj-neat-gradient-active .wlj-content-wrap,
body.darkMode.wlj-neat-gradient-active .xb-accordion .block,
body.darkMode.wlj-neat-gradient-active .wlj-about-mission-card,
body.darkMode.wlj-neat-gradient-active .wlj-about-service-card,
body.darkMode.wlj-neat-gradient-active .wlj-about-contact-wrap,
body.darkMode.wlj-neat-gradient-active .wlj-footer-cta,
body.darkMode.wlj-neat-gradient-active .wlj-footer-links,
body.darkMode.wlj-neat-gradient-active .auth-form-card,
body.darkMode.wlj-neat-gradient-active .help-card {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-glass-surface-edge) !important;
}

/* Typed fields: comment-box focus (solid blue on the visible box, no offset halo). */
html body.wlj-neat-gradient-active input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([type="range"]):not([type="color"]):not([type="hidden"]):focus,
html body.wlj-neat-gradient-active input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([type="range"]):not([type="color"]):not([type="hidden"]):focus-visible,
html body.wlj-neat-gradient-active textarea:focus,
html body.wlj-neat-gradient-active textarea:focus-visible,
html body.wlj-neat-gradient-active select:focus,
html body.wlj-neat-gradient-active select:focus-visible,
html body.wlj-neat-gradient-active .form-control:focus,
html body.wlj-neat-gradient-active .form-control:focus-visible,
html body.wlj-neat-gradient-active .search-input:focus,
html body.wlj-neat-gradient-active .search-input:focus-visible {
  outline: none !important;
  outline-offset: 0 !important;
}

html body.wlj-neat-gradient-active .form-control:focus,
html body.wlj-neat-gradient-active .form-select:focus,
html body.wlj-neat-gradient-active textarea:focus,
html body.wlj-neat-gradient-active select:focus,
html body.wlj-neat-gradient-active input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body.wlj-neat-gradient-active input[type="search"]:not(.react-select__input):not([class*="__input"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body.wlj-neat-gradient-active input[type="email"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active input[type="password"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active input[type="number"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active input[type="date"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .react-select__control--is-focused,
html body.wlj-neat-gradient-active .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active [class*="__control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .ant-select-focused .ant-select-selector,
html body.wlj-neat-gradient-active .ant-picker-focused,
html body.wlj-neat-gradient-active .ant-input:focus {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border-color: var(--wlj-primary, #00419f) !important;
  box-shadow: var(--wlj-focus-ring, 0 0 0 3px rgba(0, 65, 159, 0.12)), var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body.wlj-neat-gradient-active .search-container:focus-within,
html body.wlj-neat-gradient-active .mainheader .search-container:focus-within,
html body.wlj-neat-gradient-active .sidebar-widget-card .search-container:focus-within,
html body.wlj-neat-gradient-active .right-sidebar-split .search-container:focus-within,
html body.wlj-neat-gradient-active .connection-search-bar:focus-within,
html body.wlj-neat-gradient-active .auth-field:focus-within,
html body.wlj-neat-gradient-active .wlj-search-field:focus-within,
html body.wlj-neat-gradient-active .search-dropdown:focus-within .searchInput,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container:focus-within,
html body.wlj-neat-gradient-active .write-message-cont:focus-within {
  border-color: var(--wlj-primary, #00419f) !important;
  box-shadow: var(--wlj-focus-ring, 0 0 0 3px rgba(0, 65, 159, 0.12)) !important;
}

/* Popup chat: focus ring on the single-line field, not the whole footer */
html body.wlj-neat-gradient-active .chat-box .chat-input:focus-within {
  background: transparent !important;
  background-color: transparent !important;
  border-color: var(--wlj-glass-divider, rgba(16, 24, 40, 0.08)) !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .chat-box .outer-chat-composer-field:focus-within {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: #00419f !important;
  box-shadow: 0 0 0 3px rgba(0, 65, 159, 0.12) !important;
}

html body.wlj-neat-gradient-active .featured-card .commentBox:focus-within,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .commentBox:focus-within,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox:focus-within,
html body.wlj-neat-gradient-active .ReplyComment .commentBox:focus-within,
html body.wlj-neat-gradient-active .addComment .commentBox:focus-within {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border-color: var(--wlj-primary, #00419f) !important;
  box-shadow: var(--wlj-focus-ring), var(--wlj-glass-shadow) !important;
}

html body.wlj-neat-gradient-active .search-container input,
html body.wlj-neat-gradient-active .search-container input:focus,
html body.wlj-neat-gradient-active .search-container input:focus-visible,
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]):focus-visible,
html body.wlj-neat-gradient-active .search-container .form-control,
html body.wlj-neat-gradient-active .search-container .form-control:focus,
html body.wlj-neat-gradient-active .search-container .search-input,
html body.wlj-neat-gradient-active .search-container .search-input:focus,
html body.wlj-neat-gradient-active .search-container .connection-input,
html body.wlj-neat-gradient-active .search-container .connection-input:focus,
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .commentBox input,
html body.wlj-neat-gradient-active .commentBox input:focus,
html body.wlj-neat-gradient-active .commentBox textarea,
html body.wlj-neat-gradient-active .commentBox textarea:focus,
html body.wlj-neat-gradient-active .auth-field input,
html body.wlj-neat-gradient-active .auth-field input:focus,
html body.wlj-neat-gradient-active .searchInput input,
html body.wlj-neat-gradient-active .searchInput input:focus,
html body.wlj-neat-gradient-active .searchInput .ant-select-selector,
html body.wlj-neat-gradient-active .write-message-cont input,
html body.wlj-neat-gradient-active .write-message-cont input:focus,
html body.wlj-neat-gradient-active .chat-input input,
html body.wlj-neat-gradient-active .chat-input input:focus,
html body.wlj-neat-gradient-active .chat-input textarea,
html body.wlj-neat-gradient-active .chat-input textarea:focus,
html body.wlj-neat-gradient-active .wlj-search-field input,
html body.wlj-neat-gradient-active .wlj-search-field input:focus {
  outline: none !important;
  outline-offset: 0 !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  -webkit-appearance: none;
  appearance: none;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .form-control:focus,
html[data-theme="dark"] body.wlj-neat-gradient-active .form-select:focus,
html[data-theme="dark"] body.wlj-neat-gradient-active textarea:focus,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container:focus-within,
html[data-theme="dark"] body.wlj-neat-gradient-active .auth-field:focus-within,
body.darkMode.wlj-neat-gradient-active .form-control:focus,
body.darkMode.wlj-neat-gradient-active .search-container:focus-within {
  background: rgba(28, 28, 36, 0.46) !important;
  background-color: rgba(28, 28, 36, 0.46) !important;
  border-color: #4d94ff !important;
  box-shadow: 0 0 0 3px rgba(77, 148, 255, 0.22) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .search-container input,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container input:focus,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container .form-control:focus,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox input:focus,
body.darkMode.wlj-neat-gradient-active .search-container input,
body.darkMode.wlj-neat-gradient-active .search-container input:focus,
body.darkMode.wlj-neat-gradient-active .commentBox input:focus {
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .modal .container.articleContainer,
html body.wlj-neat-gradient-active .container.articleContainer,
html body.wlj-neat-gradient-active .modal-content.articleContainer {
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  background-color: var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
  border-radius: 4px;
  z-index: 2;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
  max-height: calc(100vh - 32px);
}

html body.wlj-neat-gradient-active .modal.show .container.articleContainer,
html body.wlj-neat-gradient-active .modal.fade.show .container.articleContainer {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-quill,
html body.wlj-neat-gradient-active .articleContainer .form-quill.AddArticleNewQuill {
  background: var(--wlj-canvas, #f8fafc) !important;
  background-color: var(--wlj-canvas, #f8fafc) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar,
html body.wlj-neat-gradient-active .articleContainer .ql-container {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-quill,
html body.wlj-neat-gradient-active .articleContainer .form-quill.AddArticleNewQuill {
  min-height: 0 !important;
  border: 1px solid var(--wlj-field-border, #e4e7ec) !important;
  border-radius: 4px !important;
  overflow: hidden;
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar.ql-snow {
  border: none !important;
  border-bottom: 1px solid var(--wlj-field-border, #e4e7ec) !important;
  padding: 6px 8px !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-container.ql-snow {
  border: none !important;
  min-height: 148px !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-editor {
  min-height: 148px !important;
  max-height: 180px;
  color: var(--heading-color, #0f172a) !important;
}

html body.wlj-neat-gradient-active .modal.modal-xl .articleContainer .ql-container.ql-snow,
html body.wlj-neat-gradient-active .modal.article-xl-modal .articleContainer .ql-container.ql-snow {
  min-height: 148px !important;
}

html body.wlj-neat-gradient-active .modal.modal-xl .articleContainer .ql-editor,
html body.wlj-neat-gradient-active .modal.article-xl-modal .articleContainer .ql-editor {
  min-height: 148px !important;
  max-height: min(22vh, 180px);
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar button {
  width: 28px !important;
  height: 28px !important;
  padding: 4px !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar svg {
  width: 16px !important;
  height: 16px !important;
}

html body.wlj-neat-gradient-active .articleContainer .mainBtn,
html body.wlj-neat-gradient-active .articleContainer .mainBtn1,
html body.wlj-neat-gradient-active .articleContainer .mainBtn-upload,
html body.wlj-neat-gradient-active .articleContainer .btn-primary,
html body.wlj-neat-gradient-active .articleContainer button.mainBtn {
  background: #00419f !important;
  background-color: #00419f !important;
  color: #ffffff !important;
  opacity: 1 !important;
  isolation: isolate;
  position: relative;
  z-index: 5;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.22) !important;
}

html body.wlj-neat-gradient-active .articleContainer .closebtn {
  background: transparent !important;
  border: 1.5px solid var(--wlj-danger, #b50000) !important;
  color: var(--wlj-danger, #b50000) !important;
  opacity: 1 !important;
  isolation: isolate;
  position: relative;
  z-index: 5;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-stroke {
  stroke: var(--heading-color, #1e293b) !important;
}

html body.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-fill {
  fill: var(--heading-color, #1e293b) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .form-quill,
body.darkMode.wlj-neat-gradient-active .articleContainer .form-quill {
  background: var(--wlj-canvas, #0b1120) !important;
  background-color: var(--wlj-canvas, #0b1120) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-toolbar,
html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-container,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-toolbar,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-container {
  background: transparent !important;
  background-color: transparent !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-toolbar.ql-snow,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-toolbar.ql-snow {
  border-bottom-color: var(--wlj-field-border, #4b4b57) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-editor,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-editor {
  color: var(--heading-color, #f1f5f9) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-stroke,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-stroke {
  stroke: #e2e8f0 !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-fill,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-toolbar .ql-fill {
  fill: #e2e8f0 !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ql-snow .ql-picker-label,
body.darkMode.wlj-neat-gradient-active .articleContainer .ql-snow .ql-picker-label {
  color: #e2e8f0 !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .closebtn,
body.darkMode.wlj-neat-gradient-active .articleContainer .closebtn {
  background: transparent !important;
  border: 1.5px solid var(--wlj-danger, #b50000) !important;
  color: var(--wlj-danger, #b50000) !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-label .field-hint,
html body.wlj-neat-gradient-active .articleContainer .form-label .field-hint.text-danger {
  color: var(--wlj-danger, #b50000) !important;
  font-weight: 400;
}

html body.wlj-neat-gradient-active .articleContainer .article-modal-footer {
  margin-top: 0 !important;
  padding: 12px 18px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
  gap: 12px;
  border-top: 1px solid var(--wlj-divider, var(--wlj-glass-divider, rgba(16, 24, 40, 0.1)));
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  background: transparent !important;
  background-color: transparent !important;
}

html body.wlj-neat-gradient-active .articleContainer .article-modal-footer.justify-content-end,
html body.wlj-neat-gradient-active .articleContainer .article-modal-footer.justify-content-center {
  justify-content: flex-end !important;
}

html body.wlj-neat-gradient-active .articleContainer .article-modal-footer .mainBtn,
html body.wlj-neat-gradient-active .articleContainer .article-modal-footer .closebtn {
  z-index: 1;
  width: auto !important;
  flex: 0 0 auto !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-label,
html body.wlj-neat-gradient-active .articleContainer label.form-label {
  color: var(--heading-color, var(--wlj-title, #0f172a)) !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-label .text-danger {
  color: var(--wlj-danger, #b50000) !important;
}

html body.wlj-neat-gradient-active .articleContainer .form-control,
html body.wlj-neat-gradient-active .articleContainer .HuzTitleInput,
html body.wlj-neat-gradient-active .articleContainer .HuzTitleInput:focus,
html body.wlj-neat-gradient-active .articleContainer .ant-select .ant-select-selector,
html body.wlj-neat-gradient-active .articleContainer .ant-select-outlined .ant-select-selector,
html body.wlj-neat-gradient-active .articleContainer .ant-select-multiple .ant-select-selector,
html body.wlj-neat-gradient-active .articleContainer input[type="text"] {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border-color: var(--wlj-field-border, #e4e7ec) !important;
  color: var(--heading-color, #0f172a) !important;
}

html body.wlj-neat-gradient-active .articleContainer .custom-file-upload {
  background: var(--wlj-glass-field, rgba(255, 255, 255, 0.42)) !important;
  background-color: var(--wlj-glass-field, rgba(255, 255, 255, 0.42)) !important;
  border: 1px dashed var(--wlj-field-border, #e4e7ec) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .custom-file-upload,
body.darkMode.wlj-neat-gradient-active .articleContainer .custom-file-upload {
  background: rgba(255, 255, 255, 0.04) !important;
  background-color: rgba(255, 255, 255, 0.04) !important;
  border-color: var(--wlj-field-border, #4b4b57) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .form-control,
html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .HuzTitleInput,
html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .ant-select-selector,
body.darkMode.wlj-neat-gradient-active .articleContainer .form-control,
body.darkMode.wlj-neat-gradient-active .articleContainer .HuzTitleInput,
body.darkMode.wlj-neat-gradient-active .articleContainer .ant-select-selector {
  background: var(--wlj-canvas, #0b1120) !important;
  background-color: var(--wlj-canvas, #0b1120) !important;
  color: var(--heading-color, #f8fafc) !important;
  border-color: var(--wlj-field-border, #4b4b57) !important;
}

html body.wlj-neat-gradient-active .privacySelect .privacySelect__control,
html body.wlj-neat-gradient-active .privacySelect .privacySelect__control--is-focused {
  min-height: 36px !important;
  height: 36px !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border: 1px solid var(--wlj-field-border, #e4e7ec) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body.wlj-neat-gradient-active .privacySelect__menu,
html body.wlj-neat-gradient-active .privacySelect__menu-portal .privacySelect__menu,
html body.wlj-neat-gradient-active .wlj-select__menu,
html body.wlj-neat-gradient-active [class*="__menu"]:not([class*="__menu-list"]):not([class*="__menu-portal"]):not([class*="__menu-notice"]),
html body.wlj-neat-gradient-active div[class*="css-"][class$="-menu"] {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  z-index: 2000 !important;
}

html body.wlj-neat-gradient-active .privacySelect__menu-portal {
  z-index: 2000 !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .privacySelect .privacySelect__control,
body.darkMode.wlj-neat-gradient-active .privacySelect .privacySelect__control {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .privacySelect__menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-select__menu,
html[data-theme="dark"] body.wlj-neat-gradient-active [class*="__menu"]:not([class*="__menu-list"]):not([class*="__menu-portal"]):not([class*="__menu-notice"]),
body.darkMode.wlj-neat-gradient-active .privacySelect__menu,
body.darkMode.wlj-neat-gradient-active .wlj-select__menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
}

@keyframes wljStatusGlow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

html body .profileImg.animateToWork,
html body.wlj-neat-gradient-active .profileImg.animateToWork,
html body.wlj-neat-gradient-active .profileImg.new-profileImg.animateToWork,
html body.wlj-neat-gradient-active .profileCard .profileImg.animateToWork,
html body.wlj-neat-gradient-active .new-profileCard .profileImg.animateToWork {
  padding: 5px !important;
  background: linear-gradient(90deg, #002760 0%, #00419f 22%, #ac04f8 50%, #00419f 78%, #002760 100%) !important;
  background-color: transparent !important;
  background-image: linear-gradient(90deg, #002760 0%, #00419f 22%, #ac04f8 50%, #00419f 78%, #002760 100%) !important;
  background-size: 300% auto !important;
  animation: wljStatusGlow 3s ease infinite !important;
  box-shadow:
    0 0 10px rgba(172, 4, 248, 0.7),
    0 0 22px rgba(0, 65, 159, 0.45),
    0 4px 14px rgba(16, 24, 40, 0.18) !important;
  filter: none !important;
}

html body .ProfilebadgeWillingToWork h6,
html body.wlj-neat-gradient-active .ProfilebadgeWillingToWork h6,
html body.wlj-neat-gradient-active .profileCard .ProfilebadgeWillingToWork h6,
html body.wlj-neat-gradient-active .new-profileCard .ProfilebadgeWillingToWork h6 {
  background: linear-gradient(90deg, #002760 0%, #00419f 22%, #ac04f8 50%, #00419f 78%, #002760 100%) !important;
  background-color: transparent !important;
  background-image: linear-gradient(90deg, #002760 0%, #00419f 22%, #ac04f8 50%, #00419f 78%, #002760 100%) !important;
  background-size: 300% auto !important;
  animation: wljStatusGlow 3s ease infinite !important;
  box-shadow:
    0 0 8px rgba(172, 4, 248, 0.7),
    0 0 16px rgba(0, 65, 159, 0.45) !important;
}

/* Date picker cards — pane frost; vendor light/dark CSS cannot paint opaque bars */
html body .react-datepicker-popper > div,
html body .ant-picker-dropdown,
html body.wlj-neat-gradient-active .react-datepicker-popper > div,
html body.wlj-neat-gradient-active .ant-picker-dropdown {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

html body .flatpickr-calendar,
html body .flatpickr-calendar.open,
html body .react-datepicker,
html body .ant-picker-dropdown .ant-picker-panel-container,
html body.wlj-neat-gradient-active .flatpickr-calendar,
html body.wlj-neat-gradient-active .react-datepicker,
html body.wlj-neat-gradient-active .ant-picker-panel-container {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-surface-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  overflow: visible !important;
  color: var(--wlj-title, #0f172a) !important;
}

html body .flatpickr-calendar,
html body .flatpickr-calendar.open,
html body .react-datepicker {
  width: 308px !important;
  max-width: calc(100vw - 32px) !important;
  box-sizing: border-box !important;
  padding: 8px 8px 10px !important;
}

html body .flatpickr-calendar:before,
html body .flatpickr-calendar:after,
html body .react-datepicker__triangle {
  display: none !important;
}

html body .flatpickr-months,
html body .flatpickr-months .flatpickr-month,
html body .flatpickr-weekdays,
html body span.flatpickr-weekday,
html body .flatpickr-innerContainer,
html body .flatpickr-rContainer,
html body .flatpickr-days,
html body .dayContainer,
html body .flatpickr-time,
html body .react-datepicker__header,
html body .react-datepicker__month-container,
html body .react-datepicker__month,
html body .react-datepicker__day-names,
html body .ant-picker-panel,
html body .ant-picker-header,
html body .ant-picker-body,
html body .ant-picker-content {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

html body .flatpickr-innerContainer,
html body .flatpickr-days,
html body .flatpickr-months .flatpickr-month {
  overflow: visible !important;
}

html body .flatpickr-innerContainer,
html body .flatpickr-rContainer,
html body .flatpickr-weekdays,
html body .flatpickr-days,
html body .flatpickr-weekdaycontainer,
html body .dayContainer {
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  box-sizing: border-box !important;
}

html body .flatpickr-weekdaycontainer,
html body .dayContainer {
  display: grid !important;
  grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
  justify-content: stretch !important;
  justify-items: center !important;
  padding-bottom: 0 !important;
}

html body .flatpickr-weekdays,
html body .react-datepicker__header {
  border-bottom: 1px solid var(--wlj-glass-divider, rgba(16, 24, 40, 0.08)) !important;
}

html body span.flatpickr-weekday {
  flex: none !important;
  width: auto !important;
  letter-spacing: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

html body .flatpickr-current-month {
  left: 0 !important;
  width: 100% !important;
  height: 40px !important;
  padding: 0 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  transform: none !important;
}

html body .modal-content .flatpickr-calendar select,
html body .modal-content .flatpickr-calendar input,
html body .postAdd .flatpickr-calendar select,
html body .postAdd .flatpickr-calendar input,
html body .flatpickr-calendar select,
html body .flatpickr-calendar input,
html body .flatpickr-current-month .flatpickr-monthDropdown-months,
html body .flatpickr-current-month input.cur-year {
  min-height: 0 !important;
  height: 28px !important;
  width: auto !important;
  margin: 0 !important;
  padding: 0 8px !important;
  font-size: 13px !important;
  line-height: 28px !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .flatpickr-current-month .numInputWrapper {
  width: auto !important;
  padding: 0 !important;
  background: transparent !important;
  height: 28px !important;
}

html body .flatpickr-current-month input.cur-year {
  width: 4.6em !important;
  padding: 0 6px !important;
}

html body .flatpickr-months .flatpickr-prev-month,
html body .flatpickr-months .flatpickr-next-month {
  top: 50% !important;
  transform: translateY(-50%) !important;
  height: 28px !important;
  width: 28px !important;
  padding: 0 !important;
}

html body .flatpickr-day {
  width: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  line-height: 32px !important;
  margin: 2px auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

html body .flatpickr-months .flatpickr-month,
html body .flatpickr-current-month,
html body .flatpickr-current-month input.cur-year,
html body .flatpickr-current-month span.cur-month,
html body .flatpickr-months .flatpickr-prev-month,
html body .flatpickr-months .flatpickr-next-month,
html body .react-datepicker__current-month,
html body .ant-picker-header {
  color: var(--wlj-title, #0f172a) !important;
  fill: var(--wlj-title, #0f172a) !important;
}

html body span.flatpickr-weekday,
html body .react-datepicker__day-name,
html body .ant-picker-content th {
  color: var(--wlj-muted, #475569) !important;
  background: transparent !important;
}

html body .flatpickr-current-month .flatpickr-monthDropdown-months,
html body .flatpickr-current-month input.cur-year {
  background: var(--wlj-glass-field, rgba(255, 255, 255, 0.28)) !important;
  color: var(--wlj-title, #0f172a) !important;
  font-weight: 600 !important;
}

html body .flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month {
  background: var(--wlj-surface-sunken, #f1f5f9) !important;
  color: var(--wlj-title, #0f172a) !important;
}

html body .flatpickr-day,
html body .react-datepicker__day {
  color: var(--wlj-title, #0f172a) !important;
  background: none !important;
}

html body .flatpickr-day:hover,
html body .flatpickr-day.prevMonthDay:hover,
html body .flatpickr-day.nextMonthDay:hover,
html body .flatpickr-day:focus,
html body .react-datepicker__day:hover:not(.react-datepicker__day--selected):not(.react-datepicker__day--disabled) {
  background: var(--wlj-hover-bg, color-mix(in srgb, var(--buttonBase, #00419f) 12%, transparent)) !important;
  color: var(--buttonBase, #00419f) !important;
  border-color: transparent !important;
}

html body .flatpickr-day.today,
html body .react-datepicker__day--today {
  border-color: var(--buttonBase, #00419f) !important;
  color: var(--buttonBase, #00419f) !important;
  background: transparent !important;
}

html body .flatpickr-day.today:hover,
html body .flatpickr-day.today:focus {
  background: var(--wlj-hover-bg, color-mix(in srgb, var(--buttonBase, #00419f) 12%, transparent)) !important;
  color: var(--buttonBase, #00419f) !important;
  border-color: var(--buttonBase, #00419f) !important;
}

html body .flatpickr-day.selected,
html body .flatpickr-day.startRange,
html body .flatpickr-day.endRange,
html body .flatpickr-day.selected:hover,
html body .flatpickr-day.selected:focus,
html body .flatpickr-day.selected.prevMonthDay,
html body .flatpickr-day.selected.nextMonthDay,
html body .react-datepicker__day--selected,
html body .react-datepicker__day--keyboard-selected,
html body .react-datepicker__day--selected:hover,
html body .react-datepicker__day--keyboard-selected:hover {
  background: var(--buttonBase, #00419f) !important;
  border-color: var(--buttonBase, #00419f) !important;
  color: #fff !important;
}

html body .flatpickr-day.prevMonthDay,
html body .flatpickr-day.nextMonthDay,
html body .react-datepicker__day--outside-month {
  color: var(--wlj-text-muted, #64748b) !important;
  opacity: 0.78;
}

html body .flatpickr-day.selected.prevMonthDay,
html body .flatpickr-day.selected.nextMonthDay {
  opacity: 1;
}

html body .flatpickr-day.flatpickr-disabled,
html body .flatpickr-day.flatpickr-disabled:hover,
html body .react-datepicker__day--disabled {
  color: var(--wlj-text-disabled, #94a3b8) !important;
  opacity: 0.45;
  background: transparent !important;
}

html body .flatpickr-months .flatpickr-prev-month:hover svg,
html body .flatpickr-months .flatpickr-next-month:hover svg {
  fill: var(--buttonBase, #00419f) !important;
}

html[data-theme="dark"] body .flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month,
body.darkMode .flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month {
  background: var(--wlj-surface-sunken, #1a1a22) !important;
  color: var(--wlj-title, #f8fafc) !important;
}

/* Job card lock — rounded card, pill badges, outline View / solid Apply */
html body .jobCard,
html body.wlj-neat-gradient-active .jobCard,
html body.wlj-neat-gradient-active .IncreaseWidthJobCard .jobCard,
html body.wlj-neat-gradient-active .job-search-workspace .jobCard,
html body.wlj-neat-gradient-active .manageJobsPage .jobCard,
html body.wlj-neat-gradient-active .job-history .jobCard,
html body.wlj-neat-gradient-active .job-history .card-container .jobCard {
  border-radius: var(--wlj-card-radius, 4px) !important;
  overflow: visible !important;
}

html body .jobCard:hover,
html body.wlj-neat-gradient-active .jobCard:hover {
  border-color: color-mix(in srgb, var(--buttonBase, #00419f) 45%, var(--wlj-glass-surface-edge, #e8edf5)) !important;
  transform: none !important;
}

html body .jobCard .job-location,
html body .jobCard .job-location span {
  color: var(--buttonBase, #00419f) !important;
}

html body .jobCard .badge.badge-job,
html body .jobCard .badge.badge-date,
html body .jobCard .wlj-status-chip {
  border-radius: 999px !important;
}

html body .jobCard .job-card-foot,
html body .jobCard .job-card-post-date {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px !important;
  border-radius: var(--wlj-radius, 4px) !important;
  border-top: none !important;
  background: color-mix(in srgb, var(--wlj-muted, #475569) 8%, transparent);
}

html body .jobCard .jobAction .mainBtn,
html body .jobCard .jobAction .mainBtn4 {
  border-radius: var(--wlj-radius-btn, 4px) !important;
}

html[data-theme="dark"] body .jobCard .job-card-foot,
html[data-theme="dark"] body .jobCard .job-card-post-date,
body.darkMode .jobCard .job-card-foot,
body.darkMode .jobCard .job-card-post-date {
  background: rgba(255, 255, 255, 0.06);
}

/* ============================================================
   Portal modal fields — glass on every control (always wins)
   ============================================================ */
html body.wlj-neat-gradient-active .modal-content .form-control,
html body.wlj-neat-gradient-active .modal-content .form-select,
html body.wlj-neat-gradient-active .modal-content textarea,
html body.wlj-neat-gradient-active .modal-content select,
html body.wlj-neat-gradient-active .modal-content .flatpickr-input,
html body.wlj-neat-gradient-active .modal-content input[type="date"],
html body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller):not(.MobileCommentmodal) input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active .jobCreate .form-control,
html body.wlj-neat-gradient-active .jobCreate textarea,
html body.wlj-neat-gradient-active .jobCreate select,
html body.wlj-neat-gradient-active .jobCreate input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .settings .form-control,
html body.wlj-neat-gradient-active .settings textarea,
html body.wlj-neat-gradient-active .settings select,
html body.wlj-neat-gradient-active .settings input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .CreateCompany .form-control,
html body.wlj-neat-gradient-active .CreateCompany textarea,
html body.wlj-neat-gradient-active .CreateCompany select,
html body.wlj-neat-gradient-active .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .manageResume .form-control,
html body.wlj-neat-gradient-active .manageResume textarea,
html body.wlj-neat-gradient-active .GradeFieldEducation,
html body.wlj-neat-gradient-active .JobTitleInputField,
html body.wlj-neat-gradient-active .Dark-Input-from-group,
html body.wlj-neat-gradient-active .popover .form-control,
html body.wlj-neat-gradient-active .modal-content .react-select__control,
html body.wlj-neat-gradient-active .modal-content .wlj-select__control,
html body.wlj-neat-gradient-active .modal-content [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .jobCreate .react-select__control,
html body.wlj-neat-gradient-active .jobCreate .wlj-select__control,
html body.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .jobCreate [class$="-control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .settings .react-select__control,
html body.wlj-neat-gradient-active .settings .wlj-select__control,
html body.wlj-neat-gradient-active .settings [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .CreateCompany .react-select__control,
html body.wlj-neat-gradient-active .CreateCompany .wlj-select__control,
html body.wlj-neat-gradient-active .CreateCompany [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .iconsDropdown .react-select__control,
html body.wlj-neat-gradient-active .iconsDropdown .wlj-select__control,
html body.wlj-neat-gradient-active .iconsDropdown [class*="__control"],
html body.wlj-neat-gradient-active .dropdownBorder .react-select__control,
html body.wlj-neat-gradient-active .dropdownBorder .wlj-select__control,
html body.wlj-neat-gradient-active .dropdownBorder [class*="__control"] {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-field-border) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
  color: var(--wlj-title, #0f172a) !important;
}

html body.wlj-neat-gradient-active .modal-content .form-control:hover,
html body.wlj-neat-gradient-active .modal-content textarea:hover,
html body.wlj-neat-gradient-active .jobCreate input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):hover,
html body.wlj-neat-gradient-active .settings input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):hover,
html body.wlj-neat-gradient-active .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):hover,
html body.wlj-neat-gradient-active .JobTitleInputField:hover,
html body.wlj-neat-gradient-active .GradeFieldEducation:hover,
html body.wlj-neat-gradient-active .modal-content .react-select__control:hover,
html body.wlj-neat-gradient-active .modal-content .wlj-select__control:hover,
html body.wlj-neat-gradient-active .modal-content [class*="__control"]:not(.privacySelect__control):hover,
html body.wlj-neat-gradient-active .jobCreate .react-select__control:hover,
html body.wlj-neat-gradient-active .jobCreate .wlj-select__control:hover,
html body.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control):hover,
html body.wlj-neat-gradient-active .settings .react-select__control:hover,
html body.wlj-neat-gradient-active .settings .wlj-select__control:hover,
html body.wlj-neat-gradient-active .settings [class*="__control"]:not(.privacySelect__control):hover,
html body.wlj-neat-gradient-active .CreateCompany .react-select__control:hover,
html body.wlj-neat-gradient-active .CreateCompany .wlj-select__control:hover,
html body.wlj-neat-gradient-active .CreateCompany [class*="__control"]:not(.privacySelect__control):hover {
  background: rgba(255, 255, 255, 0.36) !important;
  background-color: rgba(255, 255, 255, 0.36) !important;
}

html body.wlj-neat-gradient-active .modal-content .form-control:focus,
html body.wlj-neat-gradient-active .modal-content textarea:focus,
html body.wlj-neat-gradient-active .jobCreate input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .settings input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .JobTitleInputField:focus,
html body.wlj-neat-gradient-active .GradeFieldEducation:focus,
html body.wlj-neat-gradient-active .modal-content .react-select__control--is-focused,
html body.wlj-neat-gradient-active .modal-content .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active .modal-content [class*="__control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .jobCreate .react-select__control--is-focused,
html body.wlj-neat-gradient-active .jobCreate .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active .jobCreate [class*="__control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .settings .react-select__control--is-focused,
html body.wlj-neat-gradient-active .settings .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active .settings [class*="__control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .CreateCompany .react-select__control--is-focused,
html body.wlj-neat-gradient-active .CreateCompany .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active .CreateCompany [class*="__control--is-focused"]:not(.privacySelect__control) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-primary, #00419f) !important;
  box-shadow: var(--wlj-focus-ring, 0 0 0 3px rgba(0, 65, 159, 0.12)), var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content .form-control,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content textarea,
html[data-theme="dark"] body.wlj-neat-gradient-active .jobCreate input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html[data-theme="dark"] body.wlj-neat-gradient-active .settings input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html[data-theme="dark"] body.wlj-neat-gradient-active .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html[data-theme="dark"] body.wlj-neat-gradient-active .JobTitleInputField,
html[data-theme="dark"] body.wlj-neat-gradient-active .GradeFieldEducation,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content .react-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content .wlj-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content [class*="__control"]:not(.privacySelect__control),
html[data-theme="dark"] body.wlj-neat-gradient-active .jobCreate .react-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .jobCreate .wlj-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control),
html[data-theme="dark"] body.wlj-neat-gradient-active .settings .react-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .settings .wlj-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .CreateCompany .react-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .CreateCompany .wlj-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .CreateCompany [class*="__control"]:not(.privacySelect__control),
body.darkMode.wlj-neat-gradient-active .modal-content .form-control,
body.darkMode.wlj-neat-gradient-active .modal-content textarea,
body.darkMode.wlj-neat-gradient-active .jobCreate input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
body.darkMode.wlj-neat-gradient-active .settings input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
body.darkMode.wlj-neat-gradient-active .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
body.darkMode.wlj-neat-gradient-active .JobTitleInputField,
body.darkMode.wlj-neat-gradient-active .GradeFieldEducation,
body.darkMode.wlj-neat-gradient-active .modal-content .react-select__control,
body.darkMode.wlj-neat-gradient-active .modal-content .wlj-select__control,
body.darkMode.wlj-neat-gradient-active .modal-content [class*="__control"]:not(.privacySelect__control),
body.darkMode.wlj-neat-gradient-active .jobCreate .react-select__control,
body.darkMode.wlj-neat-gradient-active .jobCreate .wlj-select__control,
body.darkMode.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control),
body.darkMode.wlj-neat-gradient-active .settings .react-select__control,
body.darkMode.wlj-neat-gradient-active .settings .wlj-select__control,
body.darkMode.wlj-neat-gradient-active .CreateCompany .react-select__control,
body.darkMode.wlj-neat-gradient-active .CreateCompany .wlj-select__control,
body.darkMode.wlj-neat-gradient-active .CreateCompany [class*="__control"]:not(.privacySelect__control) {
  background: rgba(28, 28, 36, 0.36) !important;
  background-color: rgba(28, 28, 36, 0.36) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  color: #f8fafc !important;
}

/* Create Job modal — same denser frost as the emoji picker */
html body.wlj-neat-gradient-active .postAdd.jobCreate .modal-content {
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  background-color: var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
}

html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class$="-control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany textarea,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany select {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-field-border) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body.wlj-neat-gradient-active .postAdd.jobCreate .wlj-select__value-container,
html body.wlj-neat-gradient-active .postAdd.jobCreate .wlj-select__indicators,
html body.wlj-neat-gradient-active .postAdd.jobCreate [class*="__value-container"],
html body.wlj-neat-gradient-active .postAdd.jobCreate [class*="__indicators"],
html body.wlj-neat-gradient-active .postAdd.jobCreate [class$="-ValueContainer"],
html body.wlj-neat-gradient-active .postAdd.jobCreate [class$="-Indicators"],
html body.wlj-neat-gradient-active .postAdd.jobCreate .wlj-select__container,
html body.wlj-neat-gradient-active .postAdd.jobCreate [class$="-container"] {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control:hover,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control):hover,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class$="-control"]:not(.privacySelect__control):hover,
html body.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField:hover {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
}

html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control--is-focused,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class$="-control--is-focused"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField:focus {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border-color: var(--wlj-primary, #00419f) !important;
  box-shadow: var(--wlj-focus-ring, 0 0 0 3px rgba(0, 65, 159, 0.12)), var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control,
html[data-theme="dark"] body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control),
html[data-theme="dark"] body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class$="-control"]:not(.privacySelect__control),
html[data-theme="dark"] body.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField,
body.darkMode.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control,
body.darkMode.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control),
body.darkMode.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class$="-control"]:not(.privacySelect__control),
body.darkMode.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField {
  background: rgba(28, 28, 36, 0.36) !important;
  background-color: rgba(28, 28, 36, 0.36) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  color: #f8fafc !important;
}

/* Portaled action menus — body-level frost (same as global search) */
html body.wlj-neat-gradient-active .dropdown-menu.wlj-portal-interaction-menu,
html body.wlj-neat-gradient-active .InteractionModal.wlj-portal-interaction-menu,
html body.wlj-neat-gradient-active .wlj-portal-interaction-menu,
html body .dropdown-menu.wlj-portal-interaction-menu,
html body .InteractionModal.wlj-portal-interaction-menu,
html body .wlj-portal-interaction-menu,
.dropdown-menu.wlj-portal-interaction-menu,
.InteractionModal.wlj-portal-interaction-menu,
.wlj-portal-interaction-menu {
  position: fixed !important;
  right: auto !important;
  bottom: auto !important;
  float: none !important;
  width: max-content !important;
  min-width: 240px !important;
  max-width: min(280px, calc(100vw - 24px)) !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  border-radius: var(--wlj-radius, 4px) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  padding: 6px !important;
  isolation: auto;
  z-index: 1080 !important;
  overflow: hidden !important;
  max-height: min(70vh, 480px) !important;
  animation: none !important;
  transform: none !important;
}

html body .wlj-portal-interaction-menu .dropdown-item,
html body .wlj-portal-interaction-menu .dropdownList,
html body .wlj-portal-interaction-menu .dropdownTxt,
html body .wlj-portal-interaction-menu .dropdownPara {
  white-space: normal !important;
  max-width: 100% !important;
}

html body.wlj-neat-gradient-active .wlj-portal-interaction-menu::before,
html body .wlj-portal-interaction-menu::before,
.dropdown-menu.wlj-portal-interaction-menu::before,
.InteractionModal.wlj-portal-interaction-menu::before,
.wlj-portal-interaction-menu::before {
  content: none !important;
  display: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .dropdown-menu.wlj-portal-interaction-menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .InteractionModal.wlj-portal-interaction-menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-portal-interaction-menu,
html[data-theme="dark"] .dropdown-menu.wlj-portal-interaction-menu,
html[data-theme="dark"] .wlj-portal-interaction-menu,
body.darkMode .dropdown-menu.wlj-portal-interaction-menu,
body.darkMode .wlj-portal-interaction-menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  border-color: var(--wlj-glass-edge) !important;
}

/* Company actions menu — in-flow glass (matches global search; no portal) */
html body.wlj-neat-gradient-active .wlj-company-action-menu,
html body .wlj-company-action-menu,
.wlj-company-action-menu.InteractionModal,
.postMenu .wlj-company-action-menu,
.tabDropdownWrapper .wlj-company-action-menu {
  position: absolute !important;
  top: calc(100% + 6px) !important;
  inset-inline-end: 0 !important;
  left: auto !important;
  right: 0 !important;
  transform: none !important;
  float: none !important;
  width: max-content !important;
  min-width: 180px !important;
  max-width: min(320px, calc(100vw - 24px)) !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  z-index: 1080 !important;
}

html body.wlj-neat-gradient-active .wlj-company-action-menu::before,
html body .wlj-company-action-menu::before,
.wlj-company-action-menu::before {
  content: none !important;
  display: none !important;
}

html body.wlj-neat-gradient-active .profileCard:has(.postMenu.is-open),
html body .profileCard:has(.postMenu.is-open),
html body.wlj-neat-gradient-active .Recommendations-hashtags:has(.postMenu.is-open),
html body .Recommendations-hashtags:has(.postMenu.is-open),
html body.wlj-neat-gradient-active .rigtside-recommendation:has(.postMenu.is-open),
html body .rigtside-recommendation:has(.postMenu.is-open),
html body.wlj-neat-gradient-active .sidebar-widget-card:has(.postMenu.is-open),
html body .sidebar-widget-card:has(.postMenu.is-open) {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  overflow: visible !important;
  z-index: 45 !important;
}

/* Portal radius lock — buttons and cards are 4px. Avatars stay circular. */
:root {
  --wlj-card-radius: 4px;
  --wlj-btn-radius: 4px;
  --wlj-radius-btn: 4px;
  --wlj-radius-sm: 4px;
  --wlj-radius: 4px;
  --wlj-radius-lg: 4px;
  --wlj-modal-radius: 4px;
  --wlj-nav-radius: 4px;
  --wlj-input-radius: 4px;
  --bs-border-radius: 4px;
  --bs-btn-border-radius: 4px;
  --bs-card-border-radius: 4px;
}

html body .card:not(.ImagesliderScroller):not(.job-apps-table-card):not(.transaction-table),
html body .wlj-card,
html body .modal-content:not(.ImagesliderScroller),
html body .ant-modal-content,
html body .MuiDialog-paper,
html body .MuiMenu-paper,
html body .MuiPopover-paper,
html body .swal2-popup,
html body .popover,
html body .offcanvas,
html body .Toastify__toast,
html body .accordion,
html body .accordion-item,
html body .dropdown-menu,
html body .suggestions-dropdown,
html body .featured-card,
html body .jobCard,
html body .draftPost,
html body .PeopleCard,
html body .HuzPeopleCard,
html body .profileCard,
html body .new-profileCard,
html body .UpdateProfile,
html body .new-UpdateProfile,
html body .aboutMe,
html body .createPostContainer,
html body .postAdd:not(.modal),
html body .pr-card,
html body .sidebar-widget-card,
html body .sidebar-light-card,
html body .sidebar-nav-card,
html body .sidebar-companies-card,
html body .settingSidebar,
html body .setting-container,
html body .recommendation-card,
html body .blogSingleCard,
html body .help-card,
html body .search-result-card,
html body .custom-message-card,
html body .connection-card,
html body .Adwrapper,
html body .main-container,
html body .main-container.connectionPage,
html body .main-container.manageJobsPage,
html body .job-notification-card,
html body .job-apps-card,
html body .wlj-ats-panel,
html body .wlj-header-quick-panel,
html body .chat-box,
html body .chat-box-main-container {
  border-radius: 4px !important;
}

html body button:not(.rounded-circle):not(.btn-close):not(.MuiIconButton-root),
html body .btn:not(.rounded-circle):not(.MuiIconButton-root),
html body .wljPrimaryBtn,
html body .wljSecondaryBtn,
html body .wljSecondaryBtnR,
html body .wljDangerBtn,
html body .wlj-btn-ghost,
html body .wlj-chip-btn,
html body .mainBtn,
html body .mainBtn1,
html body .mainBtn4,
html body .closebtn,
html body .compBtn,
html body .messageBtn,
html body .accordion-button,
html body .nav-pills .nav-link,
html body .wlj-filter-chip,
html body .resume-action-button .wljPrimaryBtn,
html body .resume-action-button .wljSecondaryBtn,
html body .resume-action-button .wljDangerBtn,
html body .PeopleCard .wljPrimaryBtn,
html body .PeopleCard .wljSecondaryBtn,
html body .PeopleCard .wljDangerBtn,
html body .jobCard .jobAction .mainBtn,
html body .jobCard .jobAction .mainBtn4,
html body .noti-btn {
  border-radius: 4px !important;
}

html body .rounded-circle,
html body .avatar,
html body .MuiAvatar-root,
html body .MuiIconButton-root,
html body .circle-img,
html body img.circle-img,
html body .UserImageRes img,
html body .nav-avatar,
html body .ImgAvatar,
html body .ImgAvatar img,
html body .profileImg,
html body .profileImg img,
html body .image-cont img,
html body .chat-msg-avatar,
html body .imgBg1,
html body .imgBg1 img,
html body .mainheader .notification .nav-link,
html body .mainheader .message-icon,
html body .mainheader .user .dropdown-toggle {
  border-radius: 50% !important;
}

html body .sideLinks.leftNav .UserImageRes,
html body .sideLinks.leftNav .sidebar-profile-link,
html body .sideLinks.leftNav .personal.sidebar-light-card,
html body .sideLinks.leftNav a.UserImageRes.personal {
  border-radius: 4px !important;
}

html body .chat-msg-avatar,
html body .messages-cont > div:not(.message-box) > img,
html body .write-message-text > .me-2 img {
  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  min-height: 30px !important;
  max-width: 30px !important;
  max-height: 30px !important;
  object-fit: cover !important;
}

html body .write-message-row > .me-2 img,
html body .write-message-row .chat-msg-avatar {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  max-width: 40px !important;
  max-height: 40px !important;
}

html body .imgBg1 {
  flex-shrink: 0;
}

html body .sidebar-companies-card .imgBg1 {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  overflow: hidden !important;
}

html body .imgBg1 img,
html body .imgBg1 .nav-avatar,
html body .sidebar-companies-card .imgBg1 img {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  object-fit: cover !important;
  display: block;
}

html body .jobCard .badge.badge-job,
html body .jobCard .badge.badge-date,
html body .jobCard .wlj-status-chip {
  border-radius: 999px !important;
}

html body .followerTabs,
html body .followerTabsNews,
html body .businessCardButton,
html body .followerTabsNewsInnerbusinessCardButton,
html body .Post-Action-Content.bordertop-custom {
  border-top: none !important;
  box-shadow: none !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .postAdd .postAdd-action-icons svg,
html body .postAdd .postAdd-action-icons .MuiSvgIcon-root,
html body .featured-card.feature-responsive .postAdd-action-icons svg,
html body .featured-card.feature-responsive .postAdd-action-icons .MuiSvgIcon-root,
html body .postengagement .postAction svg,
html body .postengagement .postAction .MuiSvgIcon-root,
html body .wlj-post-action-icon.MuiSvgIcon-root {
  width: var(--wlj-icon-size, 20px) !important;
  height: var(--wlj-icon-size, 20px) !important;
  min-width: var(--wlj-icon-size, 20px) !important;
  min-height: var(--wlj-icon-size, 20px) !important;
  max-width: var(--wlj-icon-size, 20px) !important;
  max-height: var(--wlj-icon-size, 20px) !important;
  font-size: var(--wlj-icon-size, 20px) !important;
}

html body .postAdd .postAdd-action-icons svg.postAdd-article-icon,
html body .featured-card.feature-responsive .postAdd-action-icons svg.postAdd-article-icon {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
  max-width: 16px !important;
  max-height: 16px !important;
  font-size: 16px !important;
}

html body .auth-split .auth-submit,
html body .auth-split .auth-submit > span,
html body .auth-split .auth-submit svg,
html body .auth-split .auth-submit .MuiSvgIcon-root,
html body .auth-split .auth-feature-icon,
html body .auth-split .auth-feature-icon svg,
html body .auth-split .auth-feature-icon .MuiSvgIcon-root,
html body .auth-split .auth-split-count {
  color: #fff !important;
  -webkit-text-fill-color: #fff !important;
  fill: currentColor !important;
}

/* Portal list rows — same wash hover as Connections / Followers */
html body.wlj-neat-gradient-active .follow-list > .aboutMe,
html body.wlj-neat-gradient-active .profileTabContent .follow-list .aboutMe,
html body.wlj-neat-gradient-active .followers-main-container .aboutMe {
  margin: 0 !important;
  padding: 12px 16px !important;
  border-radius: var(--wlj-card-radius, 4px) !important;
  box-shadow: none !important;
  isolation: isolate;
}

html body.wlj-neat-gradient-active .follow-list > .aboutMe:hover,
html body.wlj-neat-gradient-active .follow-list > .aboutMe:focus-within,
html body.wlj-neat-gradient-active .profileTabContent .follow-list .aboutMe:hover,
html body.wlj-neat-gradient-active .followers-main-container .aboutMe:hover,
html body.wlj-neat-gradient-active .followers-main-container .aboutMe:focus-within,
html body.wlj-neat-gradient-active .connection-card .message-cards:hover,
html body.wlj-neat-gradient-active .sidebar-widget-card .listLink > div:hover,
html body.wlj-neat-gradient-active .sidebar-widget-card .listLink li:hover,
html body.wlj-neat-gradient-active .rigtside-recommendation .listLink li:hover,
html body.wlj-neat-gradient-active .rigtside-recommendation .navLinks:hover,
html body.wlj-neat-gradient-active .Recommendations-hashtags .listLink li:hover,
html body.wlj-neat-gradient-active .Recommendations-hashtags .navLinks:hover,
html body.wlj-neat-gradient-active .notification-main-container .custom-message-card:hover,
html body.wlj-neat-gradient-active .PeopleCard:hover,
html body.wlj-neat-gradient-active .HuzPeopleCard:hover,
html body.wlj-neat-gradient-active .sr-people-card:hover,
html body.wlj-neat-gradient-active .sr-entity-card:hover,
html body.wlj-neat-gradient-active .recommendation-card:hover,
html body.wlj-neat-gradient-active .pr-card:hover,
html body.wlj-neat-gradient-active .search-result-card:hover {
  background: color-mix(
      in srgb,
      var(--wlj-primary, var(--buttonBase, #00419f)) 8%,
      var(--wlj-glass-surface, rgba(255, 255, 255, 0.14))
    ) !important;
  background-color: color-mix(
      in srgb,
      var(--wlj-primary, var(--buttonBase, #00419f)) 8%,
      var(--wlj-glass-surface, rgba(255, 255, 255, 0.14))
    ) !important;
  transform: none !important;
  box-shadow: none !important;
}

html[data-theme="dark"] body.wlj-neat-gradient-active .follow-list > .aboutMe:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .followers-main-container .aboutMe:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .PeopleCard:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .notification-main-container .custom-message-card:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .sidebar-widget-card .listLink li:hover,
html[data-theme="dark"] body.wlj-neat-gradient-active .rigtside-recommendation .navLinks:hover,
body.darkMode.wlj-neat-gradient-active .follow-list > .aboutMe:hover,
body.darkMode.wlj-neat-gradient-active .followers-main-container .aboutMe:hover,
body.darkMode.wlj-neat-gradient-active .PeopleCard:hover,
body.darkMode.wlj-neat-gradient-active .notification-main-container .custom-message-card:hover,
body.darkMode.wlj-neat-gradient-active .sidebar-widget-card .listLink li:hover,
body.darkMode.wlj-neat-gradient-active .rigtside-recommendation .navLinks:hover {
  background: color-mix(
      in srgb,
      var(--wlj-primary, #60a5fa) 16%,
      var(--wlj-glass-surface, rgba(28, 28, 36, 0.32))
    ) !important;
  background-color: color-mix(
      in srgb,
      var(--wlj-primary, #60a5fa) 16%,
      var(--wlj-glass-surface, rgba(28, 28, 36, 0.32))
    ) !important;
}

/* Portal tabs — icon + label, muted idle, navy underline (not filled chips) */
:root {
  --wlj-tab-inactive: #5a6b87;
  --wlj-tab-active: #002760;
}
html[data-theme="dark"] {
  --wlj-tab-inactive: #94a3b8;
  --wlj-tab-active: #e8eef8;
}
html body .tabButton.connectionTab > button,
html body .tabButton.connectionTab > a,
html body .tabButton.connectionTab > div,
html body .tabButton > button,
html body .tabButton > a,
html body .tabButton.new-tabButton > div,
html body .manageJobsTab > div,
html body .allconnectionTab > div,
html body .followerTabsNewsInnertabButton > a,
html body .friend-main-container.discovery-page .tabButton.connectionTab > button {
  position: relative;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  color: var(--wlj-tab-inactive, #5a6b87) !important;
}
html body .tabButton .tabName,
html body .tabButton.connectionTab .tabName,
html body .tabButton.connectionTab .MuiSvgIcon-root,
html body .tabButton .connection-tab-icon,
html body .tabButton .manage-jobs-tab-icon,
html body .manageJobsTab .tabName,
html body .manageJobsTab .MuiSvgIcon-root {
  color: var(--wlj-tab-inactive, #5a6b87) !important;
}
html body .tabButton.connectionTab > button.activeTab,
html body .tabButton.connectionTab > a.activeTab,
html body .tabButton.connectionTab > div.activeTab,
html body .tabButton.new-tabButton > div.activeTab,
html body .tabButton.new-tabButton > a.activeTab,
html body .manageJobsTab > div.activeTab,
html body .allconnectionTab > div.activeTab,
html body .followerTabsNewsInnertabButton > a.activeTab,
html body .friend-main-container.discovery-page .tabButton.connectionTab > button.activeTab {
  background: transparent !important;
  background-color: transparent !important;
  color: var(--wlj-tab-active, #002760) !important;
  border-radius: 0 !important;
}
html body .tabButton .activeTab .tabName,
html body .tabButton a.activeTab .tabName,
html body .tabButton button.activeTab .tabName,
html body .tabButton div.activeTab .tabName,
html body .tabButton.connectionTab > button.activeTab .MuiSvgIcon-root,
html body .tabButton.connectionTab > a.activeTab .MuiSvgIcon-root,
html body .tabButton.connectionTab > div.activeTab .MuiSvgIcon-root,
html body .tabButton .activeTab .MuiSvgIcon-root,
html body .tabButton .activeTab .connection-tab-icon,
html body .tabButton .activeTab .manage-jobs-tab-icon,
html body .manageJobsTab > div.activeTab .tabName,
html body .manageJobsTab > div.activeTab .MuiSvgIcon-root {
  color: var(--wlj-tab-active, #002760) !important;
}
html body .tabButton.connectionTab > button.activeTab::after,
html body .tabButton.connectionTab > a.activeTab::after,
html body .tabButton.connectionTab > div.activeTab::after,
html body .tabButton.new-tabButton > div.activeTab::after,
html body .tabButton.new-tabButton > a.activeTab::after,
html body .manageJobsTab > div.activeTab::after,
html body .allconnectionTab > div.activeTab::after,
html body .followerTabsNewsInnertabButton > a.activeTab::after,
html body .profileHeader .tabButton > a.activeTab::after,
html body .profileHeader .tabButton > div.activeTab::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: var(--wlj-tab-active, #002760) !important;
  border-radius: 3px;
  display: block !important;
}

/* Modal pane + fields — portal blue frost, readable text (last in this sheet) */
html body .modal.show::before,
html body .modal.fade.show::before,
html body .modal.postAdd.show::before,
html body.wlj-neat-gradient-active .modal.show::before,
html body.wlj-neat-gradient-active .modal.fade.show::before {
  background-color: var(--wlj-modal-scrim) !important;
  backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
}

html body .modal-backdrop.show,
html body .chat-forward-modal-backdrop,
html body .message-confirm-modal-backdrop {
  background-color: var(--wlj-modal-scrim) !important;
  backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller),
html body .postAdd.modal .modal-content:not(.ImagesliderScroller),
html body .postAdd.jobCreate .modal-content,
html body .skillModal .modal-content,
html body .dashboard-modal .modal-content,
html body .chat-forward-modal,
html body .message-confirm-modal,
html body .ant-modal-content,
html body .MuiDialog-paper,
html body .swal2-popup,
html body .modal-content.articleContainer,
html body .modal .container.articleContainer,
html body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
html body.wlj-neat-gradient-active .postAdd.jobCreate .modal-content,
html body.wlj-neat-gradient-active .chat-forward-modal,
html body.wlj-neat-gradient-active .message-confirm-modal,
html body.wlj-neat-gradient-active .ant-modal-content,
html body.wlj-neat-gradient-active .MuiDialog-paper,
html body.wlj-neat-gradient-active .swal2-popup,
html body.wlj-neat-gradient-active .modal-content.articleContainer,
html[data-theme="dark"] body .modal-content:not(.ImagesliderScroller),
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
html[data-theme="dark"] body .chat-forward-modal,
html[data-theme="dark"] body .message-confirm-modal,
html[data-theme="dark"] body .ant-modal-content,
html[data-theme="dark"] body .MuiDialog-paper,
html[data-theme="dark"] body .swal2-popup,
body.darkMode .modal-content:not(.ImagesliderScroller),
body.darkMode.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller),
body.darkMode .chat-forward-modal,
body.darkMode .message-confirm-modal,
body.darkMode .ant-modal-content,
body.darkMode .MuiDialog-paper,
body.darkMode .swal2-popup {
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  background-color: var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
  color: var(--wlj-text-primary) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) > .modalBorder:has(h6),
html body .modal .modal-content:not(.ImagesliderScroller) > .modalBorder:has(h5),
html body .modal .modal-content:not(.ImagesliderScroller) .setting-header,
html body .modal .modal-content:not(.ImagesliderScroller) > .modal-header,
html body.wlj-neat-gradient-active .modal .modal-content:not(.ImagesliderScroller) > .modalBorder:has(h6),
html body.wlj-neat-gradient-active .modal .modal-content:not(.ImagesliderScroller) .setting-header,
html body.wlj-neat-gradient-active .modal .modal-content:not(.ImagesliderScroller) > .modal-header,
html body .modal .articleContainer .modalBorder:has(h6),
html body .articleContainer .modalBorder:has(h6),
html body.wlj-neat-gradient-active .articleContainer .modalBorder:has(h6),
html body .modal .articleContainer > .setting-header,
html body .modal .articleContainer > .modalBorder.setting-header,
html body.wlj-neat-gradient-active .articleContainer > .setting-header,
html body .chat-forward-modal-header,
html body .message-confirm-modal-header {
  background: transparent !important;
  background-image: none !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  align-self: stretch !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:not(:has(h6)):not(:has(h5)) {
  background: transparent !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:has(> .mainBtn1),
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:has(> .mainBtn),
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:has(> .wljPrimaryBtn),
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:has(> .wljDangerBtn),
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder:has(> .closebtn) {
  background: transparent !important;
  background-color: transparent !important;
  justify-content: flex-end !important;
  border-bottom: none !important;
  border-top: 1px solid var(--wlj-divider) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) h6,
html body .modal .modal-content:not(.ImagesliderScroller) h5,
html body .modal .modal-content:not(.ImagesliderScroller) .setting-header h6,
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder h6,
html body.wlj-neat-gradient-active .modal-content:not(.ImagesliderScroller) h6 {
  color: var(--wlj-text-primary) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) label,
html body .modal .modal-content:not(.ImagesliderScroller) .form-label,
html body .modal .settings label:not(.form-check-label),
html body .modal .CreateCompany label:not(.form-check-label),
html body.wlj-neat-gradient-active .modal-content label,
html body.wlj-neat-gradient-active .jobCreate label,
html body.wlj-neat-gradient-active .CreateCompany label {
  color: var(--wlj-text-primary) !important;
  opacity: 1 !important;
  text-align: start !important;
  justify-content: flex-start !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .form-control,
html body .modal .modal-content:not(.ImagesliderScroller) .form-select,
html body .modal .modal-content:not(.ImagesliderScroller) textarea,
html body .modal .modal-content:not(.ImagesliderScroller) select,
html body .modal .modal-content:not(.ImagesliderScroller) .flatpickr-input,
html body .modal .modal-content:not(.ImagesliderScroller) .Dark-Input-from-group,
html body .modal .modal-content:not(.ImagesliderScroller) .JobTitleInputField,
html body .modal .modal-content:not(.ImagesliderScroller) .react-select__control,
html body .modal .modal-content:not(.ImagesliderScroller) .wlj-select__control,
html body .modal .modal-content:not(.ImagesliderScroller) [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .modal-content .form-control,
html body.wlj-neat-gradient-active .modal-content textarea,
html body.wlj-neat-gradient-active .modal-content .react-select__control,
html body.wlj-neat-gradient-active .modal-content .wlj-select__control,
html body.wlj-neat-gradient-active .modal-content [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .jobCreate .form-control,
html body.wlj-neat-gradient-active .jobCreate textarea,
html body.wlj-neat-gradient-active .jobCreate .JobTitleInputField,
html body.wlj-neat-gradient-active .jobCreate .react-select__control,
html body.wlj-neat-gradient-active .jobCreate .wlj-select__control,
html body.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany .wlj-select__control,
html body.wlj-neat-gradient-active .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control),
html body.wlj-neat-gradient-active .postAdd.jobCreate .JobTitleInputField,
html body.wlj-neat-gradient-active .CreateCompany .form-control,
html body.wlj-neat-gradient-active .CreateCompany .react-select__control,
html body.wlj-neat-gradient-active .CreateCompany [class*="__control"]:not(.privacySelect__control) {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  color: var(--wlj-text-primary) !important;
  border-color: var(--wlj-field-border) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .form-control::placeholder,
html body .modal .modal-content:not(.ImagesliderScroller) textarea::placeholder,
html body .modal .modal-content:not(.ImagesliderScroller) input::placeholder,
html body .modal .modal-content:not(.ImagesliderScroller) [class*="placeholder"],
html body .modal .modal-content:not(.ImagesliderScroller) [class$="-placeholder"],
html body .modal .modal-content:not(.ImagesliderScroller) .react-select__placeholder,
html body.wlj-neat-gradient-active .modal-content [class*="placeholder"],
html body.wlj-neat-gradient-active .modal-content [class$="-placeholder"],
html body.wlj-neat-gradient-active .jobCreate [class*="placeholder"],
html body.wlj-neat-gradient-active .jobCreate [class$="-placeholder"],
html body.wlj-neat-gradient-active .CreateCompany [class*="placeholder"] {
  color: var(--wlj-modal-placeholder) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--wlj-modal-placeholder) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) [class*="singleValue"],
html body .modal .modal-content:not(.ImagesliderScroller) [class$="-singleValue"],
html body .modal .modal-content:not(.ImagesliderScroller) .react-select__single-value,
html body.wlj-neat-gradient-active .modal-content [class*="singleValue"],
html body.wlj-neat-gradient-active .jobCreate [class*="singleValue"] {
  color: var(--wlj-text-primary) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .react-select__input,
html body .modal .modal-content:not(.ImagesliderScroller) input[class*="__input"] {
  color: var(--wlj-text-primary) !important;
}

/* Chrome glass — header, footer, post options (locks last) */
html body .footer-nav {
  background: var(--wlj-glass-sheen), var(--wlj-glass-bg, rgba(255, 255, 255, 0.72)) !important;
  background-color: var(--wlj-glass-bg, rgba(255, 255, 255, 0.72)) !important;
  backdrop-filter: saturate(180%) blur(20px) !important;
  -webkit-backdrop-filter: saturate(180%) blur(20px) !important;
  border-top: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: 0 -8px 24px rgba(16, 24, 40, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.45) !important;
}

/* Emoji picker — lock to denser frost matching .mainheader look */
html body .EmojiPickerReact,
html body .themed-emoji-picker-wrap .EmojiPickerReact,
html body .HuzEmojiPickerChangeColor .EmojiPickerReact,
html body .messageEmoji .EmojiPickerReact,
html body .wlj-portal-emoji-picker .EmojiPickerReact {
  --epr-bg-color: transparent;
  --epr-category-label-bg-color: transparent;
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  background-color: var(--wlj-modal-glass, rgba(255, 255, 255, 0.82)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
  isolation: isolate;
  z-index: var(--wlj-z-picker, 1350) !important;
}

html[data-theme="dark"] body .EmojiPickerReact,
html[data-theme="dark"] body .themed-emoji-picker-wrap .EmojiPickerReact,
html[data-theme="dark"] body .wlj-portal-emoji-picker .EmojiPickerReact,
body.darkMode .EmojiPickerReact,
body.darkMode .themed-emoji-picker-wrap .EmojiPickerReact,
body.darkMode .wlj-portal-emoji-picker .EmojiPickerReact {
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, rgba(28, 28, 36, 0.86)) !important;
  background-color: var(--wlj-modal-glass, rgba(28, 28, 36, 0.86)) !important;
}

html body .wlj-portal-emoji-picker,
html body .HuzEmojiPickerChangeColor.wlj-portal-emoji-picker {
  position: fixed !important;
  right: auto !important;
  bottom: auto !important;
  z-index: var(--wlj-z-picker, 1350) !important;
}

html body .HuzEmojiPickerChangeColor,
html body .themed-emoji-picker-wrap {
  z-index: var(--wlj-z-picker, 1350) !important;
}

html body .featured-card:has(.HuzEmojiPickerChangeColor) .mainBtn1,
html body .featured-card:has(.themed-emoji-picker-wrap) .mainBtn1,
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor) .mainBtn1,
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap) .mainBtn1 {
  position: relative;
  z-index: 0 !important;
}

html body .newsfeedPosts:has(.HuzEmojiPickerChangeColor) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)),
html body .newsfeedPosts:has(.themed-emoji-picker-wrap) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)),
html body .new-newsfeedPosts:has(.HuzEmojiPickerChangeColor) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)),
html body .new-newsfeedPosts:has(.themed-emoji-picker-wrap) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)),
html body.wlj-neat-gradient-active .newsfeedPosts:has(.HuzEmojiPickerChangeColor) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)),
html body.wlj-neat-gradient-active .newsfeedPosts:has(.themed-emoji-picker-wrap) .featured-card:not(:has(.HuzEmojiPickerChangeColor)):not(:has(.themed-emoji-picker-wrap)) {
  z-index: 1 !important;
}

html[data-theme="dark"] body .footer-nav,
body.darkMode .footer-nav {
  background: var(--wlj-glass-sheen), var(--wlj-glass-bg, rgba(28, 28, 36, 0.72)) !important;
  background-color: var(--wlj-glass-bg, rgba(28, 28, 36, 0.72)) !important;
  border-top-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.14)) !important;
}

html body .footer-nav > a,
html body .footer-nav > .footer-action {
  flex: 1 1 0;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 0;
  padding: 0 !important;
  text-align: center;
}

html body .footer-nav .navLinks,
html[dir="rtl"] body .footer-nav .navLinks {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  margin: 0 !important;
  padding: 0 !important;
  gap: 0 !important;
  border-radius: 50%;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box;
  line-height: 0;
  text-align: center;
  direction: ltr;
}

html body .footer-nav .navLinks .MuiSvgIcon-root,
html body .footer-nav .navLinks .material-symbols-sharp,
html body .footer-nav .navLinks svg {
  width: 22px !important;
  height: 22px !important;
  font-size: 22px !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 22px;
  position: static !important;
  transform: none !important;
}

html body .footer-nav > a.active .navLinks .MuiSvgIcon-root,
html body .footer-nav > a.active .navLinks .material-symbols-sharp {
  color: var(--wlj-primary, #00419f) !important;
}

html body .mainheader .notification .nav-link,
html body .mainheader .notification .notification-toggle,
html body .mainheader .notification .btn,
html body .mainheader.mobileView .header-action .notification .notification-toggle,
html body .mainheader.mobileView .header-action .notification .nav-link {
  background: var(--wlj-glass-sheen), var(--wlj-glass-field, rgba(255, 255, 255, 0.42)) !important;
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45) !important;
}

html[data-theme="dark"] body .mainheader .notification .nav-link,
html[data-theme="dark"] body .mainheader .notification .notification-toggle,
body.darkMode .mainheader .notification .nav-link,
body.darkMode .mainheader .notification .notification-toggle {
  background: var(--wlj-glass-sheen), rgba(255, 255, 255, 0.08) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.16)) !important;
}

html body .postengagement > .d-flex.justify-content-between,
html body .postengagement > .d-flex.justify-content-between.pt-1,
html body .Post-Action-Content.bordertop-custom {
  gap: 4px !important;
  padding: 6px 8px 8px !important;
}

html body .postengagement .postAction,
html body .postengagement .likeActions.postAction,
html body .Post-Action-Content > .postAction {
  gap: 8px !important;
  padding: 8px 6px !important;
  min-height: 44px;
}

html body .postAdd .postAdd-action-icons,
html body .featured-card.feature-responsive .postAdd-action-icons {
  gap: 4px !important;
}

html body .postAdd .postAdd-action-icons form,
html body .featured-card.feature-responsive .postAdd-action-icons form,
html body .postAdd .postAdd-action-icons span,
html body .featured-card.feature-responsive .postAdd-action-icons span {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 50%;
  background: var(--wlj-glass-sheen), var(--wlj-glass-field, rgba(255, 255, 255, 0.36));
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.45));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

html body .postAdd .postAdd-action-icons > .MuiSvgIcon-root,
html body .featured-card.feature-responsive .postAdd-action-icons > .MuiSvgIcon-root {
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  border-radius: 50%;
  background: var(--wlj-glass-sheen), var(--wlj-glass-field, rgba(255, 255, 255, 0.36));
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.45));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

html body .feed-post .comment-gradient,
html body .feed-post .comment-gradient-article,
html body .user-postOnlyContent .comment-gradient,
html body .user-postOnlyContent .comment-gradient-article {
  background: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .feed-post .addComment,
html body .user-postOnlyContent .addComment {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border-top: 0 !important;
  box-shadow: none !important;
}

html body .feed-post:has(.img-with-loader) .addComment,
html body .feed-post:has(.newsfeed-video) .addComment,
html body .feed-post:has(img.img-fluid) .addComment {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .feed-post:has(.img-with-loader) .comment-gradient:has(.commentBox),
html body .feed-post:has(.newsfeed-video) .comment-gradient:has(.commentBox),
html body .feed-post:has(img.img-fluid) .comment-gradient:has(.commentBox),
html body .feed-post:has(.img-with-loader) .comment-gradient-article:has(.commentBox) {
  position: absolute !important;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 8px 0 4px !important;
  background: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html[data-theme="dark"] body .feed-post .addComment,
body.darkMode .feed-post .addComment {
  background: transparent !important;
}

html[data-theme="dark"] body .feed-post:has(.img-with-loader) .addComment,
body.darkMode .feed-post:has(.img-with-loader) .addComment,
html[data-theme="dark"] body .feed-post:has(img.img-fluid) .addComment,
body.darkMode .feed-post:has(img.img-fluid) .addComment {
  background: transparent !important;
}

/* Overlay panes stay denser than page cards so feed copy cannot show through */
html body .react-select__menu,
html body.wlj-neat-gradient-active .react-select__menu,
html[data-theme="dark"] body.wlj-neat-gradient-active .react-select__menu,
body.darkMode.wlj-neat-gradient-active .react-select__menu,
html body .wlj-select__menu,
html body .privacySelect__menu,
html body [class*="__menu"]:not([class*="__menu-list"]):not([class*="__menu-portal"]):not([class*="__menu-notice"]),
html body div[class*="css-"][class$="-menu"],
html body .ant-select-dropdown,
html body.wlj-neat-gradient-active .ant-select-dropdown {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  z-index: var(--wlj-z-picker, 1350) !important;
}

html body .ant-select-dropdown,
html body.wlj-neat-gradient-active .ant-select-dropdown {
  overflow: auto !important;
  max-height: min(320px, 70vh) !important;
}

html body .react-select__menu-list,
html body.wlj-neat-gradient-active .react-select__menu-list,
html body .wlj-select__menu-list,
html body .privacySelect__menu-list,
html body [class*="__menu-list"] {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .modal-overlay,
html body.wlj-neat-gradient-active .modal-overlay {
  z-index: var(--wlj-z-overlay, 1250) !important;
  background: var(--wlj-modal-scrim, rgba(15, 23, 42, 0.16)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .modal-container.userauth,
html body.wlj-neat-gradient-active .modal-container.userauth,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal-container.userauth,
body.darkMode.wlj-neat-gradient-active .modal-container.userauth {
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, var(--wlj-modal-pane)) !important;
  background-color: var(--wlj-modal-glass, var(--wlj-modal-pane)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, saturate(180%) blur(24px)) !important;
  border: none !important;
  box-shadow: none !important;
  color: var(--wlj-text-primary) !important;
  border-radius: var(--wlj-modal-page-radius, 0px) !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;
  max-height: 100dvh !important;
  position: relative !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
}

html body .modal-container.userauth .form-control,
html body .modal-container.userauth .flatpickr-input,
html body .modal-container.userauth .react-select__control,
html body.wlj-neat-gradient-active .modal-container.userauth .form-control,
html body.wlj-neat-gradient-active .modal-container.userauth .react-select__control {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border-color: var(--wlj-field-border) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
  color: var(--wlj-text-primary) !important;
}

html body .modal-container.userauth .user-onboarding-choice,
html body .modal-container.userauth .user-onboarding-choice *,
html body .modal-container.userauth .user-onboarding-card,
html body .modal-container.userauth .user-onboarding-card * {
  cursor: pointer !important;
}

/* Last lock: fields frost the gradient the way .commentBox frosts a photo */
html body .form-control,
html body .form-select,
html body textarea.form-control,
html body textarea,
html body select,
html body .JobTitleInputField,
html body .GradeFieldEducation,
html body .Dark-Input-from-group,
html body .react-select__control,
html body .wlj-select__control,
html body [class*="__control"]:not(.privacySelect__control),
html body .auth-field,
html body .discovery-search,
html body .searchInput,
html body .searchInput.wlj-ddl-input,
html body .jobSearch .searchInput,
html body .job-search-workspace .searchInput,
html body .job-search-workspace .search-dropdown .searchInput,
html body.wlj-neat-gradient-active .searchInput,
html body.wlj-neat-gradient-active .searchInput.wlj-ddl-input,
html body.wlj-neat-gradient-active .jobSearch .searchInput,
html body .modal-content .form-select,
html body .modal-content textarea,
html body .modal-content .react-select__control,
html body .modal-content .wlj-select__control,
html body .postAdd .form-control,
html body .postAdd .JobTitleInputField,
html body .postAdd [class*="__control"]:not(.privacySelect__control),
html body .modal-container.userauth .form-control,
html body.wlj-neat-gradient-active .form-control,
html body.wlj-neat-gradient-active .searchInput,
html body.wlj-neat-gradient-active .jobSearch .searchInput,
html body.wlj-neat-gradient-active .job-search-workspace .search-dropdown .searchInput,
html body.wlj-neat-gradient-active .react-select__control,
html body.wlj-neat-gradient-active .modal-content .form-control,
html body.wlj-neat-gradient-active .jobCreate .JobTitleInputField,
html body.wlj-neat-gradient-active .jobCreate [class*="__control"]:not(.privacySelect__control) {
  background: var(--wlj-glass-sheen), rgba(255, 255, 255, 0.08) !important;
  background-color: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: saturate(180%) blur(28px) !important;
  -webkit-backdrop-filter: saturate(180%) blur(28px) !important;
  border: 1px solid rgba(255, 255, 255, 0.55) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), 0 8px 24px rgba(16, 24, 40, 0.06) !important;
}

html[data-theme="dark"] body .searchInput,
html[data-theme="dark"] body .form-control,
html[data-theme="dark"] body .react-select__control,
body.darkMode .searchInput,
body.darkMode .form-control {
  background: var(--wlj-glass-sheen), rgba(28, 28, 36, 0.22) !important;
  background-color: rgba(28, 28, 36, 0.22) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}

html body .search-container input,
html body .search-container .search-input,
html body .search-container .form-control,
html body .search-container .connection-input,
html body .wlj-search-field input,
html body .wlj-search-field .form-control,
html body .wlj-search-field .discovery-search,
html body .wlj-search-field .connection-input,
html body .auth-field input,
html body .searchInput input,
html body .searchInput .ant-select,
html body .searchInput .ant-select-selector,
html body .searchInput .ant-select-selection-search-input,
html body.wlj-neat-gradient-active .search-container input,
html body.wlj-neat-gradient-active .wlj-search-field input,
html body.wlj-neat-gradient-active .searchInput input,
html body.wlj-neat-gradient-active .searchInput .ant-select,
html body.wlj-neat-gradient-active .searchInput .ant-select-selector {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
}

/* Comment composer inner input — fully clear; outer .commentBox keeps glass */
html body .commentBox > input,
html body .commentBox > input:focus,
html body .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html body .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus-visible,
html body.wlj-neat-gradient-active .commentBox > input,
html body.wlj-neat-gradient-active .commentBox > input:focus,
html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus-visible,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
body.darkMode.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input),
body.darkMode.wlj-neat-gradient-active .commentBox input[type="text"]:not(.react-select__input):not([class*="__input"]):not([id^="react-select"]):not(.search-input):not(.ant-select-selection-search-input):focus,
html body .commentBox textarea,
html body .commentBox textarea:focus,
html body .feed-post .commentBox > input,
html body .user-postOnlyContent .commentBox > input,
html body .addComment .commentBox > input,
html body .postCommentWrapper .commentBox > input,
html body .modal-content .commentBox > input,
html body .modal-content .commentBox > input:focus,
html body .modal-content.ImagesliderScroller .commentBox > input,
html body .MobileCommentmodal .commentBox > input,
html body.wlj-neat-gradient-active .modal-content .commentBox > input,
html body.wlj-neat-gradient-active .modal-content .commentBox > input:focus,
html body.wlj-neat-gradient-active .ImagesliderScroller .commentBox > input,
html body.wlj-neat-gradient-active .MobileCommentmodal .commentBox > input {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  border-width: 0 !important;
  border-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

/* Search bars — same frost as .commentBox, on ::before only.
   Frost on the bar AND the inner input stacks and reads as a solid slab. */
html body .wlj-glass-search,
html body .search-container.wlj-glass-search,
html body .wlj-search-field,
html body .wlj-search-field.wlj-glass-search,
html body .job-search-workspace .job-search-toolbar .search-container,
html body .job-header .search-container,
html body .sidebar-widget-card .search-container,
html body .right-sidebar-split .search-container,
html body .mainheader .search-container,
html body.wlj-neat-gradient-active .wlj-glass-search,
html body.wlj-neat-gradient-active .wlj-search-field,
html body.wlj-neat-gradient-active .connectionPage .wlj-search-field,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field,
html body.wlj-neat-gradient-active .search-container,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container,
html body.wlj-neat-gradient-active .job-list-panel .job-search-toolbar .search-container,
html body.wlj-neat-gradient-active .job-header .search-container,
html body.wlj-neat-gradient-active .sidebar-widget-card .search-container,
html body.wlj-neat-gradient-active .right-sidebar-split .search-container,
html body.wlj-neat-gradient-active .mainheader .search-container,
html body.wlj-neat-gradient-active .headerLayout .search-container {
  position: relative !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid rgba(255, 255, 255, 0.55) !important;
  border-radius: var(--wlj-input-radius, 4px) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45), 0 8px 24px rgba(16, 24, 40, 0.08) !important;
}

html body.wlj-neat-gradient-active .search-container:focus-within,
html body.wlj-neat-gradient-active .wlj-glass-search:focus-within,
html body.wlj-neat-gradient-active .wlj-search-field:focus-within,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container:focus-within,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-search-field:focus-within,
body.darkMode.wlj-neat-gradient-active .search-container:focus-within,
body.darkMode.wlj-neat-gradient-active .wlj-search-field:focus-within {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .wlj-glass-search::before,
html body .search-container.wlj-glass-search::before,
html body .wlj-search-field::before,
html body .wlj-search-field.wlj-glass-search::before,
html body .job-search-workspace .job-search-toolbar .search-container::before,
html body .job-header .search-container::before,
html body .sidebar-widget-card .search-container::before,
html body .right-sidebar-split .search-container::before,
html body .mainheader .search-container::before,
html body.wlj-neat-gradient-active .wlj-glass-search::before,
html body.wlj-neat-gradient-active .wlj-search-field::before,
html body.wlj-neat-gradient-active .connectionPage .wlj-search-field::before,
html body.wlj-neat-gradient-active .friend-main-container.discovery-page .wlj-search-field::before,
html body.wlj-neat-gradient-active .search-container::before,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container::before,
html body.wlj-neat-gradient-active .job-list-panel .job-search-toolbar .search-container::before,
html body.wlj-neat-gradient-active .job-header .search-container::before,
html body.wlj-neat-gradient-active .sidebar-widget-card .search-container::before,
html body.wlj-neat-gradient-active .right-sidebar-split .search-container::before,
html body.wlj-neat-gradient-active .mainheader .search-container::before,
html body.wlj-neat-gradient-active .headerLayout .search-container::before {
  content: "" !important;
  display: block !important;
  position: absolute !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  border-radius: inherit !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-search-fill, rgba(255, 255, 255, 0.1)) !important;
  background-color: var(--wlj-glass-search-fill, rgba(255, 255, 255, 0.1)) !important;
  backdrop-filter: var(--wlj-glass-search-blur, saturate(180%) blur(18px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-search-blur, saturate(180%) blur(18px)) !important;
  box-shadow: none !important;
}

html[data-theme="dark"] body .wlj-glass-search::before,
html[data-theme="dark"] body .wlj-search-field::before,
html[data-theme="dark"] body .search-container::before,
html[data-theme="dark"] body.wlj-neat-gradient-active .search-container::before,
html[data-theme="dark"] body.wlj-neat-gradient-active .wlj-search-field::before,
body.darkMode .wlj-glass-search::before,
body.darkMode .wlj-search-field::before,
body.darkMode .search-container::before,
body.darkMode.wlj-neat-gradient-active .search-container::before,
body.darkMode.wlj-neat-gradient-active .wlj-search-field::before {
  background: var(--wlj-glass-sheen), var(--wlj-glass-search-fill, rgba(28, 28, 36, 0.22)) !important;
  background-color: var(--wlj-glass-search-fill, rgba(28, 28, 36, 0.22)) !important;
}

html body .wlj-glass-search > *,
html body .wlj-search-field > *,
html body .search-container > .search-icon,
html body .search-container > .material-symbols-sharp,
html body .search-container > .MuiSvgIcon-root,
html body .search-container > .search-input-container,
html body.wlj-neat-gradient-active .search-container > * {
  position: relative;
  z-index: 1;
}

html body .wlj-glass-search input,
html body .wlj-glass-search input[type="text"],
html body .wlj-glass-search input[type="search"],
html body .wlj-glass-search input[type="text"]:not(.react-select__input):not([class*="__input"]),
html body .wlj-glass-search input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body .wlj-glass-search input[type="text"]:not(.react-select__input):not([class*="__input"]):focus,
html body .wlj-glass-search input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body .wlj-glass-search .search-input,
html body .wlj-glass-search .search-input:focus,
html body .wlj-glass-search .form-control,
html body .wlj-glass-search .form-control:focus,
html body .wlj-glass-search .connection-input,
html body .wlj-glass-search .search-input-container,
html body .wlj-search-field input,
html body .wlj-search-field input[type="search"],
html body .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body .wlj-search-field .form-control,
html body .wlj-search-field .form-control:focus,
html body .wlj-search-field .connection-input,
html body .wlj-search-field .connection-input:focus,
html body .wlj-search-field .discovery-search,
html body .wlj-search-field .discovery-search:focus,
html body .wlj-search-field .HuzChnagesConnection,
html body.wlj-neat-gradient-active .wlj-search-field input,
html body.wlj-neat-gradient-active .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .wlj-search-field .form-control:focus,
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .connectionPage .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .search-container input,
html body.wlj-neat-gradient-active .search-container input[type="text"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .search-container input[type="text"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .search-container input[type="search"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .search-container .search-input,
html body.wlj-neat-gradient-active .search-container .search-input:focus,
html body.wlj-neat-gradient-active .search-container .search-input-container,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container input[type="text"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container input[type="text"]:not(.react-select__input):not([class*="__input"]):focus,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container .search-input,
html body.wlj-neat-gradient-active .job-search-workspace .job-search-toolbar .search-container .search-input:focus {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  padding: 0 !important;
}

/* Modal / picker search — beat .modal-content .form-control frost */
html body .modal-content .wlj-search-field,
html body .postAdd .wlj-search-field,
html body .connection-picker-search .wlj-search-field,
html body .connection-card .wlj-search-field,
html body.wlj-neat-gradient-active .modal-content .wlj-search-field,
html body.wlj-neat-gradient-active .postAdd .wlj-search-field,
html body.wlj-neat-gradient-active .connection-picker-search .wlj-search-field,
html body.wlj-neat-gradient-active .modal-content .wlj-glass-search {
  position: relative !important;
  overflow: visible !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid rgba(255, 255, 255, 0.55) !important;
  border-radius: var(--wlj-input-radius, 4px) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45), 0 8px 24px rgba(16, 24, 40, 0.08) !important;
}

html body .modal-content .wlj-search-field::before,
html body .postAdd .wlj-search-field::before,
html body .connection-picker-search .wlj-search-field::before,
html body .connection-card .wlj-search-field::before,
html body.wlj-neat-gradient-active .modal-content .wlj-search-field::before,
html body.wlj-neat-gradient-active .postAdd .wlj-search-field::before,
html body.wlj-neat-gradient-active .connection-picker-search .wlj-search-field::before {
  content: "" !important;
  display: block !important;
  position: absolute !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  border-radius: inherit !important;
  background: var(--wlj-glass-sheen), rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: saturate(180%) blur(18px) !important;
  -webkit-backdrop-filter: saturate(180%) blur(18px) !important;
  box-shadow: none !important;
}

html body .modal-content .wlj-search-field > *,
html body .postAdd .wlj-search-field > *,
html body .connection-picker-search .wlj-search-field > * {
  position: relative;
  z-index: 1;
}

html body .modal-content .wlj-search-field .form-control,
html body .modal-content .wlj-search-field .form-control:focus,
html body .modal-content .wlj-search-field input,
html body .modal-content .wlj-search-field input[type="search"],
html body .modal-content .wlj-search-field input.search-input,
html body .postAdd .form-control.search-input,
html body .postAdd .wlj-search-field .form-control,
html body .postAdd .wlj-search-field .form-control:focus,
html body .postAdd .connection-card .wlj-search-field .form-control,
html body .connection-card .wlj-search-field .form-control,
html body .connection-picker-search .form-control,
html body.wlj-neat-gradient-active .modal-content .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .modal-content .wlj-search-field .form-control:focus,
html body.wlj-neat-gradient-active .modal-content .wlj-search-field input[type="search"]:not(.react-select__input):not([class*="__input"]),
html body.wlj-neat-gradient-active .postAdd .wlj-search-field .form-control,
html body.wlj-neat-gradient-active .postAdd .wlj-search-field .form-control:focus {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  padding: 0 !important;
  min-height: 0 !important;
}

html body .privacySelect .privacySelect__control,
html body .privacySelect .privacySelect__control--is-focused,
html body .postAdd .post-editor-container,
html body .postAdd .ql-container {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border: 1px solid var(--wlj-field-border) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body .articleContainer .form-quill {
  background: var(--wlj-canvas, #f8fafc) !important;
  background-color: var(--wlj-canvas, #f8fafc) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: 1px solid var(--wlj-field-border) !important;
}

html body .articleContainer .ql-toolbar,
html body .articleContainer .ql-container,
html body .modal-content .articleContainer .ql-container {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body .privacySelect .privacySelect__control,
html body .privacySelect .privacySelect__control--is-focused {
  min-height: 36px !important;
  height: 36px !important;
}

html body .postAdd .post-editor-container .ql-container,
html body .postAdd .post-editor-container .ql-editor,
html body .articleContainer .ql-editor,
html body .postAdd .ql-editor {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
}

html body .privacySelect__menu,
html body .privacySelect__menu-portal .privacySelect__menu,
html body .wlj-select__menu,
html body .wlj-select__menu-portal .wlj-select__menu,
html body .react-select__menu,
html body [class*="__menu"]:not([class*="__menu-list"]):not([class*="__menu-portal"]):not([class*="__menu-notice"]),
html body div[class*="css-"][class$="-menu"],
html body .ant-select-dropdown {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  border-radius: 4px !important;
  box-shadow: var(--wlj-glass-shadow) !important;
}

html body .ant-select-dropdown {
  overflow: auto !important;
  max-height: min(320px, 70vh) !important;
}

html body .ant-select-dropdown.wlj-job-filter-dropdown,
html body.wlj-neat-gradient-active .ant-select-dropdown.wlj-job-filter-dropdown {
  overflow: auto !important;
  max-height: min(280px, 70vh) !important;
  z-index: var(--wlj-z-picker, 1350) !important;
}

html body .ant-select-dropdown.wlj-job-filter-dropdown .ant-checkbox-wrapper,
html body .ant-select-dropdown.wlj-job-filter-dropdown .custom-checkbox-item,
html body .ant-select-dropdown.wlj-job-filter-dropdown .ant-checkbox-wrapper span {
  color: var(--wlj-text-primary, var(--heading-color, #101828)) !important;
  opacity: 1 !important;
}

html body .searchInput.wlj-ddl-input,
html body .job-search-workspace .search-dropdown .searchInput,
html body .job-search-workspace .wlj-ddl-select-wrap,
html body.wlj-neat-gradient-active .searchInput.wlj-ddl-input {
  overflow: visible !important;
}

html body [class*="__menu-list"],
html body .privacySelect__menu-list,
html body .wlj-select__menu-list,
html body .react-select__menu-list {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body [class*="__option--is-focused"],
html body [class*="__option"]:hover,
html body .privacySelect__option--is-focused,
html body .wlj-select__option--is-focused,
html body .react-select__option--is-focused {
  background: var(--wlj-hover-bg, color-mix(in srgb, var(--buttonBase, #00419f) 8%, transparent)) !important;
  color: var(--buttonBase, #00419f) !important;
}

html body [class*="__option--is-selected"],
html body .privacySelect__option--is-selected,
html body .wlj-select__option--is-selected,
html body .react-select__option--is-selected {
  background: var(--wlj-active-bg, color-mix(in srgb, var(--buttonBase, #00419f) 12%, transparent)) !important;
  color: var(--buttonBase, #00419f) !important;
}

html[data-theme="dark"] body .privacySelect .privacySelect__control,
html[data-theme="dark"] body .postAdd .post-editor-container,
html[data-theme="dark"] body .postAdd .ql-container,
body.darkMode .privacySelect .privacySelect__control,
body.darkMode .postAdd .post-editor-container,
body.darkMode .postAdd .ql-container {
  background: var(--wlj-glass-sheen), rgba(28, 28, 36, 0.22) !important;
  background-color: rgba(28, 28, 36, 0.22) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}

html[data-theme="dark"] body .privacySelect__menu,
html[data-theme="dark"] body .wlj-select__menu,
html[data-theme="dark"] body .react-select__menu,
html[data-theme="dark"] body [class*="__menu"]:not([class*="__menu-list"]):not([class*="__menu-portal"]):not([class*="__menu-notice"]),
html[data-theme="dark"] body div[class*="css-"][class$="-menu"],
body.darkMode .privacySelect__menu,
body.darkMode .wlj-select__menu,
body.darkMode .react-select__menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
}

html body .modal.show .article-md-dialog .modal-content,
html body .modal.show .article-md-dialog .container.articleContainer,
html body .modal.show .article-md-dialog .articleContainer.modal-content,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .container.articleContainer,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal.show .article-md-dialog .container.articleContainer {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  max-height: calc(100dvh - 48px) !important;
  min-height: 0 !important;
  margin: 0 !important;
  border-radius: 4px !important;
  overflow: hidden !important;
}

html body .modal.show .article-md-dialog .form-quill,
html body .modal.show .article-md-dialog .form-quill.AddArticleNewQuill,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .form-quill,
html[data-theme="dark"] body.wlj-neat-gradient-active .modal.show .article-md-dialog .form-quill,
body.darkMode.wlj-neat-gradient-active .modal.show .article-md-dialog .form-quill {
  background: var(--wlj-canvas, #f8fafc) !important;
  background-color: var(--wlj-canvas, #f8fafc) !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  isolation: isolate;
}

html body .modal.show .article-md-dialog .article-title-col .HuzTitleInput,
html body .modal.show .article-md-dialog .article-title-col .form-control,
html body .modal.show .article-md-dialog .article-tags-col .ant-select .ant-select-selector,
html body .modal.show .article-md-dialog .article-tags-col .ant-select-outlined .ant-select-selector,
html body .modal.show .article-md-dialog .article-tags-col .ant-select-multiple .ant-select-selector,
html body .modal.show .article-md-dialog .ArticleHuzTagColor .ant-select-selector,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .article-title-col .HuzTitleInput,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .article-title-col .form-control,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .article-tags-col .ant-select .ant-select-selector,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .ArticleHuzTagColor .ant-select-selector {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  border: 1px solid var(--wlj-field-border, #e4e7ec) !important;
  border-radius: var(--wlj-radius, 4px) !important;
  min-height: 44px !important;
  height: 44px;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
}

html body .modal.show .article-md-dialog .ql-toolbar,
html body .modal.show .article-md-dialog .ql-container,
html body .modal.show .article-md-dialog .ql-editor,
html body .modal.show .article-md-dialog .ant-select-selection-wrap,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .ql-toolbar,
html body.wlj-neat-gradient-active .modal.show .article-md-dialog .ql-container {
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .modal.show .article-md-dialog .ant-select-selection-search,
html body .modal.show .article-md-dialog .ant-select-selection-search-input,
html body .modal.show .article-md-dialog input.ant-select-selection-search-input {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  min-height: 0 !important;
  height: auto !important;
  padding: 0 !important;
  outline: none !important;
}

/* Centered overlay modal — last lock. Matches production (welinkjobs.com). */
html body .modal,
html body .modal.fade,
html body .modal.show,
html body.wlj-neat-gradient-active .modal.show {
  --bs-modal-margin: 24px !important;
}

html body .modal.modal-lg,
html body .modal.modal-lg.show,
html body.wlj-neat-gradient-active .modal.modal-lg {
  --bs-modal-width: 920px !important;
}

html body .modal.modal-xl,
html body .modal.modal-xl.show,
html body .modal.article-xl-modal,
html body .modal.article-xl-modal.show {
  --bs-modal-width: 1140px !important;
}

html body .modal.show,
html body .modal.fade.show,
html body .modal.postAdd.show,
html body.wlj-neat-gradient-active .modal.show,
html body.wlj-neat-gradient-active .modal.fade.show {
  position: fixed !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  padding: 24px !important;
  margin: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 1300 !important;
  transform: none !important;
  filter: none !important;
  contain: none !important;
}

html body .modal.show .modal-dialog,
html body .modal.fade.show .modal-dialog,
html body .modal.show .modal-dialog.modal-dialog-centered,
html body .modal.show .modal-dialog.modal-dialog-scrollable,
html body.wlj-neat-gradient-active .modal.show .modal-dialog,
html body.wlj-neat-gradient-active .modal.show .modal-dialog.modal-dialog-centered {
  --bs-modal-margin: 0px !important;
  width: min(var(--bs-modal-width, 500px), calc(100vw - 48px)) !important;
  max-width: min(var(--bs-modal-width, 500px), calc(100vw - 48px)) !important;
  min-width: 0 !important;
  height: auto !important;
  max-height: calc(100dvh - 48px) !important;
  min-height: 0 !important;
  margin: 0 auto !important;
  padding: 0 !important;
  transform: none !important;
  flex: 0 1 auto !important;
  align-self: center !important;
  align-items: stretch !important;
  display: flex !important;
}

html body .modal.modal-lg.show .modal-dialog,
html body .modal.modal-lg .modal-dialog,
html body.wlj-neat-gradient-active .modal.modal-lg.show .modal-dialog {
  --bs-modal-width: 920px !important;
  width: min(920px, calc(100vw - 48px)) !important;
  max-width: min(920px, calc(100vw - 48px)) !important;
}

html body .modal.modal-xl.show .modal-dialog,
html body .modal.modal-xl .modal-dialog,
html body .modal.modal-xl.show .modal-dialog.modal-dialog-centered,
html body .modal.modal-xl.show .modal-dialog.modal-dialog-scrollable,
html body .modal.article-xl-modal.show .modal-dialog,
html body .modal.article-xl-modal .modal-dialog,
html body .modal.article-xl-modal.show .modal-dialog.modal-dialog-centered,
html body .modal.article-xl-modal.show .modal-dialog.modal-dialog-scrollable,
html body .modal.show:has(.articleContainer) .modal-dialog,
html body .modal.show:has(.articleContainer) .modal-dialog.modal-dialog-centered,
html body .modal.show:has(.articleContainer) .modal-dialog.modal-dialog-scrollable,
html body .modal.show .modal-dialog.article-md-dialog,
html body .modal.show .modal-dialog.article-md-dialog.modal-dialog-centered,
html body .modal.show .modal-dialog.article-md-dialog.modal-dialog-scrollable,
html body.wlj-neat-gradient-active .modal.modal-xl.show .modal-dialog,
html body.wlj-neat-gradient-active .modal.show:has(.articleContainer) .modal-dialog {
  --bs-modal-width: 1140px !important;
  width: min(1140px, calc(100vw - 48px)) !important;
  max-width: min(1140px, calc(100vw - 48px)) !important;
}

html body .modal.show .modal-content,
html body .modal.fade.show .modal-content,
html body.wlj-neat-gradient-active .modal.show .modal-content,
html body.wlj-neat-gradient-active .modal.show .modal-content:not(.ImagesliderScroller) {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  max-height: calc(100dvh - 48px) !important;
  min-height: 0 !important;
  margin: 0 !important;
  border-radius: 4px !important;
  box-shadow: var(--wlj-shadow-lg) !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-start !important;
  overflow: hidden !important;
}

html body .modal.show .modal-content > form {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

html body .modal.show .articleContainer.modal-content {
  flex: 0 1 auto !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
  align-self: stretch !important;
  align-items: stretch !important;
}

html body .modal.show .articleContainer.modal-content > form {
  flex: 0 1 auto !important;
}

html body .modal.show .article-md-dialog {
  min-height: 0 !important;
  height: auto !important;
  flex-direction: column !important;
}

html body .wlj-overlay-modal.modal.show,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal.show {
  position: fixed !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 24px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: var(--wlj-z-modal, 1300) !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .wlj-overlay-modal.modal.show::before,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal.show::before {
  content: "" !important;
  display: block !important;
  position: fixed !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  background-color: var(--wlj-modal-scrim, rgba(15, 23, 42, 0.16)) !important;
  backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
}

html body .wlj-overlay-modal .modal-dialog,
html body.wlj-neat-gradient-active .wlj-overlay-modal .modal-dialog {
  width: min(var(--bs-modal-width, 500px), calc(100vw - 48px)) !important;
  max-width: min(var(--bs-modal-width, 500px), calc(100vw - 48px)) !important;
  max-height: calc(100dvh - 48px) !important;
  margin: 0 auto !important;
  height: auto !important;
}

html body .wlj-overlay-modal.modal-lg .modal-dialog,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal-lg .modal-dialog {
  --bs-modal-width: 920px !important;
  width: min(920px, calc(100vw - 48px)) !important;
  max-width: min(920px, calc(100vw - 48px)) !important;
}

html body .wlj-overlay-modal.modal-xl .modal-dialog,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal-xl .modal-dialog {
  --bs-modal-width: 1140px !important;
  width: min(1140px, calc(100vw - 48px)) !important;
  max-width: min(1140px, calc(100vw - 48px)) !important;
}

html body .wlj-overlay-modal .modal-content,
html body .wlj-overlay-modal.postAdd .modal-content,
html body.wlj-neat-gradient-active .wlj-overlay-modal .modal-content,
html body.wlj-neat-gradient-active .wlj-overlay-modal.postAdd .modal-content {
  width: 100% !important;
  max-height: calc(100dvh - 48px) !important;
  border-radius: 4px !important;
  overflow: hidden !important;
  transform: none !important;
  background: var(--wlj-glass-sheen), var(--wlj-modal-glass, var(--wlj-glass-dropdown)) !important;
  background-color: var(--wlj-modal-glass, var(--wlj-glass-dropdown)) !important;
  backdrop-filter: var(--wlj-modal-glass-blur, var(--wlj-glass-dropdown-blur)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-glass-blur, var(--wlj-glass-dropdown-blur)) !important;
  border: 1px solid var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
}

html body .wlj-overlay-modal .settings input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body .wlj-overlay-modal .settings textarea,
html body .wlj-overlay-modal .settings select,
html body .wlj-overlay-modal .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body .wlj-overlay-modal .CreateCompany textarea,
html body .wlj-overlay-modal .CreateCompany select,
html body .wlj-overlay-modal .CreateCompany .react-select__control,
html body .wlj-overlay-modal .CreateCompany .wlj-select__control,
html body .wlj-overlay-modal .CreateCompany [class*="__control"]:not(.privacySelect__control),
html body .wlj-overlay-modal .flatpickr-input {
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  border: 1px solid var(--wlj-field-border, #e4e7ec) !important;
  border-radius: 4px !important;
  backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur, saturate(150%) blur(20px)) !important;
  box-shadow: var(--wlj-glass-surface-shadow, var(--wlj-shadow)) !important;
  color: var(--wlj-text-primary) !important;
}

/* Create New stays beside the field label (Institution / Organization / Company) */
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .wlj-edu-label-row {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  gap: 8px !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .wlj-edu-label-row > label,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"]:has(> .createModal) > label {
  width: auto !important;
  max-width: none !important;
  flex: 0 0 auto !important;
  margin-bottom: 0 !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"]:has(> .createModal) {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
  align-items: center !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"]:has(> .createModal) > .createModal {
  flex: 0 0 auto !important;
  margin-bottom: 0 !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"]:has(> .createModal) > :not(label):not(.createModal) {
  flex: 1 0 100% !important;
  width: 100% !important;
}

/* Is Present / Is Expirable — checkbox beside the label, not stretched to the right */
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .wlj-is-present-row,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .d-flex.align-items-center:has(> input[type="checkbox"]):not(:has(.closebtn)):not(:has(.mainBtn1)):not(:has(.wljPrimaryBtn)) {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  justify-content: flex-start !important;
  align-items: center !important;
  gap: 8px !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .wlj-is-present-row > label,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .d-flex.align-items-center:has(> input[type="checkbox"]):not(:has(.closebtn)):not(:has(.mainBtn1)) > label {
  width: auto !important;
  max-width: none !important;
  flex: 0 0 auto !important;
  margin-bottom: 0 !important;
  margin-inline-end: 0 !important;
}

/* Close / Cancel / Delete / Unfollow stay the red outline format */
html body .modal .modal-content:not(.ImagesliderScroller) .closebtn,
html body .postAdd.modal .closebtn,
html body .skillModal .closebtn,
html body .modal .modal-footer .closebtn,
html body .articleContainer .closebtn,
html body.wlj-neat-gradient-active .articleContainer .closebtn,
html body .modal .modal-content:not(.ImagesliderScroller) .wljDangerBtn,
html[data-theme="dark"] body.wlj-neat-gradient-active .articleContainer .closebtn,
body.darkMode.wlj-neat-gradient-active .articleContainer .closebtn {
  background: transparent !important;
  border: 1.5px solid var(--wlj-danger, #b50000) !important;
  color: var(--wlj-danger, #b50000) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .closebtn:hover,
html body .postAdd.modal .closebtn:hover,
html body .skillModal .closebtn:hover,
html body .articleContainer .closebtn:hover,
html body.wlj-neat-gradient-active .articleContainer .closebtn:hover,
html body .modal .modal-content:not(.ImagesliderScroller) .wljDangerBtn:hover {
  background: var(--wlj-danger, #b50000) !important;
  border-color: var(--wlj-danger, #b50000) !important;
  color: #fff !important;
}

/* Buttons stay bottom-right. Sync With Resume stays left via inner 100% + margin-inline-end auto */
html body .modal .modal-content:not(.ImagesliderScroller) .row > .text-end.mt-4,
html body .modal .modal-content:not(.ImagesliderScroller) .row > .col-md-12.text-end,
html body .modal .modal-content:not(.ImagesliderScroller) .row > .col-12.text-end,
html body .modal .modal-content:not(.ImagesliderScroller) .text-end.mt-4:has(.closebtn),
html body .modal .modal-content:not(.ImagesliderScroller) .text-end.mt-4:has(.mainBtn1),
html body .modal .modal-content:not(.ImagesliderScroller) .updateBtn:has(.closebtn),
html body .modal .modal-content:not(.ImagesliderScroller) .updateBtn:has(.mainBtn1) {
  justify-content: flex-end !important;
  text-align: end !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .text-end > .d-flex.align-items-center,
html body .modal .modal-content:not(.ImagesliderScroller) .text-end > .d-flex.justify-content-end,
html body .modal .modal-content:not(.ImagesliderScroller) .updateBtn > .d-flex.align-items-center {
  width: 100% !important;
  flex: 1 1 auto !important;
  flex-wrap: nowrap !important;
  justify-content: flex-end !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .text-end .d-flex.align-items-center > input[type="checkbox"],
html body .modal .modal-content:not(.ImagesliderScroller) .d-flex.justify-content-end:has(.closebtn) > input[type="checkbox"] {
  flex: 0 0 auto !important;
  margin: 0 !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .text-end .d-flex.align-items-center > input[type="checkbox"] + label,
html body .modal .modal-content:not(.ImagesliderScroller) .d-flex.justify-content-end > input[type="checkbox"] + label {
  width: auto !important;
  flex: 0 0 auto !important;
  margin-bottom: 0 !important;
  margin-inline-start: 8px !important;
  margin-inline-end: auto !important;
  text-align: start !important;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row.modal-p,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row.settings,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) form > .row {
  --bs-gutter-y: 12px;
  row-gap: 12px;
}

html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"].mt-2,
html body .modal:not(.dashboard-modal) .modal-content:not(.ImagesliderScroller) .row > [class*="col-"].mt-3 {
  margin-top: 0 !important;
}

/* React-select: never paint a white/glass slab over the selected value */
html body .react-select__value-container,
html body .react-select__indicators,
html body .react-select__indicator,
html body .react-select__input-container,
html body .react-select__single-value,
html body .react-select__placeholder,
html body .wlj-select__value-container,
html body .wlj-select__indicators,
html body .wlj-select__indicator,
html body .wlj-select__input-container,
html body .wlj-select__single-value,
html body .wlj-select__placeholder,
html body [class*="__value-container"],
html body [class*="__indicators"],
html body [class$="-ValueContainer"],
html body [class$="-IndicatorsContainer"],
html body [class$="-Indicators"],
html body [class$="-Input"],
html body [class$="-input-container"],
html body input[id^="react-select"],
html body input[id^="react-select"]:hover,
html body input[id^="react-select"]:focus,
html body .react-select__input,
html body .react-select__input input,
html body .wlj-select__input,
html body .wlj-select__input input,
html body [class$="-Input"] input,
html body .modal .modal-content input[id^="react-select"],
html body .modal .modal-content [class$="-ValueContainer"],
html body .modal .modal-content [class*="__value-container"],
html body .CreateCompany [class$="-ValueContainer"],
html body .settings [class$="-ValueContainer"],
html body.wlj-neat-gradient-active [class$="-ValueContainer"],
html body.wlj-neat-gradient-active input[id^="react-select"] {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body input[id^="react-select"],
html body input[id^="react-select"]:hover,
html body input[id^="react-select"]:focus,
html body .modal .modal-content input[id^="react-select"],
html body .wlj-select__control input,
html body .react-select__control input,
html body [class$="-control"] input,
html body [class*="__control"]:not(.privacySelect__control) input {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  border: none !important;
  min-height: 0 !important;
  height: auto !important;
  max-height: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

html body .wlj-select__control:not(.wlj-select__control--is-focused) input,
html body .react-select__control:not(.react-select__control--is-focused) input,
html body [class$="-control"]:not([class*="--is-focused"]) input {
  opacity: 0 !important;
  width: 2px !important;
  min-width: 0 !important;
}

/* Glass backdrop-filter on cards creates a containing block for position:fixed.
   Strip it from the app tree while any modal is open so in-page dialogs can
   cover header + sidebars. Portaled modals on document.body are unaffected. */
html:has(.modal.show) #root,
html:has(.modal.show) #root *:not(.modal, .modal *, #wlj-portal-root, #wlj-portal-root *) {
  transform: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  contain: none !important;
  perspective: none !important;
  will-change: auto !important;
}

html body .modal.show,
html body .modal.fade.show,
html body .modal.postAdd.show,
html body .wlj-overlay-modal.modal.show,
html body.wlj-neat-gradient-active .modal.show,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal.show {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100dvh !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 24px !important;
  overflow: hidden !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 4000 !important;
  transform: none !important;
  filter: none !important;
  contain: none !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .modal.show::before,
html body .modal.fade.show::before,
html body .modal.postAdd.show::before,
html body .wlj-overlay-modal.modal.show::before,
html body.wlj-neat-gradient-active .modal.show::before,
html body.wlj-neat-gradient-active .wlj-overlay-modal.modal.show::before {
  content: "" !important;
  display: block !important;
  position: fixed !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  background-color: var(--wlj-modal-scrim, rgba(15, 23, 42, 0.16)) !important;
  backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
}

html body .modal.show .modal-dialog,
html body .wlj-overlay-modal .modal-dialog {
  position: relative !important;
  z-index: 1 !important;
}

html body .modal .wlj-modal-footer,
html body .modal .modal-content .text-end.wlj-modal-footer,
html body .modal .modal-content .d-flex.wlj-modal-footer,
html body .modal .modal-content .text-center:has(.wljDangerBtn),
html body .modal .modal-content .text-center:has(.closebtn),
html body .modal .modal-content .text-center:has(.btnDanger) {
  display: flex !important;
  justify-content: flex-end !important;
  align-items: center !important;
  gap: 8px !important;
  text-align: end !important;
}

html body .modal .modal-content .text-center:has(.closebtn:only-child) .closebtn,
html body .modal .modal-content .text-center:has(.closebtn:only-of-type) .closebtn,
html body .modal .wlj-modal-footer .closebtn:only-child,
html body .modal .modal-content .text-end .closebtn:only-child {
  background: transparent !important;
  border: 1.5px solid var(--wlj-danger, #b50000) !important;
  color: var(--wlj-danger, #b50000) !important;
  box-shadow: none !important;
}

@media screen and (max-width: 991.98px) {
  html body .modal.show,
  html body .wlj-overlay-modal.modal.show {
    padding: 12px !important;
  }

  html body .postAdd.jobCreate .modal-dialog,
  html body .jobCreate .modal-dialog,
  html body .postAdd.jobCreate .modal-dialog.modal-dialog-scrollable,
  html body.wlj-neat-gradient-active .postAdd.jobCreate .modal-dialog {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px) !important;
    max-height: calc(100dvh - 24px) !important;
    height: auto !important;
    margin: 0 auto !important;
  }

  html body .postAdd.jobCreate .modal-content,
  html body .jobCreate .modal-content,
  html body.wlj-neat-gradient-active .postAdd.jobCreate .modal-content {
    max-height: calc(100dvh - 24px) !important;
    height: auto !important;
    border-radius: 4px !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  html body .jobCreate .bs-stepper {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    display: block !important;
  }

  html body .jobCreate .bs-stepper-content,
  html body .jobCreate .bs-stepper-content > .content.active,
  html body .jobCreate .bs-stepper-content > .content.active > .row,
  html body .jobCreate .bs-stepper-content form,
  html body .jobCreate .bs-stepper-content > .content.active form,
  html body .jobCreate .CreateCompany {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    flex: none !important;
    overflow: visible !important;
    max-width: 100% !important;
  }

  html body .jobCreate .bs-stepper-content > .content:not(.active) {
    display: none !important;
  }

  html body .jobCreate .CreateCompany > [class*="col-"]:not(.wlj-job-create-footer) {
    width: 100% !important;
    max-width: 100% !important;
    flex: none !important;
    overflow: visible !important;
  }

  html body .jobCreate .CreateCompany > [class*="col-"] {
    margin-top: 8px !important;
  }
}

/* ============================================================
   Overlay glass lock — emoji picker / global search recipe on
   every modal, dropdown, picker, and menu. Last in this sheet.
   Outer scrim frosts the page (img1); the dialog pane stays glass.
   ============================================================ */
html body .modal-backdrop,
html body .modal-backdrop.show,
html body .chat-forward-modal-backdrop,
html body .message-confirm-modal-backdrop,
html body .modal.show::before,
html body .modal.fade.show::before,
html body .wlj-overlay-modal.modal.show::before {
  background-color: var(--wlj-modal-scrim, rgba(15, 23, 42, 0.16)) !important;
  backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
  -webkit-backdrop-filter: var(--wlj-modal-scrim-blur, saturate(140%) blur(16px)) !important;
}

html body .modal-overlay {
  background-color: var(--wlj-modal-scrim, rgba(15, 23, 42, 0.16)) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .modal.show .modal-dialog,
html body .wlj-overlay-modal .modal-dialog,
html body .postAdd.jobCreate .modal-dialog {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  transform: none !important;
  filter: none !important;
  isolation: auto !important;
}

html body .modal .modal-content:not(.ImagesliderScroller),
html body .postAdd.modal .modal-content:not(.ImagesliderScroller),
html body .postAdd.jobCreate .modal-content,
html body .wlj-overlay-modal .modal-content,
html body .skillModal .modal-content,
html body .dashboard-modal .modal-content,
html body .chat-forward-modal,
html body .message-confirm-modal,
html body .ant-modal-content,
html body .MuiDialog-paper,
html body .swal2-popup,
html body .modal-content.articleContainer,
html body .modal .container.articleContainer,
html body .EmojiPickerReact,
html body .themed-emoji-picker-wrap .EmojiPickerReact,
html body .HuzEmojiPickerChangeColor .EmojiPickerReact,
html body .wlj-portal-emoji-picker .EmojiPickerReact,
html body .suggestions-dropdown,
html body .mainheader .suggestions-dropdown,
html body .dropdown-menu.wlj-portal-interaction-menu,
html body .InteractionModal.wlj-portal-interaction-menu,
html body .wlj-portal-interaction-menu,
html body .wlj-company-action-menu.InteractionModal,
html body .wlj-company-action-menu.dropdown-menu,
html body .wlj-header-quick-panel,
html body .notificationDropdown,
html body .topmessages,
html body .top-notifications-menu,
html body .react-select__menu,
html body .wlj-select__menu,
html body .privacySelect__menu,
html body .ant-select-dropdown,
html body .ant-dropdown,
html body .MuiMenu-paper,
html body .MuiPopover-paper,
html body .MuiAutocomplete-paper,
html body .popover,
html body .flatpickr-calendar,
html body .flatpickr-calendar.open,
html body .language-sub-menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.5)) !important;
  box-shadow: var(--wlj-glass-shadow, var(--wlj-shadow-lg)) !important;
}

html body .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu):not(.wlj-company-action-menu):not(.wlj-portal-interaction-menu)::before,
html body .InteractionModal:not(.wlj-company-action-menu):not(.wlj-portal-interaction-menu)::before {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .setting-header,
html body .modal .modal-content:not(.ImagesliderScroller) .modal-header,
html body .modal .modal-content:not(.ImagesliderScroller) .modal-body,
html body .modal .modal-content:not(.ImagesliderScroller) .modal-footer,
html body .modal .modal-content:not(.ImagesliderScroller) .modalBorder,
html body .modal .modal-content:not(.ImagesliderScroller) .settings,
html body .modal .modal-content:not(.ImagesliderScroller) .CreateCompany,
html body .modal .modal-content:not(.ImagesliderScroller) .bs-stepper,
html body .modal .modal-content:not(.ImagesliderScroller) .bs-stepper-header,
html body .modal .modal-content:not(.ImagesliderScroller) .bs-stepper-content,
html body .modal .modal-content:not(.ImagesliderScroller) .bs-stepper-content > .content,
html body .modal .modal-content:not(.ImagesliderScroller) form,
html body .modal .modal-content:not(.ImagesliderScroller) .card,
html body .modal .modal-content:not(.ImagesliderScroller) .card-body,
html body .postAdd.jobCreate .settings.CreateCompany,
html body .postAdd.jobCreate .bs-stepper,
html body .articleContainer .card,
html body .articleContainer .card-body,
html body .articleContainer .Article-body,
html body .articleContainer .HuzaifaAddArticleForm {
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) .form-control,
html body .modal .modal-content:not(.ImagesliderScroller) .form-select,
html body .modal .modal-content:not(.ImagesliderScroller) textarea,
html body .modal .modal-content:not(.ImagesliderScroller) select,
html body .modal .modal-content:not(.ImagesliderScroller) .JobTitleInputField,
html body .modal .modal-content:not(.ImagesliderScroller) .flatpickr-input,
html body .modal .modal-content:not(.ImagesliderScroller) .react-select__control,
html body .modal .modal-content:not(.ImagesliderScroller) .wlj-select__control,
html body .modal .modal-content:not(.ImagesliderScroller) [class*="__control"]:not(.privacySelect__control),
html body .postAdd.jobCreate .settings.CreateCompany .wlj-select__control,
html body .postAdd.jobCreate .settings.CreateCompany [class*="__control"]:not(.privacySelect__control),
html body .postAdd.jobCreate .JobTitleInputField,
html body .wlj-overlay-modal .CreateCompany .react-select__control,
html body .wlj-overlay-modal .CreateCompany .wlj-select__control,
html body .wlj-overlay-modal .CreateCompany [class*="__control"]:not(.privacySelect__control),
html body .wlj-overlay-modal .CreateCompany input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([id^="react-select"]):not(.react-select__input):not([class*="__input"]),
html body .wlj-overlay-modal .CreateCompany textarea,
html body .wlj-overlay-modal .CreateCompany select {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  background: var(--wlj-glass-sheen), var(--wlj-modal-field, rgba(255, 255, 255, 0.14)) !important;
  background-color: var(--wlj-modal-field, rgba(255, 255, 255, 0.14)) !important;
}

html body .postAdd.jobCreate .CreateCompany > [class*="col-"],
html body .jobCreate .CreateCompany > [class*="col-"],
html body .modal .CreateCompany > [class*="col-"] {
  min-width: 0 !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) [class*="__control"],
html body .postAdd.jobCreate [class*="__control"],
html body .CreateCompany [class*="__control"] {
  display: flex !important;
  min-width: 0 !important;
  overflow: hidden !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) [class*="__value-container"],
html body .postAdd.jobCreate [class*="__value-container"],
html body .CreateCompany [class*="__value-container"],
html body .modal .modal-content:not(.ImagesliderScroller) [class$="-ValueContainer"],
html body .postAdd.jobCreate [class$="-ValueContainer"] {
  min-width: 0 !important;
  overflow: hidden !important;
  flex: 1 1 auto !important;
}

html body .modal .modal-content:not(.ImagesliderScroller) [class$="-singleValue"],
html body .modal .modal-content:not(.ImagesliderScroller) .react-select__single-value,
html body .postAdd.jobCreate [class$="-singleValue"],
html body .CreateCompany [class$="-singleValue"] {
  position: absolute !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

html body .modal .privacySelect [class*="__control"],
html body .privacySelect .privacySelect__control {
  min-width: 158px !important;
  overflow: visible !important;
}

html body .modal .privacySelect [class*="__value-container"],
html body .privacySelect .privacySelect__value-container {
  overflow: visible !important;
  max-width: none !important;
}

html body .modal .privacySelect [class*="__single-value"],
html body .privacySelect .privacySelect__single-value,
html body .modal .privacySelect [class$="-singleValue"] {
  position: static !important;
  max-width: none !important;
  overflow: visible !important;
  text-overflow: unset !important;
  transform: none !important;
  white-space: nowrap !important;
}

html[data-theme="dark"] body .modal .modal-content:not(.ImagesliderScroller),
html[data-theme="dark"] body .postAdd.jobCreate .modal-content,
html[data-theme="dark"] body .suggestions-dropdown,
html[data-theme="dark"] body .EmojiPickerReact,
html[data-theme="dark"] body .dropdown-menu.wlj-portal-interaction-menu,
html[data-theme="dark"] body .react-select__menu,
html[data-theme="dark"] body .ant-select-dropdown,
body.darkMode .modal .modal-content:not(.ImagesliderScroller),
body.darkMode .postAdd.jobCreate .modal-content,
body.darkMode .suggestions-dropdown,
body.darkMode .EmojiPickerReact {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  border-color: var(--wlj-glass-edge, rgba(255, 255, 255, 0.18)) !important;
}

html[data-theme="dark"] body .modal .modal-content:not(.ImagesliderScroller) .form-control,
html[data-theme="dark"] body .modal .modal-content:not(.ImagesliderScroller) [class*="__control"]:not(.privacySelect__control),
html[data-theme="dark"] body .postAdd.jobCreate .JobTitleInputField,
body.darkMode .modal .modal-content:not(.ImagesliderScroller) .form-control,
body.darkMode .postAdd.jobCreate .JobTitleInputField {
  background: var(--wlj-glass-sheen), var(--wlj-modal-field, rgba(28, 28, 36, 0.32)) !important;
  background-color: var(--wlj-modal-field, rgba(28, 28, 36, 0.32)) !important;
}

/* Match .suggestions-dropdown: frost on the pane, parent card is not a
   backdrop root, overflow stays hidden on the frosted node. */
html body .featured-card,
html body .featured-card.user-postOnlyContent,
html body .draftPost,
html body.wlj-neat-gradient-active .featured-card,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent,
html body.wlj-neat-gradient-active .draftPost {
  position: relative !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .featured-card::before,
html body .featured-card.user-postOnlyContent::before,
html body .draftPost::before,
html body.wlj-neat-gradient-active .featured-card::before,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent::before,
html body.wlj-neat-gradient-active .draftPost::before {
  content: "" !important;
  display: block !important;
  position: absolute !important;
  inset: 0 !important;
  z-index: 0 !important;
  pointer-events: none !important;
  border-radius: inherit !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-surface) !important;
  background-color: var(--wlj-glass-surface) !important;
  backdrop-filter: var(--wlj-glass-surface-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-surface-blur) !important;
}

html body .featured-card > *:not(.dropdown-menu):not(.InteractionModal),
html body .draftPost > *:not(.dropdown-menu):not(.InteractionModal) {
  position: relative;
  z-index: 1;
}

html body .commentBox,
html body .ReplyComment .commentBox,
html body .addComment .commentBox,
html body .postCommentWrapper .commentBox,
html body .feed-post .commentBox,
html body .featured-card .commentBox,
html body .featured-card.user-postOnlyContent .commentBox,
html body .user-postOnlyContent .commentArea .commentBox,
html body .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .commentBox,
html body.wlj-neat-gradient-active .feed-post .commentBox,
html body.wlj-neat-gradient-active .featured-card .commentBox,
html body.wlj-neat-gradient-active .featured-card.user-postOnlyContent .commentBox,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .user-postOnlyContent .commentArea .commentBox,
html body.wlj-neat-gradient-active .ReplyComment .commentBox,
html body.wlj-neat-gradient-active .addComment .commentBox,
html body.wlj-neat-gradient-active .postCommentWrapper .commentBox,
html body .featured-card:has(.dropdown-menu.show) .commentBox,
html body .featured-card:has(.InteractionModal) .commentBox,
html body .featured-card:has(.HuzEmojiPickerChangeColor) .commentBox,
html body .featured-card:has(.themed-emoji-picker-wrap) .commentBox,
html body.wlj-neat-gradient-active .featured-card:has(.dropdown-menu.show) .commentBox,
html body.wlj-neat-gradient-active .featured-card:has(.InteractionModal) .commentBox,
html body.wlj-neat-gradient-active .featured-card:has(.HuzEmojiPickerChangeColor) .commentBox,
html body.wlj-neat-gradient-active .featured-card:has(.themed-emoji-picker-wrap) .commentBox,
html[data-theme="dark"] body .commentBox,
html[data-theme="dark"] body.wlj-neat-gradient-active .commentBox,
html[data-theme="dark"] body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox,
body.darkMode .commentBox,
body.darkMode.wlj-neat-gradient-active .commentBox,
body.darkMode.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox {
  position: relative !important;
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border: 1px solid var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  overflow: hidden !important;
}

html body .commentBox:focus-within,
html body.wlj-neat-gradient-active .commentBox:focus-within,
html body.wlj-neat-gradient-active .featured-card .commentBox:focus-within,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox:focus-within {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border-color: var(--wlj-primary) !important;
  box-shadow: var(--wlj-focus-ring), var(--wlj-glass-shadow) !important;
}

html body .commentBox::before,
html body .ReplyComment .commentBox::before,
html body .addComment .commentBox::before,
html body .postCommentWrapper .commentBox::before,
html body .featured-card .commentBox::before,
html body .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body .featured-card:has(.dropdown-menu.show) .commentBox::before,
html body .featured-card:has(.InteractionModal) .commentBox::before,
html body .featured-card:has(.HuzEmojiPickerChangeColor) .commentBox::before,
html body .featured-card:has(.themed-emoji-picker-wrap) .commentBox::before,
html body.wlj-neat-gradient-active .commentBox::before,
html body.wlj-neat-gradient-active .featured-card .commentBox::before,
html body.wlj-neat-gradient-active .newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body.wlj-neat-gradient-active .new-newsfeedPosts .featured-card.user-postOnlyContent .commentArea .commentBox::before,
html body.wlj-neat-gradient-active .ReplyComment .commentBox::before,
html body.wlj-neat-gradient-active .addComment .commentBox::before,
html body.wlj-neat-gradient-active .postCommentWrapper .commentBox::before,
html[data-theme="dark"] body .commentBox::before,
body.darkMode .commentBox::before {
  content: none !important;
  display: none !important;
}

html body .commentBox input,
html body .commentBox textarea,
html body .commentBox > * {
  position: relative;
  z-index: 1;
  background: transparent !important;
  background-color: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

html body .suggestions-dropdown,
html body .headerLayout .suggestions-dropdown,
html body .mainheader .suggestions-dropdown,
html body .dropdown-menu.wlj-portal-interaction-menu,
html body .InteractionModal.wlj-portal-interaction-menu,
html body .wlj-portal-interaction-menu,
html body .wlj-company-action-menu.InteractionModal,
html body .wlj-company-action-menu.dropdown-menu,
html body .wlj-header-quick-panel,
html body .notificationDropdown,
html body .topmessages,
html body .top-notifications-menu,
html body .react-select__menu,
html body .wlj-select__menu,
html body .privacySelect__menu,
html body .ant-select-dropdown,
html body .ant-dropdown,
html body .MuiMenu-paper,
html body .MuiPopover-paper,
html body .MuiAutocomplete-paper,
html body .language-sub-menu {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  border-color: var(--wlj-glass-edge) !important;
  box-shadow: var(--wlj-glass-shadow) !important;
  overflow: hidden !important;
}

html body .dropdown-menu:not(.notificationDropdown):not(.wlj-header-quick-panel):not(.topmessages):not(.top-notifications-menu):not(.wlj-company-action-menu):not(.wlj-portal-interaction-menu)::before,
html body .InteractionModal:not(.wlj-company-action-menu):not(.wlj-portal-interaction-menu)::before {
  background: var(--wlj-glass-sheen), var(--wlj-glass-dropdown) !important;
  background-color: var(--wlj-glass-dropdown) !important;
  backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
  -webkit-backdrop-filter: var(--wlj-glass-dropdown-blur) !important;
}
`;
